import type {
  BerthPredictionsQueryResult,
  BerthPredictionsQueryVariables,
  CoaContractId,
  Order,
  OrderId,
  OrganizationId,
  ScenarioTimeline_CoaFragment,
  ScenarioTimeline_PortCallFragment,
  ScenarioTimeline_PortNoticeFragment,
  ScenarioTimeline_ShipFragment,
  ShincShex,
  ShipId,
  VoyageAttribute,
  VoyageCharterId,
} from '../../graphql';
import {
  BerthPredictionsDocument,
  EventType,
  IncludedExcludedUnlessUsed,
  VesselArea,
  VesselSpeedTag,
  VesselSpeedType,
  WeightUnits,
} from '../../graphql';
import { z } from 'zod';
import type { ExactProps, Range as TimeRange, NONETYPE } from '../../lib/util';
import {
  assertDefined,
  assertedCast,
  assertedDefined,
  AssertionError,
  assertKind,
  divideWithRemainder,
  exhaustiveCheck,
  filteredMap,
  findIndexWith,
  findNextIndexWith,
  findPrevIndexWith,
  findWith,
  get,
  groupBySequence,
  isDefined,
  isInRange,
  mapFlatmap,
  mapKeyBy,
  mapMap,
  mapMerge,
  mappedMax,
  mappedMin,
  NONE,
  replaceContents,
  setIntersection,
  swapSubArrays,
  withDefined,
} from '../../lib/util';
import {
  dateStringToNumOpt,
  defaultConstraints,
  getValidEventPermutations,
  stringToNumOpt,
} from './util';
import type { EventTimeline, VoyageGroup } from './Timelines';
import type { ScenarioLocation, ScenarioOrder } from '../../lib/planningData';
import { toast } from 'react-toastify';
import type { TCScheduleEntry } from '../Scenario';
import type { ClientId } from './ClientId';
import { generateClientId, parseClientId } from './ClientId';
import {
  compact,
  countBy,
  findIndex,
  findLast,
  findLastIndex,
  first,
  flatten,
  groupBy,
  last,
  memoize,
  min,
  orderBy,
  partition,
  shuffle,
  sum,
  sumBy,
  uniq,
  uniqBy,
  without,
} from 'lodash';
import isEqual from '@wry/equality';
import type { ApolloClient } from '@apollo/client';
import type {
  CachedBerthPrediction,
  CachedBerthPredictionInput,
} from './BerthPrediction';
import {
  cachedBerthPredictionResultSchema,
  getCachedPrediction,
  QUANTITY_GROUPING_THRESHOLD,
  saveCachedPredictions,
} from './BerthPrediction';
import { getEventsTotalDistance } from '../distances/distance-utils';
import type { Distances, RouteData } from '../distances/context';
import { floatCmp } from '../../lib/math';
import type { NewCargoAllocation } from './Core.cargo-allocation';
import {
  getCompartments,
  isCorrectCargoAllocationType,
} from './Core.cargo-allocation';
import type { GlobalStore } from '../../state/zustand';
import moment from 'moment';
import 'moment-timezone';
import { normalizeTimezone } from '../../lib/moment-timezone-utils';
import type { MetricTonnes } from '../../constants/cargoUnits';
import {
  LONG_TON_IN_METRIC_TONS,
  POUND_IN_METRIC_TONS,
  SHORT_TON_IN_METRIC_TONS,
} from '../../constants/cargoUnits';
import { Objective } from './suggestions/VoyageSuggestionParams';
import type { OrderViolationLimits } from '../ScenarioImprovementParams';
import type { VoyagePlacement } from '../timeline/ScenarioTimeline';
import type { ViolationIndicatorBasis } from '../../state/planning/options';

import type {
  PortCallBunkerOverride,
  PortCallBunkerPriceSource,
  CargoEntry,
  CoaEntry,
  Constraint,
  Entry,
  OffHireEntry,
  CargoAllocation,
  SpotEntry,
  PortCallEntry,
  SingleEntry,
  TCOrderEntry,
  LocationId,
} from '@seaber/common/core/entries';
import {
  EventCategory,
  isCoaEntry,
  isOffHireEntry,
  isPortCallEntry,
  isSingleEntry,
  isSpotEntry,
  isTCOrderEntry,
  minimumEventDuration,
  portCallBunkerOverridesSchema,
  portCallBunkerPriceSourcesSchema,
} from '@seaber/common/core/entries';
import { polygons as ecaPolygons } from '@seaber/common/core/eca_polygons';

export type {
  CargoAllocation,
  CargoEntry,
  CoaEntry,
  OffHireEntry,
  PortCallEntry,
  SingleEntry,
  SpotEntry,
  TCOrderEntry,
} from '@seaber/common/core/entries';

import {
  DefaultMap,
  mapGroupBy,
  mapKeyBy2,
} from '@seaber/common/lib/collections';
import { msToH, hToMs } from '../../lib/time';
import type {
  CalculatedWork,
  CargoEvent,
  CoaEvent,
  DischargeEvent,
  Event,
  Events,
  LoadEvent,
  OffHireEvent,
  OrderConstraints,
  PortCallArrivalEvent,
  PortCallBerthingEvent,
  PortCallDepartureEvent,
  PortCallEvent,
  SingleEvent,
  SpotEvent,
  TCOrderEvent,
  TCOrderEvents,
  TCTimelineEvent,
  TimeData,
  TimeDataInputs,
  TimeDataTotals,
  WorkShift,
} from './TimeData';
import {
  CoreEventType,
  getEventEndLocationId,
  getEventLocationId,
  getEventStartLocationId,
  isCargoEvent,
  isCargoEventTimeData,
  isCoaEvent,
  isCoaEventTimeData,
  isDischargeEvent,
  isDischargeEventTimeData,
  isLoadEvent,
  isLoadEventTimeData,
  isOffHireEvent,
  isOffHireEventTimeData,
  isPortCallArrivalEvent,
  isPortCallArrivalEventTimeData,
  isPortCallBerthingEventTimeData,
  isPortCallDepartureEventTimeData,
  isPortCallEvent,
  isPortCallEventTimeData,
  isSingleEvent,
  isSingleEventTimeData,
  isSpotEvent,
  isSpotEventTimeData,
  isStaticEvent,
  isStaticEventTimeData,
  isTcInEvent,
  isTCOrderEvent,
  isTCOrderEventTimeData,
  isTCTimelineEvent,
  isTimeData,
  PortCallEventType,
  SingleEventType,
} from './TimeData';
import type {
  CalculationConfig,
  ScheduleDataContext,
} from './ScheduleDataContext';
import type {
  BestSchedule,
  CargoAllocationOptions,
  OrderPlacement,
  OrdersToVesselCombination,
} from './suggestions/Suggestions';
import {
  CreateAllCombinations,
  OPTION_NO_CARGO_ALLOCATIONS,
} from './suggestions/Suggestions';
import { defaultVessel, getVesselBannedChannels, type Vessel } from './Vessel';
import type { MassVolumeSize } from './Capacity';
import { cargoRangeFits } from './Capacity';
import type { VesselToSortedEvents, VesselSchedule } from './VesselSchedule';
import type { DeriveCapacitiesAndTimesParams } from './DeriveCapacitiesAndTimes';
import { DeriveCapacitiesAndTimes } from './DeriveCapacitiesAndTimes';
import {
  computeScheduleMetrics,
  hasMetricsViolations,
} from './scheduleMetrics';
import { getTargetShipmentForSuggestion } from './Core.voyage-metrics';
import {
  assignOrders,
  computePlacementBounds,
  insertSingleEvents,
  validateCargoAllocations,
} from './order-assignment/assignOrders';
import type { SuggestionLogEntry } from './suggestions/getSuggestions';
import { updateAssignmentsInBestSchedule } from './suggestions/getSuggestions';
import {
  getSortedTcInEvents,
  isFirstTcInEvent,
  isLastTcInEvent,
} from './tc_in';
import {
  dischargeTimesAfterLoadTimes,
  ensureLoadBeforeDischarge,
  getLoadingTime,
} from './Core.loading-discharging';
import type {
  AssortmentExclusionsMap,
  VoyageExclusionItem,
} from '../hooks/useAssortmentExclusions';
import type { useSpecialRequirements } from '../hooks/useSpecialRequirements';
import { getEffectiveHandlingRates } from './Core.loading-discharging';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point } from '@turf/helpers';

// For legacy reasons to avoid updating all existing imports from this file.
export * from '@seaber/common/core/entries';

export const minimumEventDurationHours = msToH(minimumEventDuration);

export const getPortCallId = (
  e: Events | Pick<Event, 'portCallId'>,
): ClientId | null => e.portCallId ?? null;

/**
 * Parses bunker grade details to BunkerOverrides
 * Handles data from different backend endpoints: some return JSON strings, others return parsed JSONB objects
 * @param  {PortCallEntry} pc
 * @returns {PortCallBunkerOverride | null}
 */
/**
 * Helper function to handle arbitrarily nested JSON encoding from different backend endpoints
 * @param {unknown} rawData - The raw data that might be a string or already parsed JSON
 * @returns {unknown} - The parsed data
 */
function parseNestedJson(rawData: unknown): string | unknown {
  let parsedData = rawData;

  while (typeof parsedData === 'string') {
    parsedData = JSON.parse(parsedData);
  }

  return parsedData;
}

export function parseBunkerOverrides(
  pc: ScenarioTimeline_PortCallFragment,
): PortCallBunkerOverride[] | null {
  const logError = (reason: string, details: unknown, rawData: unknown) => {
    console.error(
      `Failed to parse BunkerOverrides for port call ${pc.clientId}${
        pc.id ? ` with dbId ${pc.id}` : ''
      }: ${reason}`,
      details,
      'Raw data:',
      rawData,
    );
  };

  try {
    const rawBunkerOverrides = parseNestedJson(pc.bunkerOverrides ?? []);
    const parsedBunkerOverrides =
      portCallBunkerOverridesSchema.safeParse(rawBunkerOverrides);

    if (parsedBunkerOverrides.success) {
      return parsedBunkerOverrides.data;
    } else {
      logError(
        'Schema validation failed',
        parsedBunkerOverrides.error,
        rawBunkerOverrides,
      );
      return null;
    }
  } catch (error) {
    logError('JSON parsing failed', error, pc.bunkerOverrides);
    return null;
  }
}

/**
 * Parses bunker ROB type details to BunkerPriceSource
 * Handles data from different backend endpoints: some return JSON strings, others return parsed JSONB objects
 * @param  {PortCallEntry} pc
 * @returns {PortCallBunkePriceSource | null}
 */
export function parseBunkerPriceSource(
  pc: ScenarioTimeline_PortCallFragment,
): PortCallBunkerPriceSource[] | null {
  const logError = (reason: string, details: unknown, rawData: unknown) => {
    console.error(
      `Failed to parse BunkerPriceSource for port call ${pc.clientId}${
        pc.id ? ` with dbId ${pc.id}` : ''
      }: ${reason}`,
      details,
      'Raw data:',
      rawData,
    );
  };

  try {
    const rawBunkerPriceSource = parseNestedJson(pc.bunkerPriceSource ?? []);
    const parsedBunkerPriceSource =
      portCallBunkerPriceSourcesSchema.safeParse(rawBunkerPriceSource);

    if (parsedBunkerPriceSource.success) {
      return parsedBunkerPriceSource.data;
    } else {
      logError(
        'Schema validation failed',
        parsedBunkerPriceSource.error,
        rawBunkerPriceSource,
      );
      return null;
    }
  } catch (error) {
    logError('JSON parsing failed', error, pc.bunkerPriceSource);
    return null;
  }
}

export type PortCallData = {
  id: ClientId | null;
  locationId: LocationId;
  tds: TimeData[];
};

/** The id that identifies the related persistable resource uniquely */
export const getEventResourceId = (ev: Events): ClientId => {
  const res = ev.entry.clientId;

  if (typeof res !== 'string') {
    throw new Error(`Event resource id is not a valid ClientId: ${res}`);
  }

  return parseClientId(res);
};

export type EventKey =
  `ev-${EventCategory}-${CoreEventType | PortCallEventType | SingleEventType}-${ClientId}`;

/** Unique key for any event. Cargo entries and port call entries create multiple events per entry, so plain clientId is not enough to identify the event. Category is redundant, but provides an additional debugging hint. */
export const getEventKey = (ev: Events): EventKey =>
  `ev-${ev.category}-${
    'event' in ev ? ev.event : ev.type
  }-${ev.entry.clientId}`;

/** Returns the Location object for an event.
 * In the rare case that a location is not found in the given mapping a dummy object is returned instead.
 */
export function getEventLocation(
  event: Events,
  locations: Map<LocationId, ScenarioLocation>,
  /** Some events (offhires) may end in a location different from start */
  startOrEnd: 'start' | 'end' = 'start',
): ScenarioLocation {
  const locationId =
    startOrEnd === 'start'
      ? getEventStartLocationId(event)
      : getEventEndLocationId(event);
  const location = locations.get(locationId);

  if (!location) {
    console.warn(`Missing location for location id ${locationId}`, event);
  }

  return (
    location ?? {
      id: locationId,
      name: '',
      zoneLocationsByLocationId: {
        nodes: [],
      },
      clusterId: null,
    }
  );
}

/** For load events, the loading window, for discharge events the discharging window. Undefined for all other event types. */
export const getEventOperationsWindow = memoize(
  (event: Events): TimeRange<number | undefined> | undefined => {
    if ('order' in event) {
      if (event.type === CoreEventType.Load) {
        return event.order.loadingWindow;
      } else {
        return event.order.dischargingWindow;
      }
    }
    return undefined;
  },
);

export const getEventOrder = (e: {
  category: Events['category'];
  order?: ScenarioOrder;
}) => e.order ?? null;

export type WithDbId<T, Brand extends number> = T & {
  /** Committed items always have dbId. Only consider it relevant if you just fetched the item. */
  dbId: Brand;
  archived: boolean | undefined;
};

/**
 * Should be used only for new entries
 */
export const initDefaultCoaSpotEntryTimes = (
  start: number,
  end?: number,
  startIsOverride = false,
): Pick<
  SpotEntry,
  | 'loadingStart'
  | 'loadingEnd'
  | 'loadingDuration'
  | 'dischargingStart'
  | 'dischargingEnd'
  | 'dischargingDuration'
  | 'loadingStartOverride'
  | 'loadingDurationOverride'
  | 'loadingSeaDurationOverride'
  | 'dischargingSeaDurationOverride'
  | 'dischargingStartOverride'
  | 'dischargingDurationOverride'
> => {
  const duration = hToMs(6);
  const discharge = Math.max(end ?? 0 - duration, start + hToMs(24));
  return {
    loadingStart: start,
    loadingEnd: start + duration,
    loadingDuration: duration,
    dischargingStart: discharge,
    dischargingEnd: discharge + duration,
    dischargingDuration: duration,

    loadingStartOverride: startIsOverride ? start : null,
    loadingDurationOverride: null,

    loadingSeaDurationOverride: null,
    dischargingSeaDurationOverride: null,

    dischargingStartOverride: null,
    dischargingDurationOverride: null,
  };
};

// Ensures that durations match end-start
export const checkFixEntryTimestamps = (
  entry: CoaEntry | SpotEntry | TCOrderEntry,
  change: CoaEntry | SpotEntry | TCOrderEntry,
): void => {
  if ('loadingDuration' in change) {
    entry.loadingEnd = entry.loadingStart + entry.loadingDuration;
  } else if ('loadingStart' in change || 'loadingEnd' in change) {
    entry.loadingDuration = entry.loadingEnd - entry.loadingStart;
  }
  if ('dischargingDuration' in change) {
    entry.dischargingEnd = entry.dischargingStart + entry.dischargingDuration;
  } else if ('dischargingStart' in change || 'dischargingEnd' in change) {
    entry.dischargingDuration = entry.dischargingEnd - entry.dischargingStart;
  }

  // In the case a duration override is set but it doesn't match the actual duration we'll remove it. This can happen if there are SoF facts limiting the actual duration.
  if (entry.loadingDurationOverride !== entry.loadingDuration) {
    entry.loadingDurationOverride = null;
  }
  if (entry.dischargingDurationOverride !== entry.dischargingDuration) {
    entry.dischargingDurationOverride = null;
  }
};

/** Makes sure *a* and *b* are not different types. When *b* is undefined this is also considered true. */
export function assertSameEntryType<T extends Entry>(
  a: T,
  b: Entry | undefined,
  msg?: string,
): asserts b is T | undefined {
  if (!b) {
    return;
  }
  if (a.category !== b.category) {
    throw new AssertionError(
      msg ??
        `Failed assertion, entry types ${a.category} and ${b?.category} expected to be the same`,
    );
  }
}

export const buildOffHireEventFromEntry = (
  entry: OffHireEntry,
  vessel: Vessel,
  locations: Map<LocationId, ScenarioLocation>,
): OffHireEvent => {
  const { locationAId, locationBId, start, end } = entry;
  const locationA = assertedDefined(
    locations.get(locationAId),
    `Expected location ${locationAId} to exist`,
  );
  const locationB = assertedDefined(
    locations.get(locationBId),
    `Expected location ${locationBId} to exist`,
  );
  return {
    category: EventCategory.OffHire,
    locationAId,
    locationBId,
    start,
    end,
    duration: end - start,
    type: CoreEventType.OffHire,
    entry,
    locationA,
    locationB,
    locationAName: locationA.name,
    locationATimeZone: locationA.timezone ?? null,
    locationBName: locationB.name,
    locationBTimeZone: locationB.timezone ?? null,
    startOverride: entry.start,
    vesselId: vessel.id,
    vessel,
    constraints: defaultConstraints,
    seaDurationOverride: null,
    // We can't ensure with types that port call id doesn't get accidentally assigned so prevent this by making it a getter
    get portCallId() {
      return null;
    },
  };
};

export type Scenario = {
  schedule: readonly Entry[];
  orders: readonly Order[];
  vessels: readonly Vessel[];
};

type OrderWithContraints = Pick<
  Order,
  'layday' | 'cancelling' | 'rta' | 'deliveredBefore'
>;

const getOrderConstraints = (order: OrderWithContraints): OrderConstraints => {
  const { layday, cancelling, rta, deliveredBefore } = order;
  return {
    layday: dateStringToNumOpt(layday),
    cancelling: dateStringToNumOpt(cancelling),
    rta: dateStringToNumOpt(rta),
    deliveredBefore: dateStringToNumOpt(deliveredBefore),
  };
};

type EntryTypeToEventType<T extends SpotEntry | CoaEntry> = T extends SpotEntry
  ? SpotEvent
  : T extends CoaEntry
    ? CoaEvent
    : never;

export const getSofAndNoticeOverridesFromPortCall = (
  portCall: Pick<
    ScenarioTimeline_PortCallFragment,
    'statementOfFactsByPortCallId'
  >,
) => {
  const { loadingCommenced, loadingCompleted } =
    portCall.statementOfFactsByPortCallId.nodes[0] || {};
  const commenced = dateStringToNumOpt(loadingCommenced);
  const completed = dateStringToNumOpt(loadingCompleted);
  const duration = commenced && completed ? completed - commenced : null;
  return {
    commenced,
    completed,
    duration,
  };
};
const getSofAndNoticeOverrides = (order: Order | ScenarioOrder) => {
  const portCalls = order.orderPortCallRelationsByOrderId.nodes;
  const load = portCalls.find((pc) => pc?.isLoadPort)?.portCallByPortCallId;
  const discharge = portCalls.find(
    (pc) => pc?.isLoadPort === false,
  )?.portCallByPortCallId;
  const {
    commenced: loadingCommenced,
    completed: loadingCompleted,
    duration: loadingDuration,
  } = load
    ? getSofAndNoticeOverridesFromPortCall(load)
    : ({} as Record<string, undefined>);
  const {
    commenced: dischargingCommenced,
    completed: dischargingCompleted,
    duration: dischargingDuration,
  } = discharge
    ? getSofAndNoticeOverridesFromPortCall(discharge)
    : ({} as Record<string, undefined>);

  return {
    loadingCommenced,
    loadingCompleted,
    loadingDuration,
    dischargingCommenced,
    dischargingCompleted,
    dischargingDuration,
  };
};

function getOrderPcsOrSet(order: ScenarioOrder) {
  if (
    order.cargoUnitByCargoUnitId?.name === 'pcs' ||
    order.cargoUnitByCargoUnitId?.name === 'set'
  ) {
    return Number(order.cargoAmount);
  }
  return 0;
}

export function deriveCoaSpotEvents<T extends SpotEntry | CoaEntry>(
  h: T,
  order: ScenarioOrder,
  locations: ScheduleDataContext['locations'],
  vessel?: Vessel,
): Array<EntryTypeToEventType<T>> {
  const constraints = getOrderConstraints(order);
  const pcs = getOrderPcsOrSet(order);
  const loadingStart = h.loadingStart;
  const loadingDuration = h.loadingDurationOverride ?? h.loadingDuration;
  const loadingEnd = loadingStart + loadingDuration;

  const dischargingStart = h.dischargingStart;
  const dischargingDuration =
    h.dischargingDurationOverride ?? h.dischargingDuration;
  const dischargingEnd = dischargingStart + dischargingDuration;

  assertDefined(order.loadPortId, `order ${order.id} should have load port`);
  assertDefined(
    order.dischargePortId,
    `order ${order.id} should have discharge port`,
  );

  const {
    loadingCommenced,
    loadingDuration: sofLoadDuration,
    dischargingCommenced,
    dischargingDuration: sofDischargeDuration,
  } = getSofAndNoticeOverrides(order);

  const loadLocation = assertedDefined(
    locations.get(order.loadPortId ?? NaN),
    `Expected location ${order.loadPortId} to exist`,
  );
  const dischargeLocation = assertedDefined(
    locations.get(order.dischargePortId),
    `Expected location ${order.dischargePortId} to exist`,
  );
  if (isSpotEntry(h)) {
    const load: SpotEvent = {
      category: h.category,
      start: loadingStart,
      end: loadingEnd,
      duration: loadingDuration,
      location: loadLocation,
      locationId: order.loadPortId,
      locationTimeZone: order.locationByLoadPortId?.timezone ?? null,
      type: CoreEventType.Load,
      concurrent: h.loadingConcurrent,
      entry: h,
      vesselId: h.vesselId ?? null,
      vessel,
      order,
      pcs,
      constraints,
      startOverride: h.loadingStartOverride,
      seaDurationOverride: h.loadingSeaDurationOverride,
      durationOverride: h.loadingDurationOverride,
      sofCommenced: loadingCommenced,
      sofDuration: sofLoadDuration,
      portCallId: h.loadingPortCallId,
    };

    const discharge: SpotEvent = {
      category: h.category,
      start: dischargingStart,
      end: dischargingEnd,
      duration: dischargingDuration,
      location: dischargeLocation,
      locationId: order.dischargePortId,
      locationTimeZone: order.locationByDischargePortId?.timezone ?? null,
      type: CoreEventType.Discharge,
      concurrent: h.dischargingConcurrent,
      entry: h,
      vesselId: h.vesselId ?? null,
      vessel,
      constraints,
      order,
      pcs,
      startOverride: h.dischargingStartOverride,
      seaDurationOverride: h.dischargingSeaDurationOverride,
      durationOverride: h.dischargingDurationOverride,
      sofCommenced: dischargingCommenced,
      sofDuration: sofDischargeDuration,
      portCallId: h.dischargingPortCallId,
    };

    dischargeTimesAfterLoadTimes(load, discharge);

    return [load, discharge] as Array<EntryTypeToEventType<T>>;
  } else if (isCoaEntry(h)) {
    const load: CoaEvent = {
      category: h.category,
      start: loadingStart,
      end: loadingEnd,
      duration: loadingDuration,
      location: loadLocation,
      locationId: order.loadPortId,
      locationTimeZone: order.locationByLoadPortId?.timezone ?? null,
      type: CoreEventType.Load,
      concurrent: h.loadingConcurrent,
      entry: h,
      vesselId: h.vesselId ?? null,
      vessel,
      order,
      pcs,
      constraints,
      startOverride: h.loadingStartOverride,
      seaDurationOverride: h.loadingSeaDurationOverride,
      durationOverride: h.loadingDurationOverride,
      sofCommenced: loadingCommenced,
      sofDuration: sofLoadDuration,
      portCallId: h.loadingPortCallId,
    };

    const discharge: CoaEvent = {
      category: h.category,
      start: dischargingStart,
      end: dischargingEnd,
      duration: dischargingDuration,
      location: dischargeLocation,
      locationId: order.dischargePortId,
      locationTimeZone: order.locationByDischargePortId?.timezone ?? null,
      type: CoreEventType.Discharge,
      concurrent: h.dischargingConcurrent,
      entry: h,
      vesselId: h.vesselId ?? null,
      vessel,
      constraints,
      order,
      pcs,
      startOverride: h.dischargingStartOverride,
      seaDurationOverride: h.dischargingSeaDurationOverride,
      durationOverride: h.dischargingDurationOverride,
      sofCommenced: dischargingCommenced,
      sofDuration: sofDischargeDuration,
      portCallId: h.dischargingPortCallId,
    };

    dischargeTimesAfterLoadTimes(load, discharge);

    return [load, discharge] as Array<EntryTypeToEventType<T>>;
  } else {
    throw new Error('Incorrect entry type');
  }
}

export function updateEventFromTimeData<T extends Events>(
  event: T,
  td: TimeData,
): T {
  return {
    ...event,
    start: td.start,
    duration: td.calculatedDuration,
    end: td.end,
  };
}

type CoaEventGroupsByContractAndVessel = DefaultMap<
  CoaContractId,
  DefaultMap<ShipId | null, DefaultMap<ShipmentOrOrderId, CoaEvent[]>>
>;
export type CoaEventGroup = {
  key: string;
  coaId: CoaContractId;
  vesselId: ShipId | null;
  events: (CoaEvent | PortCallEvent)[];
};

/**
 * Whether consecutive COA voyages on the given vessel should chain
 * (one voyage group, ballast legs between, threaded timing) rather than
 * being treated as isolated per-shipment voyage groups.
 */
export const consecutiveCoaVoyagesEnabled = (vessel: Vessel): boolean =>
  vessel.enableConsecutiveCoaVoyages;

let warnedMissingCoaOrder = false;
let warnedMissingCoaVessel = false;

export function groupCoaEntriesToSequences(
  entries: readonly CoaEntry[],
  orderById: Map<OrderId, ScenarioOrder>,
  scheduleContext: ScheduleDataContext,
  portCallEntries: Map<ClientId, PortCallEntry>,
): CoaEventGroup[] {
  const coaEventGroupsByContractAndVessel: CoaEventGroupsByContractAndVessel =
    new DefaultMap(() => new DefaultMap(() => new DefaultMap(() => [])));

  entries.forEach((entry) => {
    const order = orderById.get(entry.orderId);
    if (!order) {
      if (!warnedMissingCoaOrder) {
        warnedMissingCoaOrder = true;
        console.warn(`Order for coa assignment not found`);
      }
      return;
    }

    const vessel =
      entry.vesselId != null
        ? scheduleContext.vessels.get(entry.vesselId)
        : defaultVessel;
    if (entry.vesselId != null && vessel == null) {
      if (!warnedMissingCoaVessel) {
        warnedMissingCoaVessel = true;
        console.warn(`Vessel for coa assignment not found`);
      }
    }

    const events = deriveCoaSpotEvents(
      entry,
      order,
      scheduleContext.locations,
      vessel,
    );

    const groupKey =
      entry.vesselId != null &&
      vessel != null &&
      vessel !== defaultVessel &&
      consecutiveCoaVoyagesEnabled(vessel)
        ? `vessel-${entry.coaId}-${entry.vesselId}`
        : order.shipmentId != null
          ? `shipment-${order.shipmentId}`
          : `order-${order.id}`;

    coaEventGroupsByContractAndVessel
      .get(entry.coaId)
      .get(entry.vesselId ?? null)
      .get(groupKey)
      .push(...events);
  });

  const result: CoaEventGroup[] = [];

  coaEventGroupsByContractAndVessel.forEach((contractEvents, coaId) =>
    contractEvents.forEach((vesselEvents, vesselId) =>
      vesselEvents.forEach((shipmentEvents, key) => {
        shipmentEvents.sort((a, b) => a.start - b.start);
        ensureLoadBeforeDischarge(shipmentEvents);

        const actualVessel = vesselId
          ? (scheduleContext.vessels.get(vesselId) ?? null)
          : null;

        const withPortCalls = attachPortCalls(
          shipmentEvents,
          actualVessel,
          true,
          scheduleContext,
          portCallEntries,
        );

        result.push({
          key,
          coaId,
          vesselId,
          events: withPortCalls,
        });
      }),
    ),
  );

  return result;
}

type ShipmentOrOrderId = string;
type SpotEventGroupsByOrgAndVessel = DefaultMap<
  OrganizationId | null,
  DefaultMap<ShipId | null, DefaultMap<ShipmentOrOrderId, SpotEvent[]>>
>;
export type SpotEventGroup = {
  key: string;
  shipownerOrgId: OrganizationId | null;
  vesselId: ShipId | null;
  events: (SpotEvent | PortCallEvent)[];
};

let warnedMissingSpotOrder = false;

export function groupSpotEntriesToSequences(
  entries: readonly SpotEntry[],
  orderById: Map<number, ScenarioOrder>,
  scheduleContext: ScheduleDataContext,
  portCallEntries: Map<ClientId, PortCallEntry>,
): SpotEventGroup[] {
  const eventGroupsByOrgAndVessel: SpotEventGroupsByOrgAndVessel =
    new DefaultMap(() => new DefaultMap(() => new DefaultMap(() => [])));

  entries.forEach((spot) => {
    const order = orderById.get(spot.orderId);
    if (!order) {
      if (!warnedMissingSpotOrder) {
        warnedMissingSpotOrder = true;
        console.warn(`Order for spot assignment not found`);
      }
      return;
    }

    const vessel =
      spot.vesselId != null
        ? scheduleContext.vessels.get(spot.vesselId)
        : defaultVessel;

    const events = deriveCoaSpotEvents(
      spot,
      order,
      scheduleContext.locations,
      vessel,
    );

    const groupByVessel = false;

    eventGroupsByOrgAndVessel
      .get(spot.ownerOrganisationId ?? null)
      .get(groupByVessel ? (spot.vesselId ?? null) : null)
      .get(
        order.shipmentId != null
          ? `shipment-${order.shipmentId}`
          : `order-${order.id}`,
      )
      .push(...events);
  });

  const result: SpotEventGroup[] = [];
  eventGroupsByOrgAndVessel.forEach((organizationEvents, shipownerOrgId) => {
    return Array.from(organizationEvents.values()).forEach((vesselEvents) => {
      vesselEvents.forEach((shipmentEvents, key) => {
        shipmentEvents.sort((a, b) => a.start - b.start);
        ensureLoadBeforeDischarge(shipmentEvents);

        const vesselId = shipmentEvents[0].vesselId;
        const vessel = vesselId
          ? (scheduleContext.vessels.get(vesselId) ?? null)
          : null;

        const withPortCalls = attachPortCalls(
          shipmentEvents,
          vessel,
          true,
          scheduleContext,
          portCallEntries,
        );

        result.push({
          key,
          shipownerOrgId,
          vesselId,
          events: withPortCalls,
        });
      });
    });
  });

  return result;
}

export function checkOrderCompatibleWithCoa(
  order: ScenarioOrder,
  coa: ScenarioTimeline_CoaFragment,
  behavior: 'noop' | 'toast' = 'noop',
): boolean {
  const orderAssortmentId = order.assortmentId;
  const orderAssortmentGroupId =
    order.assortmentByAssortmentId?.assortmentGroup;

  const coaAssortment =
    coa.coaAssortments.nodes.filter(isDefined).find((ca) => {
      const isCatchall =
        ca.assortmentId === null && ca.assortmentGroupId === null;
      if (isCatchall) return true;
      const assortmentGroupMatches =
        ca.assortmentGroupId !== null &&
        ca.assortmentGroupId === orderAssortmentGroupId;
      if (assortmentGroupMatches) return true;
      return ca.assortmentId !== null && ca.assortmentId === orderAssortmentId;
    }) ?? null;

  const coaPorts = compact(coa.ports.nodes).find((route) => {
    const originLocations = new Set([
      route.originId,
      ...compact(route.zoneByOriginZoneId?.zoneLocationsByZoneId?.nodes).map(
        (zl) => zl?.locationId,
      ),
    ]);
    const destinationLocations = new Set([
      route.destinationId,
      ...compact(
        route.zoneByDestinationZoneId?.zoneLocationsByZoneId?.nodes,
      ).map((zl) => zl?.locationId),
    ]);

    const originMatches = originLocations.has(order.loadPortId);
    const destinationMatches = destinationLocations.has(order.dischargePortId);

    return originMatches && destinationMatches;
  });

  const errHandler = (msg: string) => {
    if (behavior === 'toast') {
      toast(msg, {
        type: toast.TYPE.ERROR,
      });
      return false;
    } else {
      return false;
    }
  };

  if (coaAssortment === null) {
    return errHandler(
      `Assortment ${order.assortmentByAssortmentId?.name} is not included in COA ${coa.name}`,
    );
  }

  if (coaPorts === null) {
    return errHandler(
      `Route ${order.locationByLoadPortId?.name} - ${order.locationByDischargePortId?.name} is not included in COA ${coa.name}`,
    );
  }

  return true;
}

export const isPortCallEventStartSourced = (event: PortCallEvent): boolean => {
  switch (event.event) {
    case PortCallEventType.Arrival: {
      return !!(
        event.entry.plannedArrivalOverride &&
        event.entry.plannedArrivalOverrideSourcedFrom
      );
    }
    case PortCallEventType.Berthing: {
      return !!(
        event.entry.plannedBerthingOverride &&
        event.entry.plannedBerthingOverrideSourcedFrom
      );
    }
    case PortCallEventType.Departure: {
      return !!(
        event.entry.plannedDepartureOverride &&
        event.entry.plannedDepartureOverrideSourcedFrom
      );
    }
    default: {
      exhaustiveCheck(event);
    }
  }
};

export const tcOrderEntryToTcOrderEvents = (
  entry: TCOrderEntry,
  vessel: Vessel,
  order: ScenarioOrder,
  locations: ScheduleDataContext['locations'],
  opts?: { isNew?: boolean; waitForLaycan?: boolean },
): [LoadEvent & TCOrderEvent, DischargeEvent & TCOrderEvent] | undefined => {
  const { loadPortId, dischargePortId } = order;
  if (loadPortId == null || dischargePortId == null) {
    return;
  }

  const constraints = getOrderConstraints(order);
  const pcs = getOrderPcsOrSet(order);
  const {
    loadingCommenced,
    loadingDuration: sofLoadDuration,
    dischargingCommenced,
    dischargingDuration: sofDischargeDuration,
  } = getSofAndNoticeOverrides(order);

  const loadLocation = assertedDefined(
    locations.get(order.loadPortId!),
    `Expected location ${order.loadPortId} to exist`,
  );
  const dischargeLocation = assertedDefined(
    locations.get(order.dischargePortId),
    `Expected location ${order.dischargePortId} to exist`,
  );

  const load: LoadEvent = {
    category: EventCategory.TC,
    start: entry.loadingStart,
    end: entry.loadingEnd,
    duration: entry.loadingDuration,
    location: loadLocation,
    locationId: loadPortId,
    locationTimeZone: order.locationByLoadPortId?.timezone ?? null,
    type: CoreEventType.Load,
    concurrent: entry.loadingConcurrent,
    startOverride: entry.loadingStartOverride,
    durationOverride: entry.loadingDurationOverride,
    seaDurationOverride: entry.loadingSeaDurationOverride ?? null,
    sofCommenced: loadingCommenced,
    sofDuration: sofLoadDuration,
    entry: entry,
    vesselId: vessel.id,
    vessel,
    order,
    pcs,
    constraints,
    portCallId: entry.loadingPortCallId,
    cargoAllocations: opts?.isNew ? [] : [...(order.cargoAllocations ?? [])],
  };

  const discharge: DischargeEvent = {
    category: EventCategory.TC,
    start: entry.dischargingStart,
    end: entry.dischargingEnd,
    duration: entry.dischargingDuration,
    location: dischargeLocation,
    locationId: dischargePortId,
    locationTimeZone: order.locationByDischargePortId?.timezone ?? null,
    type: CoreEventType.Discharge,
    concurrent: entry.dischargingConcurrent,
    startOverride: entry.dischargingStartOverride,
    durationOverride: entry.dischargingDurationOverride,
    seaDurationOverride: entry.dischargingSeaDurationOverride ?? null,
    sofCommenced: dischargingCommenced,
    sofDuration: sofDischargeDuration,
    entry: entry,
    vesselId: vessel.id,
    vessel,
    constraints,
    order,
    pcs,
    portCallId: entry.dischargingPortCallId,
    cargoAllocations: opts?.isNew ? [] : [...(order.cargoAllocations ?? [])],
  };

  if (opts?.waitForLaycan) {
    load.delayUntil = getEventOperationsWindow(load)?.start;
    discharge.delayUntil = getEventOperationsWindow(discharge)?.start;
  }

  dischargeTimesAfterLoadTimes(load, discharge);

  return [load, discharge];
};

export const portCallEntryToPortCallEvents = (
  entry: PortCallEntry,
  portCall: ScenarioTimeline_PortCallFragment | null,
  vessel: Vessel | null,
  locations: Map<LocationId, ScenarioLocation>,
  // The last two parameters supplement times from the first event because legacy port calls do not have these values (correctly) set.
  // We could remove these if we 1) Update these values in port calls in database AND 2) Make sure the data is also up to date in port call entries locally OR wipe local scenario data again
  firstEventStart: number | null, // planned  computed times may not be correct, prefer planned times from a cargo event
  firstEventSeaDurationOverride: number | null, // legacy port calls do not have a sea duration override, supplement from the first event
  shipmentCount: number,
  isCanalTransitPortCall: boolean,
): {
  a: PortCallArrivalEvent;
  b: PortCallBerthingEvent;
  d: PortCallDepartureEvent;
} => {
  const { commenced: sofCommenced, duration: sofDuration } = portCall
    ? getSofAndNoticeOverridesFromPortCall(portCall)
    : ({} as Record<string, undefined>);
  const sourcedArrival = entry?.plannedArrivalOverrideSourcedFrom
    ? entry.plannedArrivalOverride
    : null;
  const sourcedBerthing = entry?.plannedBerthingOverrideSourcedFrom
    ? entry.plannedBerthingOverride
    : null;

  const arrivalDuration = Math.max(
    sourcedArrival && sourcedBerthing && sourcedBerthing > sourcedArrival
      ? sourcedBerthing - sourcedArrival
      : (entry.plannedArrivalDuration ?? minimumEventDuration),
    minimumEventDuration,
  );

  const berthingDuration = Math.max(
    sourcedBerthing && sofCommenced && sofCommenced > sourcedBerthing
      ? sofCommenced - sourcedBerthing
      : (entry.plannedBerthingDuration ?? minimumEventDuration),
    minimumEventDuration,
  );

  // Timing precedence: 1) Arrival fact 2) min(Berthing fact, Planned override) 3) Start from a cargo event adjusted by duration overrides 4) Computed planned time
  const arrival =
    sourcedArrival ??
    (sourcedBerthing || entry.plannedArrivalOverride
      ? Math.min(
          entry.plannedArrivalOverride ?? Infinity,
          (sourcedBerthing ?? Infinity) - 1,
        )
      : firstEventStart
        ? firstEventStart - arrivalDuration - berthingDuration
        : entry.plannedArrival);

  const berthing = Math.max(
    arrival + arrivalDuration,
    entry.plannedBerthingOverride ??
      (!sourcedArrival
        ? firstEventStart
          ? firstEventStart - berthingDuration
          : entry.plannedBerthing
        : -Infinity),
  );

  const departure = Math.max(
    berthing + berthingDuration,
    entry.plannedDepartureOverride ?? entry.plannedDeparture,
  );

  const departureDuration = minimumEventDuration;

  const locationId = entry.locationId;

  const location = locations.get(locationId);
  assertDefined(
    location,
    `Expected location ${locationId} to exist for port call ${entry.clientId}`,
  );

  const locationName = location.name || 'Unknown port';

  const locationTimeZone = location.timezone ?? null;

  const vesselOrUndefined = vessel ?? undefined;

  const vesselId = entry.vesselId;

  const portCallId = entry.clientId;

  return {
    a: {
      category: EventCategory.PortCall,
      type: CoreEventType.Single,
      entry,
      portCall,
      location,
      locationId,
      locationName,
      locationTimeZone,
      previousLocationId: entry.previousLocationId,
      shipmentCount,
      skipOnSwap: true,
      vesselId,
      constraints: defaultConstraints,
      vessel: vesselOrUndefined,
      portCallId,
      event: PortCallEventType.Arrival,
      start: arrival,
      startOverride: entry.plannedArrivalOverride,
      duration: arrivalDuration,
      end: arrival + arrivalDuration,
      sofCommenced,
      sofDuration,
      seaDurationOverride:
        entry.seaDurationOverride ?? firstEventSeaDurationOverride,
      totalLoadDurationOverride: entry.totalLoadDurationOverride ?? null,
      totalDischargeDurationOverride:
        entry.totalDischargeDurationOverride ?? null,
      legAvoidsChannels: entry.legAvoidsChannels ?? false,
      channelCostDefaultId: entry.channelCostDefaultId ?? null,
      oneTimeOverrideChannelCost: entry.oneTimeOverrideChannelCost ?? null,
      oneTimeOverrideCost: entry.oneTimeOverrideCost ?? null,
      portCostDefaultId: entry.portCostDefaultId ?? null,
      useHistoricalPortCost: entry.useHistoricalPortCost ?? false,
      useLatestHistoricalPortCost: entry.useLatestHistoricalPortCost ?? false,
      useOneTimeOverrideCost: entry.useOneTimeOverrideCost ?? false,
      useIntegrationPortCost: entry.useIntegrationPortCost ?? false,
      channelDurationOverhead: entry.channelDurationOverhead,
      isCanalTransitPortCall,
      weatherFactor: entry.weatherFactor ?? null,
    },
    b: {
      category: EventCategory.PortCall,
      type: CoreEventType.Single,
      entry,
      portCall,
      location,
      locationId,
      locationName,
      locationTimeZone,
      shipmentCount,
      skipOnSwap: true,
      vesselId,
      constraints: defaultConstraints,
      vessel: vesselOrUndefined,
      portCallId,
      event: PortCallEventType.Berthing,
      start: berthing,
      startOverride: entry.plannedBerthingOverride,
      duration: berthingDuration,
      end: berthing + berthingDuration,
      sofCommenced,
      sofDuration,
      seaDurationOverride: null,
      isCanalTransitPortCall,
    },
    d: {
      category: EventCategory.PortCall,
      type: CoreEventType.Single,
      entry,
      portCall,
      location,
      locationId,
      locationName,
      locationTimeZone,
      shipmentCount,
      skipOnSwap: true,
      vesselId,
      constraints: defaultConstraints,
      vessel: vesselOrUndefined,
      portCallId,
      event: PortCallEventType.Departure,
      start: departure,
      startOverride: entry.plannedDepartureOverride,
      duration: departureDuration,
      end: departure + departureDuration,
      sofCommenced,
      sofDuration,
      seaDurationOverride: null,
      isCanalTransitPortCall,
    },
  };
};

/*
Output map from vessel to sorted list of events
*/
export const GroupByVesselSortByTime = (
  schedule: Entry[] | Scenario['schedule'],
  orderById: Map<number, ScenarioOrder>,
  context: ScheduleDataContext,
  portCallEntries: Map<ClientId, PortCallEntry>,
): VesselToSortedEvents => {
  // @TODO: minor cleanup/rewrite on GroupByVesselSortByTime to not replaceContents, instead create new map
  const scheduleMap: VesselToSortedEvents = new Map();
  const vesselMap = context.vessels;

  context.vessels.forEach((vessel) => {
    scheduleMap.set(vessel.id, []);
  });

  schedule.forEach((entry) => {
    const { vesselId } = entry;
    // TODO: https://github.com/Seaber-io/frontend/pull/1710#discussion_r1032390916
    if (typeof vesselId !== 'number') return;

    const vessel = vesselMap.get(vesselId);
    const events = scheduleMap.get(vesselId);
    // TODO: https://github.com/Seaber-io/frontend/pull/1710#discussion_r1032390916
    if (!vessel || !events) return;

    if (isTCOrderEntry(entry)) {
      const { orderId } = entry;

      const order = orderById.get(orderId);
      // TODO: https://github.com/Seaber-io/frontend/pull/1710#discussion_r1032390916
      if (!order) return;

      const createdEvents = tcOrderEntryToTcOrderEvents(
        entry,
        vessel,
        order,
        context.locations,
      );

      if (createdEvents) {
        events.push(...createdEvents);
      }
    } else if (isSingleEntry(entry)) {
      const { locationId } = entry;
      const location = context.locations.get(locationId);

      // TODO: https://github.com/Seaber-io/frontend/pull/1710#discussion_r1032390916
      if (!location) return;

      const event: SingleEvent = singleEntryToSingleEvent(
        entry,
        vessel,
        location,
      );
      events.push(event);
    } else if (isOffHireEntry(entry)) {
      const event = buildOffHireEventFromEntry(
        entry,
        vessel,
        context.locations,
      );
      events.push(event);
    } else if (isSpotEntry(entry)) {
      console.error(`Should not be handling spot entries in this method`);
    } else if (isCoaEntry(entry)) {
      console.error(`Should not be handling coa entries in this method`);
    } else if (isPortCallEntry(entry)) {
      console.error(`Should not be handling port call entries in this method`);
    } else {
      exhaustiveCheck(entry);
    }
  });

  for (const [vesselId, vesselEvents] of scheduleMap) {
    const vessel = context.vessels.get(vesselId);
    assertDefined(vessel, 'vessel data should be available');

    const tcInEvents = getSortedTcInEvents(
      compact(vessel.tcContractsByVesselId.nodes),
      context,
    );

    vesselEvents.push(...tcInEvents);

    sortVesselEvents(vesselEvents);

    ensureLoadBeforeDischarge(vesselEvents);
    const withPortCalls = attachPortCalls(
      vesselEvents,
      vesselEvents[0]?.vessel ?? null,
      true,
      context,
      portCallEntries,
    );
    replaceContents(vesselEvents, withPortCalls);
  }

  return scheduleMap;
};

function getEventSortTimestamp(e: Events): number {
  // We sort TC in events by their end time to maintain proper order
  // when reinserting them as their timestamps cannot be updated
  // by the scenario
  if (isTcInEvent(e)) {
    if (isFirstTcInEvent(e)) {
      // First TC In event should always be at index 0
      return -Infinity;
    }

    if (isLastTcInEvent(e)) {
      // Last TC In event should always be at last index
      return Infinity;
    }

    return e.end;
  }

  return e.start;
}

function getAssortmentKeyParts(order: ScenarioOrder): string[] {
  const assortment = order.assortmentByAssortmentId;
  const parts: string[] = [];
  if (assortment?.name) {
    parts.push(`A${assortment.name}`);
  }
  if (assortment?.assortmentGroupByAssortmentGroup?.name) {
    parts.push(`G${assortment.assortmentGroupByAssortmentGroup.name}`);
  }
  return parts;
}

function countVoyagesBetweenEvents(
  events: Events[],
  startEvent: Events,
  endEvent: Events,
): number {
  const startIndex = events.findIndex(
    (e) => getEventKey(e) === getEventKey(startEvent),
  );
  const endIndex = events.findIndex(
    (e) => getEventKey(e) === getEventKey(endEvent),
  );

  if (startIndex === -1 || endIndex === -1 || startIndex >= endIndex) {
    return 0;
  }

  const eventsBetween = events.slice(startIndex + 1, endIndex);
  const voyageStarts = new Set<string>();

  for (const event of eventsBetween) {
    if (isLoadEvent(event) && event.order.shipmentId) {
      voyageStarts.add(event.order.shipmentId);
    }
  }

  return voyageStarts.size;
}

function applyCleaningEvents(
  events: Events[],
  voyageExclusions: ScheduleDataContext['voyageExclusions'],
): Events[] {
  // Remove all existing cleaning events to rebalance from scratch
  const newEvents: Events[] = events.filter(
    (e) => !(isSingleEvent(e) && e.event === SingleEventType.CLEANING),
  );

  const cleaningRequirements: {
    dischargeEvent: DischargeEvent;
    nextLoadEvent: LoadEvent;
    exclusion: VoyageExclusionItem;
    cleaningDuration: number;
  }[] = [];

  // For each discharge event, find all subsequent load events
  for (let i = 0; i < newEvents.length; i++) {
    const event = newEvents[i];

    if (isDischargeEvent(event)) {
      const dischargeEvent = event;

      // Look for all subsequent load events
      const nextLoadEvents: LoadEvent[] = [];
      for (let j = i + 1; j < newEvents.length; j++) {
        const potentialLoadEvent = newEvents[j];
        if (isLoadEvent(potentialLoadEvent)) {
          nextLoadEvents.push(potentialLoadEvent);
        }
      }

      // Find the immediate next load event
      const nextLoadEvent =
        nextLoadEvents.length > 0 ? nextLoadEvents[0] : null;

      // For each subsequent load event, determine if cleaning is required and find the longest cleaning time
      let longestCleaningDuration = 0;
      let exclusionForLongestCleaning: VoyageExclusionItem | null = null;

      for (const loadEvent of nextLoadEvents) {
        if (
          isTCOrderEvent(loadEvent) &&
          nextLoadEvent &&
          isTCOrderEvent(nextLoadEvent) &&
          !nextLoadEvent.entry.cleaningOmitted
        ) {
          const applicableExclusions = getApplicableCleaningExclusions(
            dischargeEvent,
            loadEvent,
            voyageExclusions,
            newEvents,
          );

          if (applicableExclusions.length > 0) {
            const longestExclusion = applicableExclusions.reduce(
              (max, current) =>
                Number(current.holdCleaningTimeHours) >
                Number(max.holdCleaningTimeHours)
                  ? current
                  : max,
            );

            const cleaningDuration = hToMs(
              Number(longestExclusion.holdCleaningTimeHours),
            );

            // Keep track of the longest cleaning duration among all subsequent load events
            if (cleaningDuration > longestCleaningDuration) {
              longestCleaningDuration = cleaningDuration;
              exclusionForLongestCleaning = longestExclusion;
            }
          }
        }
      }

      // If we found a load event requiring cleaning and have the immediate next load event,
      // add the requirement with the longest duration
      if (
        nextLoadEvent &&
        exclusionForLongestCleaning &&
        longestCleaningDuration > 0
      ) {
        cleaningRequirements.push({
          dischargeEvent,
          nextLoadEvent, // Use the immediate next load event for location info
          exclusion: exclusionForLongestCleaning,
          cleaningDuration: longestCleaningDuration,
        });
      }
    }
  }

  // Insert cleaning events based on the requirements
  for (const requirement of cleaningRequirements) {
    if (!requirement.nextLoadEvent.vessel) {
      continue;
    }
    // Find the correct position to insert the cleaning event
    const dischargeIndex = newEvents.findIndex(
      (e) => getEventKey(e) === getEventKey(requirement.dischargeEvent),
    );
    const loadIndex = newEvents.findIndex(
      (e) => getEventKey(e) === getEventKey(requirement.nextLoadEvent),
    );

    if (
      dischargeIndex !== -1 &&
      loadIndex !== -1 &&
      dischargeIndex < loadIndex
    ) {
      // Insert cleaning event right after the discharge event
      const insertPosition = dischargeIndex + 1;
      const previousEvent = newEvents[dischargeIndex];

      const cleaningEntry = CreateSingleEntry({
        duration: requirement.cleaningDuration,
        end: previousEvent.end + requirement.cleaningDuration,
        type: singleEventTypeToEventType(SingleEventType.CLEANING),
        locationId: requirement.nextLoadEvent.locationId,
        start: previousEvent.end,
        vesselId: requirement.nextLoadEvent.vesselId,
        cleaningAssortmentExclusionId: requirement.exclusion.id,
      });

      const cleaningEvent = singleEntryToSingleEvent(
        cleaningEntry,
        requirement.nextLoadEvent.vessel,
        requirement.nextLoadEvent.location,
      );

      newEvents.splice(insertPosition, 0, cleaningEvent);
    }
  }

  return newEvents;
}

function getApplicableCleaningExclusions(
  dischargeEvent: DischargeEvent,
  loadEvent: LoadEvent,
  voyageExclusions: AssortmentExclusionsMap,
  allEvents: Events[],
): VoyageExclusionItem[] {
  const previousAssortmentKeyParts = getAssortmentKeyParts(
    dischargeEvent.order,
  );
  const nextAssortmentKeyParts = getAssortmentKeyParts(loadEvent.order);

  // Every assortment can have a group, so we need to check all combinations
  // of previous and next assortment and group
  const baseExclusionKeys: string[] = [];
  for (const prevPart of previousAssortmentKeyParts) {
    for (const nextPart of nextAssortmentKeyParts) {
      baseExclusionKeys.push(`${prevPart}-${nextPart}`);
    }
  }

  const vesselCostClassId =
    dischargeEvent.vessel?.vesselAttributeByPrimaryAttributeId?.id;

  const applicableExclusions = [];

  for (const baseExclusionKey of baseExclusionKeys) {
    let exclusion;

    // First check for vessel-specific exclusion
    if (vesselCostClassId) {
      const specificExclusionKey = `${baseExclusionKey}-${vesselCostClassId}`;
      exclusion = voyageExclusions.get(specificExclusionKey);
    }

    // If no vessel-specific exclusion, check for general exclusion
    if (!exclusion) {
      exclusion = voyageExclusions.get(baseExclusionKey);
    }

    if (exclusion?.cleaningPossible) {
      const voyagesCount = countVoyagesBetweenEvents(
        allEvents,
        dischargeEvent,
        loadEvent,
      );
      if (!exclusion.voyages || voyagesCount < exclusion.voyages) {
        applicableExclusions.push(exclusion);
      }
    }
  }

  return applicableExclusions;
}

function sortVesselEvents(vesselEvents: VesselSchedule) {
  vesselEvents.sort(
    (a, b) => getEventSortTimestamp(a) - getEventSortTimestamp(b),
  );
}

export type ScheduleData<EventType extends Events = Events> = {
  timeData: TimeData<EventType>[];
  totals: TimeDataTotals;
  inputs: TimeDataInputs;
};

export type DefinedPrices = NonNullable<
  NonNullable<NonNullable<GlobalStore['bunkerEx']>['prices']>[number]
>;

export type VoyageAttributeById = Map<
  number,
  Pick<VoyageAttribute, 'id' | 'name' | 'color' | 'isActive'>
>;

export function ceilIfEnabled(
  value: number,
  useRoundedEstimations: boolean,
): number {
  return useRoundedEstimations ? Math.ceil(value) : value;
}

export const fallbackBallastSpeed = 11;
export const fallbackLadenSpeed = 11;

export function getDefaultVesselSpeedByType(
  vessel: Vessel | undefined,
  type: VesselSpeedType,
  fallBackSpeed: number,
): number {
  return (
    +(
      vessel?.vesselSpeedConsumptionsByShipId.nodes.find(
        (i) => i?.type === type && i.tag === VesselSpeedTag.DEFAULT,
      )?.speed ?? 0
    ) || fallBackSpeed
  );
}

export function calculateSeaDuration(
  speedLaden: number,
  speedBallast: number,
  distance: number,
  isLaden: boolean,
  roundToNextHour: boolean,
): number {
  const speed = isLaden ? speedLaden : speedBallast;
  const durationH = distance / speed;
  return hToMs(roundToNextHour ? Math.ceil(durationH) : durationH);
}

export type BunkerGradeSelectionPreference = {
  tag: VesselSpeedTag | null | 'match';
  area:
    | VesselArea.ALL_AREAS
    | 'match'
    | 'org_default_for_area'
    | 'vessel_default';
};

/**
 * This function evaluates the route data based on nautical miles inside and outside an ECA. It returns
 * an enumeration value indicating whether the vessel's route is primarily inside the ECA, outside the ECA,
 * or equally distributed across both areas. If `inside_nm` or `outside_nm` are not provided, they are treated as 0.
 *
 * @param {RouteData} routeData
 * @returns {VesselArea}
 */
export function determineVesselArea(
  routeData: RouteData | undefined,
): VesselArea {
  const inside_nm = routeData?.inside_nm ?? 0;
  const outside_nm = routeData?.outside_nm ?? 0;

  if (inside_nm > outside_nm) {
    return VesselArea.INSIDE_ECA;
  } else if (outside_nm > inside_nm) {
    return VesselArea.OUTSIDE_ECA;
  } else {
    return VesselArea.ALL_AREAS;
  }
}

/**
 * This function determines the VesselArea for a port location by checking if the location's coordinates are inside any ECA polygon. It returns
 * an enumeration value indicating whether the port is INSIDE_ECA or OUTSIDE_ECA.
 * If latitude or longitude are not provided, the location is null, or no intersection is found, it returns OUTSIDE_ECA.
 * The function is memoized by location.id for performance optimizations.
 *
 * @param {ScenarioLocation | null} location
 * @returns {VesselArea}
 */
export const determinePortArea = memoize(
  (location: ScenarioLocation | null): VesselArea => {
    if (location?.latitude && location.longitude) {
      const pt = point([Number(location.longitude), Number(location.latitude)]);
      for (const polygon of ecaPolygons.features) {
        if (booleanPointInPolygon(pt, polygon)) {
          return VesselArea.INSIDE_ECA;
        }
      }
    }

    return VesselArea.OUTSIDE_ECA;
  },
  (location) => location?.id,
);

export const workHourPartSchema = z.object({
  __typename: z.literal('WorkHourPart').optional(),
  fromHours: z.number(),
  fromMinutes: z.number(),
  toHours: z.number(),
  toMinutes: z.number(),
});

export const workHourSchema = z.object({
  __typename: z.literal('Workhour').optional(),
  workhours: z.array(workHourPartSchema).nullable(),
});

export const workingHoursSchema = z.tuple([
  workHourSchema,
  workHourSchema,
  workHourSchema,
  workHourSchema,
  workHourSchema,
  workHourSchema,
  workHourSchema,
  workHourSchema,
]);

export type WorkHourPart = z.infer<typeof workHourPartSchema>;
export type WorkHour = z.infer<typeof workHourSchema>;
export type WorkingHours = z.infer<typeof workingHoursSchema>;

export const aroundTheClockWorkHours: WorkHourPart = {
  fromHours: 0,
  fromMinutes: 0,
  toHours: 24,
  toMinutes: 0,
};

const nonWorkingDayWorkHours: WorkHourPart = {
  fromHours: 0,
  fromMinutes: 0,
  toHours: 0,
  toMinutes: 0,
};

export function getDefaultWorkingHours(): WorkingHours {
  return [
    { workhours: [aroundTheClockWorkHours] },
    { workhours: [aroundTheClockWorkHours] },
    { workhours: [aroundTheClockWorkHours] },
    { workhours: [aroundTheClockWorkHours] },
    { workhours: [aroundTheClockWorkHours] },
    { workhours: [aroundTheClockWorkHours] },
    { workhours: [aroundTheClockWorkHours] },
    { workhours: [aroundTheClockWorkHours] },
  ];
}

export function getOrderLaytimeDays(
  action: 'loading' | 'discharge',
  order: ScenarioOrder,
  coasById: ScheduleDataContext['coasById'],
): ShincShex | null {
  const locationId =
    action === 'loading' ? order.loadPortId : order.dischargePortId;
  if (!locationId) return null;

  const voyageCharter =
    order.incomingVoyageCharterRelation?.voyageCharterByVoyageCharterId;
  if (!voyageCharter) return null;

  const voyageCharterLaytimeDays =
    voyageCharter?.voyageCharterPortDataByVoyageCharterId.nodes.find(
      (pd) => pd?.portDatumByPortDataId?.portId === locationId,
    )?.portDatumByPortDataId?.laytimeDays;

  if (voyageCharterLaytimeDays && voyageCharterLaytimeDays.incExUu !== null) {
    return voyageCharterLaytimeDays;
  }

  const coaId =
    voyageCharter?.voyageCharterToContractsByVoyageCharterId?.nodes.at(
      0,
    )?.coaId;

  if (!coaId) return null;

  const coaLaytimeDays = coasById
    .get(coaId)
    ?.portDataRelations.nodes.find((pd) => pd?.portData?.portId === locationId)
    ?.portData?.laytimeDays;

  return coaLaytimeDays ?? null;
}

function laytimeDaysToWorkingHours(laytimeDays: ShincShex): WorkingHours {
  const workingHours: WorkingHours = getDefaultWorkingHours();
  const { ho, incExUu, sa, su } = laytimeDays;

  if (incExUu === IncludedExcludedUnlessUsed.INC) {
    if (sa === false) {
      workingHours[5].workhours = [nonWorkingDayWorkHours];
    }

    if (su === false) {
      workingHours[6].workhours = [nonWorkingDayWorkHours];
    }

    if (ho === false) {
      workingHours[7].workhours = [nonWorkingDayWorkHours];
    }
  }

  if (incExUu === IncludedExcludedUnlessUsed.EX) {
    if (sa === true) {
      workingHours[5].workhours = [nonWorkingDayWorkHours];
    }

    if (su === true) {
      workingHours[6].workhours = [nonWorkingDayWorkHours];
    }

    if (ho === true) {
      workingHours[7].workhours = [nonWorkingDayWorkHours];
    }
  }

  return workingHours;
}

export function calculateWorkShifts(
  locationData: Partial<ScenarioLocation> | null | undefined,
  start: number | null,
  loadingTimeInHours: number,
  laytimeDays: ShincShex | null,
): CalculatedWork {
  const workshifts: WorkShift[] = [];

  if (!locationData) {
    return { duration: loadingTimeInHours, loadingTimeInHours, workshifts };
  }

  const contractWorkingHours = laytimeDays
    ? laytimeDaysToWorkingHours(laytimeDays)
    : null;
  const { id, timezone, workingHours: locationWorkingHours } = locationData;
  const workingHours = contractWorkingHours || locationWorkingHours;

  if (!timezone || !workingHours) {
    return { duration: loadingTimeInHours, loadingTimeInHours, workshifts };
  }
  if (workingHours.length < 7) {
    console.warn('Strange working hours for location: ' + id);
    return { duration: loadingTimeInHours, loadingTimeInHours, workshifts };
  }

  const utc = moment(start || '2021-01-04 08:00:00'); // Monday
  const local = utc.tz(normalizeTimezone(timezone), start === null); // calculate from 8 in the morning local time if null
  const offset = local.utcOffset();

  let localHour = local.hours();
  let localMinute = local.minutes();
  let day = local.isoWeekday() - 1;

  const firstDay = workingHours[day]?.workhours ?? [];
  let whIndex = firstDay.findIndex(
    (wh) =>
      wh != null &&
      (wh.toHours > localHour ||
        (wh.toHours === localHour && wh.toMinutes >= localMinute)),
  );

  let totalHours = 0;
  let remainingWork = loadingTimeInHours;

  while (whIndex < firstDay.length && remainingWork) {
    const { fromHours, fromMinutes, toHours, toMinutes } =
      firstDay[whIndex] ??
      firstDay[firstDay.length - 1] ??
      aroundTheClockWorkHours;
    // Time until start of working hours
    if (
      localHour < fromHours ||
      (localHour === fromHours && localMinute < fromMinutes)
    ) {
      totalHours += fromHours - localHour + (fromMinutes - localMinute) / 60;
      localMinute = fromMinutes;
      localHour = fromHours;
    }

    // Working hours after arrival
    if (
      localHour < toHours ||
      (localHour === toHours && localMinute < toMinutes)
    ) {
      const hoursFirstDay =
        toHours - localHour + (toMinutes - localMinute) / 60;
      const usefulHours = Math.min(hoursFirstDay, remainingWork);

      remainingWork -= usefulHours;
      totalHours += usefulHours;

      workshifts.push({
        day,
        fromHours: localHour,
        fromMinutes: localMinute,
        toHours: localHour + Math.floor(usefulHours),
        toMinutes: (localMinute + (usefulHours % 1) * 60) % 60,
      });

      localMinute = toMinutes;
      localHour = toHours;
    }
    if (whIndex === -1) {
      break;
    } else {
      whIndex++;
    }
  }

  if (workshifts.length === 0) {
    workshifts.push({
      day,
      fromHours: localHour,
      fromMinutes: localMinute,
      toHours: localHour,
      toMinutes: localMinute,
    });
  }

  if (remainingWork) {
    // Time until midnight
    totalHours += 24 - localHour - localMinute / 60;
    localMinute = 0;
    localHour = 0;
  }

  while (remainingWork) {
    whIndex = 0;
    day = (day + 1) % 7;
    const today = workingHours[day]?.workhours ?? [];
    while ((whIndex < today.length || whIndex === 0) && remainingWork) {
      const { fromHours, fromMinutes, toHours, toMinutes } =
        today[whIndex] ?? aroundTheClockWorkHours;

      // Time until start of working hours
      if (
        localHour < fromHours ||
        (localHour === fromHours && localMinute < fromMinutes)
      ) {
        totalHours += fromHours - localHour + (fromMinutes - localMinute) / 60;
      }

      const hours = toHours - fromHours + (toMinutes - fromMinutes) / 60;

      // Full work shift or remaining amount of work
      const usefulHours = Math.min(hours, remainingWork);
      remainingWork -= usefulHours;
      totalHours += usefulHours;

      workshifts.push({
        day,
        fromHours,
        fromMinutes,
        toHours: fromHours + Math.floor(usefulHours),
        toMinutes: (fromMinutes + (usefulHours % 1) * 60) % 60,
      });

      localMinute = toMinutes;
      localHour = toHours;

      whIndex++;
    }

    if (remainingWork) {
      // Evening until midnight
      totalHours += 24 - localHour - localMinute / 60;
      localMinute = 0;
      localHour = 0;
    }
  }

  local.add(totalHours, 'hours');
  const nextOffset = local.utcOffset();
  if (offset !== nextOffset) {
    // Handle change of daylight savings time
    totalHours += (offset - nextOffset) / 60;
  }

  return { duration: totalHours, loadingTimeInHours, workshifts };
}

export const roundToNearestHundred = (value: number) =>
  Math.round(value / 100) * 100;

export const DELAY_CONSTRAINT_HOURS = 24 * 30;

/** When comparing combinations we ignore delays below this threshold */
const DELAY_LATE_THRESHOLD_HOURS = 48;

/**
 * Creates an entry and events for  *new* TC assignment.
 * For existing assignments the events should be created directly from the TC entry alone, not using this method.
 */
export function deriveOrderEvents(
  order: ScenarioOrder,
  vessel: Vessel,
  start: number = Date.now(),
  entryDefaults: Partial<TCOrderEntry>,
  locations: ScheduleDataContext['locations'],
  opts: {
    startIsOverride?: boolean;
    delayUntilEarliestLD?: boolean;
    /** Optionally reuse order events for orderId-vesselId combination. Avoids thousands of allocations during voyage suggestion generation. It's up to the caller to ensure that the events are valid wrt start time and default values. */
    orderEventsCache?: DefaultMap<number, Map<number, TCOrderEvents>>;
    shipmentId?: ClientId | null;
  },
): TCOrderEvents | undefined {
  const cached = opts.orderEventsCache?.get(order.id)?.get(vessel.id);
  if (cached) {
    return cached;
  }
  const { loadPortId, dischargePortId } = order;
  if (loadPortId == null || dischargePortId == null) {
    return undefined;
  }

  const startIsOverride = !!opts.startIsOverride;
  const dischargeStart = start + hToMs(1);

  const entry: TCOrderEntry = {
    dbId: null,
    clientId: generateClientId(),
    orderId: order.id,
    category: EventCategory.TC,
    loadingStart: start,
    loadingEnd: start + hToMs(1),
    loadingDuration: hToMs(1),
    dischargingStart: dischargeStart,
    dischargingEnd: dischargeStart + hToMs(1), // dummy, will be replaced with calculated values later
    dischargingDuration: hToMs(1), // dummy, will be replaced with calculated values later
    loadingStartOverride: startIsOverride ? start : null,
    loadingDurationOverride: null,
    loadingSeaDurationOverride: null,
    dischargingStartOverride:
      startIsOverride && order.rta ? dischargeStart : null,
    dischargingDurationOverride: null,
    dischargingSeaDurationOverride: null,
    loadingConcurrent: false,
    dischargingConcurrent: false,
    shared: false,
    loadingPortCallId: null,
    dischargingPortCallId: null,
    updatedAt: Date.now(),
    vesselId: vessel.id,
    shipmentId:
      opts.shipmentId !== undefined
        ? opts.shipmentId
        : (order.shipmentId ?? null),
    cleaningOmitted: false,
    ...entryDefaults,
  };

  const events = tcOrderEntryToTcOrderEvents(entry, vessel, order, locations, {
    isNew: true,
  });

  if (!events) {
    return undefined;
  }

  const [load, discharge] = events;

  if (opts.delayUntilEarliestLD) {
    load.delayUntil = dateStringToNumOpt(order.layday) ?? start;
    discharge.delayUntil = dateStringToNumOpt(order.rta) ?? undefined;
  }

  if (opts.orderEventsCache) {
    opts.orderEventsCache.get(order.id)?.set(vessel.id, { load, discharge });
  }
  return { load, discharge };
}

/** Returns L/D indices for each given order. Every order expected to be present in events array, but this does not throw an error. */
export function getOrderLDIndices(
  events: TCTimelineEvent[],
  orders: ScenarioOrder[],
): OrderPlacement[] {
  const loadIndexByOrderId = new Map<number, number>();
  const dischargeIndexByOrderId = new Map<number, number>();
  for (let i = 0; i < events.length; i++) {
    const e = events[i];
    if (isLoadEvent(e)) {
      loadIndexByOrderId.set(e.order.id, i);
    } else if (isDischargeEvent(e)) {
      dischargeIndexByOrderId.set(e.order.id, i);
    }
  }

  return filteredMap(orders, (order): OrderPlacement | NONETYPE => {
    const loadIndex = loadIndexByOrderId.get(order.id);
    const dischargeIndex = dischargeIndexByOrderId.get(order.id);
    if (loadIndex === undefined || dischargeIndex === undefined) {
      console.warn(`Order ${order.id} not found in new schedule`);
      return NONE;
    }
    return {
      order,
      loadIndex,
      dischargeIndex,
    };
  });
}

/**
 * When applying a new start time to a group of events the start time should be applied
 * to the first berthing event if port calls events are present, otherwise apply the start time
 * to the first cargo event
 */
export const applyVoyageStart = (
  events: Array<Events>,
  startOverride: number,
): Array<Events> => {
  const hasPortCalls = isPortCallEvent(events[0]);
  const targetIndex = hasPortCalls
    ? events.findIndex(
        (event) =>
          isPortCallEvent(event) && event.event === PortCallEventType.Berthing,
      )
    : 0;

  const newEvents = events.slice(0);
  newEvents[targetIndex] = {
    ...newEvents[targetIndex],
    startOverride,
  };
  return newEvents;
};

/** Which port calls are fully affected & movable/removable when moving/removing given orders from the timeline */
export function getAffectedVoyageEvents(
  tds: TimeData[],
  orderIds: Set<number>,
  isVoyageCollapsed: boolean,
): [affectedTds: TimeData[], remainingTimeline: TimeData[]] {
  const movedCargoTds = tds
    .filter(isCargoEventTimeData)
    .filter((td) => orderIds.has(td.event.order.id));
  const firstMovedCargoTd = first(movedCargoTds);
  const lastMovedCargoTd = last(movedCargoTds);

  if (!firstMovedCargoTd || !lastMovedCargoTd) {
    throw new Error(`Expected there to be order events to move`);
  }

  const movedOrderPortCallIds = new Set(
    movedCargoTds.map((td) => td.event.portCallId).filter(isDefined),
  );
  const arrivalPortCallIdsInTimeRange = new Set(
    tds
      .filter(isPortCallArrivalEventTimeData)
      .filter((td) =>
        isInRange(td.start, {
          start: firstMovedCargoTd.start,
          end: lastMovedCargoTd.end,
        }),
      )
      .map((td) => td.event.portCallId)
      .filter(isDefined),
  );
  const allAffectedPortCallIds = new Set([
    ...movedOrderPortCallIds,
    ...arrivalPortCallIdsInTimeRange,
  ]);

  const cargoEventsByPortCall = mapGroupBy(
    tds.filter(isCargoEventTimeData),
    (td) => td.event.portCallId,
  );

  // If moving part of an expanded shipment, only move the cargo events so that
  // rest of the shipment is not affected; Otherwise move all applicable port calls too.
  const portCallIdsToMove = isVoyageCollapsed
    ? [...allAffectedPortCallIds]
        // Cannot move port calls which contain orders which are not specified for move; everything else is good
        .filter(
          (pcId) =>
            !cargoEventsByPortCall
              .get(pcId)
              ?.some((td) => !orderIds.has(td.event.order.id)),
        )
    : [];

  const isIncludedInPortCallsToMove = (e: TimeData) =>
    e.event.portCallId !== null &&
    portCallIdsToMove.includes(e.event.portCallId);

  const isIncludedInOrdersToMove = (e: TimeData) =>
    isCargoEventTimeData(e) && orderIds.has(e.event.order.id);

  const [movedTds, remainingTimelineWithoutMoved] = partition(
    tds,
    (e) =>
      isIncludedInOrdersToMove(e) ||
      (isVoyageCollapsed && isIncludedInPortCallsToMove(e)),
  );

  return [movedTds, remainingTimelineWithoutMoved];
}

/** Move a set of order assignments to a different time / position on a timeline
 * Assignments will be bundled so that every load is scheduled first, then discharges.
 * The result is a change map of Entries that can be given to createAndUpdateAssignments to update the new assignments into scenario.
 */
export const moveAssignmentsAlongTimeline = (
  timeline: EventTimeline,
  orderIds: Set<number>,
  firstStart: number,
  isCollapsed: boolean,
  context: ScheduleDataContext,
  config: CalculationConfig,
  portCallEntries: Map<ClientId, PortCallEntry>,
  opts: { voyagePlacement: VoyagePlacement },
): ChangedEntries => {
  const fullOriginalTimeData = timeline.voyageGroups.find((v) =>
    v.orders.find((o) => orderIds.has(o.id)),
  )?.events;
  assertDefined(
    fullOriginalTimeData,
    `Expected to find existing timeline events for orders to move`,
  );

  const [existingTds, timeDataWithoutMovedItems] = getAffectedVoyageEvents(
    fullOriginalTimeData,
    orderIds,
    isCollapsed,
  );

  let indexToInsertAt = timeDataWithoutMovedItems.findIndex(
    (event) => +event.start > firstStart,
  );

  if (indexToInsertAt === -1) {
    indexToInsertAt = timeDataWithoutMovedItems.length;
  }

  // Reuse the existing events but clear overrides as they are no longer relevant
  let movedEvents: Events[] = existingTds.map((td, i) => ({
    ...td.event,
    startOverride: null,
    // Do keep the very first sea duration override
    seaDurationOverride: i === 0 ? td.event.seaDurationOverride : null,
  }));

  // Apply the target time so the events land where the user dropped them.
  if (opts.voyagePlacement === 'exact') {
    // Exact time placement: only position based on the first event
    movedEvents = applyVoyageStart(movedEvents, firstStart);
  } else if (opts.voyagePlacement === 'laycan') {
    // Laycan based placement: set start time for every location individually
    movedEvents = groupBySequence(movedEvents, getEventStartLocationId)
      .map((locationEvents) => {
        const operationsStarts = locationEvents.map(
          (e) => getEventOperationsWindow(e)?.start ?? null,
        );
        const minOperationsStart = min(operationsStarts) ?? null;
        return minOperationsStart
          ? applyVoyageStart(locationEvents, minOperationsStart)
          : locationEvents;
      })
      .flat();
  }

  const newAllEvents = timeDataWithoutMovedItems.map((td) => td.event);
  newAllEvents.splice(indexToInsertAt, 0, ...movedEvents);

  return deriveEventModifications(
    newAllEvents,
    fullOriginalTimeData.map((td) => td.event),
    movedEvents[0].vessel ?? null,
    context,
    config,
    portCallEntries,
    { firstChangedIndex: indexToInsertAt },
  );
};

export interface CargoAndMass {
  assortmentName: string;
  mass: number;
}

export type BerthPredictionInputsMap = DefaultMap<
  ClientId,
  Map<'L' | 'D', CachedBerthPredictionInput>
>;

export function getBerthPredictionInputs(
  tds: TimeData[],
  vesselName: string | null,
  imo: number | null,
  isBerthPredictionAvailable: boolean,
): BerthPredictionInputsMap {
  const cargoByPortCall = new DefaultMap<
    ClientId,
    DefaultMap<'L' | 'D', CargoAndMass[]>
  >(() => new DefaultMap<'L' | 'D', CargoAndMass[]>(() => []));
  const berthPredictionInputs = new DefaultMap<
    ClientId,
    Map<'L' | 'D', CachedBerthPredictionInput>
  >(() => new Map<'L' | 'D', CachedBerthPredictionInput>());
  if (!isBerthPredictionAvailable) {
    return berthPredictionInputs;
  }
  if (vesselName == null) {
    return berthPredictionInputs;
  }
  const locationByPortCallId = new Map<ClientId, string>();
  tds.forEach((td) => {
    const { event } = td;
    if (!('type' in event)) {
      return;
    }
    switch (event.type) {
      case CoreEventType.Load:
      case CoreEventType.Discharge: {
        const { type, order, portCallId } = event;
        if (portCallId === null) return;
        const load = type === CoreEventType.Load;
        const location = load
          ? order.locationByLoadPortId
          : order.locationByDischargePortId;
        const locationName = location?.name;
        if (locationName == null) return;
        locationByPortCallId.set(portCallId, locationName);
        const assortmentName = order?.assortmentByAssortmentId?.name;
        if (assortmentName == null) return;
        cargoByPortCall
          .get(portCallId)
          .get(load ? 'L' : 'D')
          .push({
            assortmentName,
            mass: Math.abs(td.mass),
          });
      }
    }
  });
  cargoByPortCall.forEach((byOp, clientId) => {
    byOp.forEach((cargoes, op) => {
      const portName = locationByPortCallId.get(clientId);
      if (!portName) return;
      const input: CachedBerthPredictionInput = {
        cargo: uniq(cargoes.map((cargo) => cargo.assortmentName))
          .sort()
          .join(','),
        operation: op,
        portName: portName,
        vesselName: vesselName,
        vesselImo: imo,
        totalQuantity: Math.round(
          cargoes.reduce((acc, cargo) => acc + cargo.mass, 0),
        ),
      };
      berthPredictionInputs.get(clientId).set(op, { ...input, operation: op });
    });
  });
  return berthPredictionInputs;
}

export const berthPredictionResultIntoMap = (
  inputsArray: BerthPredictionInputsMap,
  result: (CachedBerthPrediction | null)[] | undefined,
): DefaultMap<
  ClientId,
  Map<'L' | 'D', CachedBerthPrediction | null | undefined>
> => {
  const predictions = new DefaultMap<
    ClientId,
    Map<'L' | 'D', CachedBerthPrediction | null | undefined>
  >(() => new Map<'L' | 'D', CachedBerthPrediction | null | undefined>());

  if (!result) {
    return predictions;
  }
  let i = 0;
  inputsArray.forEach((value, clientId) => {
    value.forEach((_input, op) => {
      predictions.get(clientId).set(op, result[i]);
      i++;
    });
  });
  return predictions;
};

export const mergeEntryModifications = (
  original: ChangedEntries,
  updated: ChangedEntries,
): ChangedEntries => {
  return {
    changedCoas: mapMerge(original.changedCoas, updated.changedCoas),
    changedEvents: mapMerge(original.changedEvents, updated.changedEvents),
    changedOffHires: mapMerge(
      original.changedOffHires,
      updated.changedOffHires,
    ),
    changedPortCalls: mapMerge(
      original.changedPortCalls,
      updated.changedPortCalls,
    ),
    changedSpots: mapMerge(original.changedSpots, updated.changedSpots),
    changedTCs: mapMerge(original.changedTCs, updated.changedTCs),
    deletedEvents: updated.deletedEvents,
    deletedPortCalls: new Set([
      ...original.deletedPortCalls,
      ...updated.deletedPortCalls,
    ]),
    updatedTimeData: updated.updatedTimeData,
    changedCargoAllocations: mapMerge(
      original.changedCargoAllocations,
      updated.changedCargoAllocations,
    ),
    changedOrderVoyageCharterIds: mapMerge(
      original.changedOrderVoyageCharterIds,
      updated.changedOrderVoyageCharterIds,
    ),
  };
};

/** Compares two sets of time datas and determines if there have been any changes which could affect berth total durations.
 * Such situations arise from a shipment being handled in another port call due to
 * 1) two port calls merging into one 2) an event moved from a port call to another
 */
export const hasOrdersChangedInPortCalls = (
  prevTds: TimeData[][],
  currentTds: TimeData[][],
): boolean => {
  const prevOrderTdsByPortCall = prevTds.map((tds) =>
    mapKeyBy2(
      tds.filter(isCargoEventTimeData),
      (td) => td.event.portCallId,
      (td) => td.event.order.id,
    ),
  );
  const currentOrderTdsByPortCall = currentTds.map((tds) =>
    mapKeyBy2(
      tds.filter(isCargoEventTimeData),
      (td) => td.event.portCallId,
      (td) => td.event.order.id,
    ),
  );

  // Check each port call present in current timedata:
  for (
    let timelineNum = 0;
    timelineNum < currentOrderTdsByPortCall.length;
    ++timelineNum
  ) {
    for (const [pcCId, pcTds] of currentOrderTdsByPortCall[timelineNum]) {
      const prevPcTds = prevOrderTdsByPortCall[timelineNum].get(pcCId);

      // 1. These must be the same number of orders present in the port call
      if (prevPcTds.size !== pcTds.size) {
        return true;
      }

      // 2. Every order in the current set must already have been there in the previous set
      for (const [orderId] of pcTds) {
        if (!prevPcTds.has(orderId)) {
          return true;
        }
      }
    }
  }

  return false;
};

/** Returns berth prediction for each input. Tries to use the berth prediction cache if possible, otherwise queries the API. */
export const cachedFetchBerthPredictions = async (
  client: ApolloClient<unknown>,
  inputs: CachedBerthPredictionInput[],
): Promise<(CachedBerthPrediction | null)[]> => {
  const predictionsInfo = await Promise.all(
    inputs.map((input, inputIndex) =>
      getCachedPrediction(input)
        .catch((err) => {
          console.error('Cached prediction fetch failed', err);
          return null;
        })
        .then((cachedPrediction) => ({
          cachedPrediction,
          input,
          inputIndex,
          queryInput: input,
          fetchedPrediction: null as null | CachedBerthPrediction,
        })),
    ),
  );

  const needToFetchInputs = predictionsInfo.filter(
    (value) => !value.cachedPrediction,
  );

  if (needToFetchInputs.length === 0) {
    return predictionsInfo.map((value) => value.cachedPrediction);
  }

  const { data } = await client.query<
    BerthPredictionsQueryResult['data'],
    BerthPredictionsQueryVariables
  >({
    query: BerthPredictionsDocument,
    variables: {
      inputs: needToFetchInputs.map((request) => request.queryInput),
    },
  });

  if (data?.GetBerthPredictions) {
    needToFetchInputs.forEach((request, requestIndex) => {
      const result = cachedBerthPredictionResultSchema.parse({
        input: request.input,
        mapping:
          data.GetBerthPredictions?.predictions?.[requestIndex]
            ?.InputUsedByTheModel,
        prediction: {
          pred: data.GetBerthPredictions?.predictions?.[requestIndex]
            ?.PredictedBerthTime,
        },
        cachedAt: Date.now(),
        quantityThreshold: QUANTITY_GROUPING_THRESHOLD,
      } as CachedBerthPrediction);
      saveCachedPredictions(result).catch((e) => console.warn(e));
      predictionsInfo[request.inputIndex].fetchedPrediction = result;
    });
  }

  return predictionsInfo.map(
    (value) => value.fetchedPrediction || value.cachedPrediction,
  );
};

/** Fetches and applies berth predictions for port calls related to given orders and recomputes the timeline.
 * Returns a new, updated modification set.
 */
export const updateBerthDurationsFromAPI = async (
  client: ApolloClient<unknown>,
  entryModifications: ChangedEntries,
  isBerthPredictionAvailable: boolean,
  context: ScheduleDataContext,
  config: CalculationConfig,
  portCallEntries: Map<ClientId, PortCallEntry>,
): Promise<ChangedEntries> => {
  if (!isBerthPredictionAvailable) {
    return entryModifications;
  }

  let updatedModifications = entryModifications;
  /** All port calls which had new/changed entries in them. At the time of writing,
   * this method isn't called for Spot/COA but taking care of those here anyway.  */
  const affectedPortCalls = new Set(
    [
      ...entryModifications.changedTCs.values(),
      ...entryModifications.changedSpots.values(),
      ...entryModifications.changedCoas.values(),
    ].flatMap((e) => [e.loadingPortCallId, e.dischargingPortCallId]),
  );

  for (const timeData of entryModifications.updatedTimeData) {
    const vessel = timeData.find((td) => td.event.vessel !== undefined)?.event
      .vessel;

    const berthPredictionInputs = getBerthPredictionInputs(
      flatten(entryModifications.updatedTimeData).filter(
        (td) =>
          td.event.portCallId && affectedPortCalls.has(td.event.portCallId),
      ),
      vessel?.name ?? null,
      stringToNumOpt(vessel?.imo) ?? null,
      isBerthPredictionAvailable,
    );

    const berthPredictionInputArray = mapFlatmap(
      berthPredictionInputs,
      (value) => mapMap(value, (v) => v),
    );

    const berthPredictionData = await cachedFetchBerthPredictions(
      client,
      berthPredictionInputArray,
    );

    const predictionResults = berthPredictionResultIntoMap(
      berthPredictionInputs,
      berthPredictionData,
    );

    const updatedEvents = timeData.map((td) => {
      if (
        isPortCallArrivalEvent(td.event) &&
        td.event.portCallId &&
        predictionResults.has(td.event.portCallId)
      ) {
        const dischargePred =
          predictionResults.get(td.event.portCallId).get('D')?.prediction
            .pred ?? null;
        const loadPred =
          predictionResults.get(td.event.portCallId).get('L')?.prediction
            .pred ?? null;
        return {
          ...td.event,
          totalDischargeDurationOverride:
            dischargePred !== null
              ? hToMs(Math.ceil(dischargePred))
              : td.event.totalDischargeDurationOverride,
          totalLoadDurationOverride:
            loadPred !== null
              ? hToMs(Math.ceil(loadPred))
              : td.event.totalLoadDurationOverride,
        };
      } else {
        return td.event;
      }
    });

    // Derive what changed because of total durations
    const predictionModifications = deriveEventModifications(
      updatedEvents,
      timeData.map((td) => td.event),
      vessel ?? null,
      context,
      config,
      portCallEntries,
    );

    // Merge total duration caused changes to whatever else was already changed
    updatedModifications = mergeEntryModifications(
      updatedModifications,
      predictionModifications,
    );
  }

  return updatedModifications;
};

/** Create one or more TC order assignments (scheduled orders).
 * The result is a change map of Entries that can be given to createAndUpdateAssignments to update the new assignments into scenario.
 */
export const createTCOrderAssignments = (
  originalEvents: VesselSchedule,
  assignments: Array<{
    order: ScenarioOrder;
    shared?: boolean;
    params: Partial<TCOrderEntry>;
  }>,
  vessel: Vessel,
  start: number,
  context: ScheduleDataContext,
  config: CalculationConfig,
  portCallEntries: Map<ClientId, PortCallEntry>,
  opts: {
    shipWithOrderId: Order['id'] | null;
    voyagePlacement: VoyagePlacement;
    /** Predefined ordering for events to be created (provided when moving events) */
    suggestedEventsOrdering?: Events[];
    allocationOptions: CargoAllocationOptions;
    singleEventsInfo?: Array<{
      singleEvents: SingleEvent[];
      preTCEvent: LoadEvent | DischargeEvent;
      nextTCEvent: LoadEvent | DischargeEvent;
    }>;
  },
  violationBasis: ViolationIndicatorBasis,
  specialRequirements: ReturnType<typeof useSpecialRequirements>,
): ChangedEntries | null => {
  const { timeData: originalTimeData } = DeriveCapacitiesAndTimes(
    vessel,
    originalEvents,
    context,
    config,
    { calculateCosts: true },
  );

  const bounds = computePlacementBounds(
    originalEvents,
    originalTimeData,
    start,
    opts.shipWithOrderId,
  );

  let newAllEvents = originalEvents.slice();

  if (bounds.arrivalResetIndex !== null) {
    resetArrivalDurations(bounds.arrivalResetIndex, newAllEvents, config);
  }

  const allAvailableOrderIds = new Set(assignments.map((a) => a.order.id));

  const result = assignOrders(
    assignments,
    originalEvents,
    newAllEvents,
    vessel,
    start,
    context,
    config,
    bounds.minimumIndex,
    bounds.maximumIndex,
    opts.allocationOptions,
    opts.voyagePlacement,
    bounds.shipmentId,
    allAvailableOrderIds,
    violationBasis,
    specialRequirements,
  );

  if (!result) {
    return null;
  }

  newAllEvents = result.schedule;
  const firstChangedIndex = result.firstChangedIndex;

  if (opts.singleEventsInfo && opts.singleEventsInfo.length > 0) {
    const eventsWithSingles = insertSingleEvents(
      newAllEvents,
      opts.singleEventsInfo,
      allAvailableOrderIds,
      start,
      vessel,
      context,
    );
    if (!eventsWithSingles) {
      return null;
    }
    newAllEvents = eventsWithSingles;
  }

  // If we were given a suggested order for events, we compare it to the one we just computed and use the one with shorter distance
  if (opts.suggestedEventsOrdering) {
    newAllEvents = applySuggestedEventsOrder(
      opts.suggestedEventsOrdering,
      newAllEvents,
      vessel,
      context,
      config,
    );
  }

  // The first one has a start override so that it will appear on the timeline exactly where the user dropped it
  // This is not required when adding onto an existing shipment
  if (
    opts.shipWithOrderId === null &&
    assignments.length > 0 &&
    opts.voyagePlacement === 'exact'
  ) {
    newAllEvents[bounds.minimumIndex].startOverride = start;
  }

  validateCargoAllocations(newAllEvents, assignments, vessel);

  return deriveEventModifications(
    newAllEvents,
    originalEvents,
    vessel,
    context,
    config,
    portCallEntries,
    { firstChangedIndex },
  );
};

/** Test a predefined ordering for events and rearrange the events if the suggested version is better */
function applySuggestedEventsOrder(
  /** Predefined ordering for events to be created (provided when moving events) */
  suggestedEventsOrdering: Events[],
  newAllEvents: TCTimelineEvent[],
  vessel: Vessel,
  context: ScheduleDataContext,
  config: CalculationConfig,
) {
  const eventsWithIndex = suggestedEventsOrdering
    .filter(isCargoEvent)
    .map((a, i) => [a, i] as const);
  const orderIndices = mapKeyBy2(
    eventsWithIndex,
    ([e]) => e.entry.orderId,
    ([e]) => e.type,
  );
  const suggestedResult = [...newAllEvents];

  // Any events that were present in the suggested sequence we sort relative to each other, others are not affected
  suggestedResult.sort((a, b) => {
    const aIndex: number | undefined = isCargoEvent(a)
      ? orderIndices.get(a.entry.orderId).get(a.type)?.[1]
      : undefined;
    const bIndex: number | undefined = isCargoEvent(b)
      ? orderIndices.get(b.entry.orderId).get(b.type)?.[1]
      : undefined;
    return aIndex === undefined || bIndex === undefined ? 0 : aIndex - bIndex;
  });

  const { totals: defaultTotals } = DeriveCapacitiesAndTimes(
    vessel,
    newAllEvents,
    context,
    config,
    { calculateCosts: true },
  );

  const { totals: suggestedTotals } = DeriveCapacitiesAndTimes(
    vessel,
    suggestedResult,
    context,
    config,
    { calculateCosts: true },
  );

  if (suggestedTotals.distance < defaultTotals.distance) {
    newAllEvents = suggestedResult;
  }
  return newAllEvents;
}

/**
 * Splice a load/discharge pair into a vessel schedule and return
 * recomputed TimeData for each event. Used by both TC and
 * consecutive-COA insertion paths.
 */
const insertCargoPairAndDeriveTimings = <E extends Events>(
  vessel: Vessel,
  events: readonly E[],
  load: E,
  discharge: E,
  insertAt: number,
  context: ScheduleDataContext,
  config: CalculationConfig,
  calculateCosts: boolean,
): { loadTd: TimeData<E>; dischargeTd: TimeData<E> } | null => {
  const schedule = [
    ...events.slice(0, insertAt),
    load,
    discharge,
    ...events.slice(insertAt),
  ];
  const { timeData } = DeriveCapacitiesAndTimes(
    vessel,
    schedule,
    context,
    config,
    {
      calculateCosts,
    },
  );
  const loadTd = timeData[insertAt];
  const dischargeTd = timeData[insertAt + 1];
  if (!loadTd || !dischargeTd) return null;
  return { loadTd, dischargeTd };
};

/* Creates a base entry for a new TC assignment. */
export const CreateScheduleEntry = (
  events: VesselSchedule,
  order: ScenarioOrder,
  vessel: Vessel,
  start: number,
  context: ScheduleDataContext,
  config: CalculationConfig,
  params: Partial<TCOrderEntry>,
  startIsOverride = false,
): TCOrderEntry | undefined => {
  const orderEvents = deriveOrderEvents(
    order,
    vessel,
    start,
    params ?? {},
    context.locations,
    {
      startIsOverride,
    },
  );
  if (!orderEvents) {
    return undefined;
  }
  const { load, discharge } = orderEvents;

  const { timeData: originalTimeData } = DeriveCapacitiesAndTimes(
    vessel,
    events,
    context,
    config,
    { calculateCosts: true },
  );

  const targetIndex = originalTimeData.findIndex(
    (event) => +event.start > start,
  );

  const loadIndex = targetIndex !== -1 ? targetIndex : events.length;

  const derived = insertCargoPairAndDeriveTimings(
    vessel,
    events,
    load,
    discharge,
    loadIndex,
    context,
    config,
    true,
  );
  if (!derived) return undefined;
  const { loadTd: loadTimeData, dischargeTd: dischargeTimeData } = derived;

  return {
    dbId: null,
    clientId: generateClientId(),
    category: EventCategory.TC,
    loadingStart: loadTimeData.start,
    loadingStartOverride: startIsOverride ? start : null,
    loadingEnd: loadTimeData.end,
    loadingDuration: loadTimeData.end - loadTimeData.start,
    loadingDurationOverride: null,
    loadingSeaDurationOverride: null,
    dischargingStart: dischargeTimeData.start,
    dischargingStartOverride: null,
    dischargingEnd: dischargeTimeData.end,
    dischargingDuration: dischargeTimeData.end - dischargeTimeData.start,
    dischargingDurationOverride: null,
    dischargingSeaDurationOverride: null,
    loadingConcurrent: params?.loadingConcurrent ?? false,
    dischargingConcurrent: params?.dischargingConcurrent ?? false,
    orderId: order.id,
    vesselId: vessel.id,
    shared: false,
    loadingPortCallId: null,
    dischargingPortCallId: null,
    updatedAt: Date.now(),
    shipmentId: order.shipmentId ?? null,
    cleaningOmitted: false,
    // Using the same TimeData -> Entry patch code everywhere, ensuring every field is derived the same way
    ...(isCargoEventTimeData(loadTimeData) &&
      cargoEntryUpdateFromTimedata(loadTimeData)),
    ...(isCargoEventTimeData(dischargeTimeData) &&
      cargoEntryUpdateFromTimedata(dischargeTimeData)),
  };
};

/* Creates a base entry for a new COA assignment. Default timings are not computed or realistic. */
export const CreateCOAEntry = (
  order: ScenarioOrder,
  opts: { startTime: number; endTime: number; startIsOverride?: boolean },
  params: Pick<CoaEntry, 'coaId' | 'vesselId'> & Partial<CoaEntry>,
): CoaEntry => {
  return {
    category: EventCategory.COA,
    dbId: null,
    clientId: generateClientId(),
    orderId: order.id,
    metadata: '',
    ...initDefaultCoaSpotEntryTimes(
      opts.startTime,
      opts.endTime,
      opts.startIsOverride ?? false,
    ),
    loadingPortCallId: null,
    dischargingPortCallId: null,
    updatedAt: Date.now(),
    ...params,
    shared: params.shared ?? false,
    loadingConcurrent: params.loadingConcurrent ?? false,
    dischargingConcurrent: params.dischargingConcurrent ?? false,
  };
};

export const computeConsecutiveCoaInsertion = (
  vessel: Vessel,
  existingEvents: readonly Events[],
  order: ScenarioOrder,
  baseEntry: CoaEntry,
  requestedStart: number,
  voyagePlacement: VoyagePlacement,
  context: ScheduleDataContext,
  config: CalculationConfig,
): {
  loadStart: number;
  loadEnd: number;
  dischargeStart: number;
  dischargeEnd: number;
  load: Events;
  discharge: Events;
} | null => {
  const lastEnd = existingEvents.at(-1)?.end ?? 0;
  // Laycan placement snaps each order to its own loadingWindow so multi-order
  // batches don't all chain-ASAP off the previous voyage. We pin via
  // loadingStartOverride (DCAT otherwise chains the cargo right after the
  // last event and ignores the input loadingStart).
  const laycanStart =
    voyagePlacement === 'laycan' ? (order.loadingWindow?.start ?? null) : null;
  const start =
    voyagePlacement === 'exact'
      ? requestedStart
      : Math.max(laycanStart ?? requestedStart, lastEnd, Date.now());

  const loadDuration = baseEntry.loadingDuration;
  const dischargeDuration = baseEntry.dischargingDuration;
  const [load, discharge] = deriveCoaSpotEvents(
    {
      ...baseEntry,
      loadingStart: start,
      loadingEnd: start + loadDuration,
      dischargingStart: start + loadDuration + 1,
      dischargingEnd: start + loadDuration + 1 + dischargeDuration,
      loadingStartOverride:
        voyagePlacement === 'exact' || voyagePlacement === 'laycan'
          ? start
          : null,
    },
    order,
    context.locations,
    vessel,
  );
  if (!load || !discharge) return null;

  const targetIdx = existingEvents.findIndex((e) => e.start > start);
  const insertAt = targetIdx === -1 ? existingEvents.length : targetIdx;

  const derived = insertCargoPairAndDeriveTimings(
    vessel,
    existingEvents,
    load,
    discharge,
    insertAt,
    context,
    config,
    false,
  );
  if (!derived) return null;
  const { loadTd, dischargeTd } = derived;

  // Caller may splice these back into a local tail for chained batch
  // insertions; rewrite start/end so `tail.at(-1)?.end` reflects derived
  // timings, since DCAT does not mutate the event objects themselves.
  return {
    loadStart: loadTd.start,
    loadEnd: loadTd.end,
    dischargeStart: dischargeTd.start,
    dischargeEnd: dischargeTd.end,
    load: { ...loadTd.event, start: loadTd.start, end: loadTd.end },
    discharge: {
      ...dischargeTd.event,
      start: dischargeTd.start,
      end: dischargeTd.end,
    },
  };
};

export const singleEventTypeToEventType = (
  type: SingleEventType,
): EventType => {
  switch (type) {
    case SingleEventType.MANEUVERING:
      return EventType.MANEUVERING;
    case SingleEventType.BUNKERING:
      return EventType.BUNKERING;
    case SingleEventType.CLEANING:
      return EventType.CLEANING;
    case SingleEventType.DRIFTING:
      return EventType.DRIFTING;
    case SingleEventType.WAITING:
      return EventType.WAITING;
    case SingleEventType.CONGESTION:
      return EventType.CONGESTION;
    case SingleEventType.PASSING:
      return EventType.PASSING;
    case SingleEventType.REPAIR:
      return EventType.REPAIR;
    case SingleEventType.CANAL_TRANSIT:
      return EventType.CANAL_TRANSIT;
    default:
    case SingleEventType.OTHER:
      return EventType.OTHER;
  }
};

type TimingOverrides = {
  startOverride: Constraint;
  seaDurationOverride: number | null;
};

const assignEventTimingOverrides = <T extends Events>(
  overrides: TimingOverrides,
  e: T,
): T => {
  const { startOverride, seaDurationOverride } = overrides;
  if (isSingleEvent(e)) {
    return {
      ...e,
      startOverride: startOverride,
      seaDurationOverride: seaDurationOverride,
      entry: {
        ...e.entry,
        startOverride: startOverride,
        seaDurationOverride: seaDurationOverride,
      },
    };
  } else if (isTCOrderEvent(e) || isCoaEvent(e) || isSpotEvent(e)) {
    return {
      ...e,
      startOverride: startOverride,
      seaDurationOverride: seaDurationOverride,
      entry: e.entry && {
        ...e.entry,
        ...(e.type === CoreEventType.Load && {
          loadingSeaDurationOverride: seaDurationOverride,
          loadingStartOverride: startOverride,
        }),
        ...(e.type === CoreEventType.Discharge && {
          dischargingSeaDurationOverride: seaDurationOverride,
          dischargingStartOverride: startOverride,
        }),
      },
    };
  } else if (isOffHireEvent(e) || isStaticEvent(e)) {
    return {
      ...e,
      startOverride: null,
      seaDurationOverride: null,
    };
  } else if (isPortCallEvent(e)) {
    return {
      ...e,
    };
  } else {
    throw new Error(
      `Unexpected event type in ${JSON.stringify(
        e,
      )}. This shouldn't have happened.`,
    );
  }
};

/** Inspects new and old events array and for any deleted items applies timing overrides onto the next remaining event instead */
const transferDeletedEventsTimingOverrides = <T extends Events>(
  newEvents: T[],
  oldEvents: T[],
): T[] => {
  const oldEventIndices = mapKeyBy2(
    oldEvents.map((e, i) => [e, i] as const),
    ([e]) => e.type,
    ([e]) => e.entry.clientId,
  );
  const newEventIndices = mapKeyBy2(
    newEvents.map((e, i) => [e, i] as const),
    ([e]) => e.type,
    ([e]) => e.entry.clientId,
  );

  return newEvents.map((e) => {
    const oldIndex = oldEventIndices.get(e.type).get(e.entry.clientId)?.[1];

    if (oldIndex === undefined) {
      // New event
      return e;
    }
    const oldPrecedingEvent = get(oldEvents, oldIndex - 1);
    if (!oldPrecedingEvent) {
      // First event in the array
      return e;
    }

    const wasDeleted = !newEventIndices
      .get(oldPrecedingEvent.type)
      .has(oldPrecedingEvent.entry.clientId);
    if (wasDeleted) {
      return assignEventTimingOverrides(oldPrecedingEvent, { ...e });
    } else {
      return e;
    }
  });
};

const clearEventTimingOverrides = <T extends Events>(e: T): T => {
  return assignEventTimingOverrides(
    { startOverride: null, seaDurationOverride: null },
    e,
  );
};

export const CreateSingleEntry = (
  params: Pick<
    SingleEntry,
    'vesselId' | 'start' | 'duration' | 'locationId' | 'type'
  > &
    Partial<SingleEntry>,
): SingleEntry => {
  return {
    dbId: null,
    clientId: generateClientId(),
    category: EventCategory.Single,
    locked: false,
    meta: '',
    concurrent: false,
    shared: false,
    updatedAt: Date.now(),
    ...params,
    end: params.start + hToMs(params.duration),
    vesselId: params.vesselId,
    startOverride: params.startOverride ?? null,
    seaDurationOverride: params.seaDurationOverride ?? null,
    portCallId: params.portCallId ?? null,
  };
};

export const CreateOffHire = (
  params: Pick<
    OffHireEntry,
    | 'start'
    | 'end'
    | 'locationAId'
    | 'locationBId'
    | 'type'
    | 'vesselId'
    | 'planningOrganisationId'
    | 'userId'
    | 'tcOutRate'
    | 'commissionPct'
    | 'ballastBonus'
    | 'ballastBonusCommissionable'
  > &
    Partial<OffHireEntry>,
): OffHireEntry => {
  return {
    start: params.start,
    end: params.end,
    duration: params.end - params.start,
    locationAId: params.locationAId,
    locationBId: params.locationBId,
    type: params.type,
    category: EventCategory.OffHire,
    dbId: params.dbId ?? null,
    clientId: params.clientId ?? generateClientId(),
    vesselId: params.vesselId,
    locked: params.locked ?? false,
    shared: params.shared ?? false,
    planningOrganisationId: params.planningOrganisationId,
    userId: params.userId,
    updatedAt: params.updatedAt ?? Date.now(),
    tcOutRate: params.tcOutRate,
    commissionPct: params.commissionPct ?? null,
    ballastBonus: params.ballastBonus ?? null,
    ballastBonusCommissionable: params.ballastBonusCommissionable ?? false,
  };
};

export const singleEntryToSingleEvent = (
  entry: SingleEntry,
  vessel: Vessel,
  location: ScenarioLocation,
): SingleEvent => {
  const locationName = location.name ?? '';
  const locationTimeZone = location.timezone ?? null;

  return {
    category: EventCategory.Single,
    start: entry.start,
    end: entry.end,
    locationId: entry.locationId,
    type: CoreEventType.Single,
    concurrent: entry.concurrent,
    startOverride: entry.startOverride,
    seaDurationOverride: entry.seaDurationOverride,
    vesselId: vessel.id,
    vessel,
    constraints: defaultConstraints,
    entry,
    duration: entry.duration,
    location,
    locationName,
    locationTimeZone,
    event: SingleEventType[entry.type],
    portCallId: entry.portCallId,
    cleaningAssortmentExclusionId: entry.cleaningAssortmentExclusionId,
  };
};

export const createSingleEventEntry = (
  eventsByVessel: VesselToSortedEvents | undefined,
  vessel: Vessel,
  type: SingleEventType,
  locationId: LocationId,
  locationName: string,
  locationTimeZone: string | null,
  duration: number,
  context: ScheduleDataContext,
  index: number,
  start: number,
  config: CalculationConfig,
  portCallEntries: Map<ClientId, PortCallEntry>,
): { entry: SingleEntry } & ModificationResult => {
  const vesselId = vessel.id;
  const events = eventsByVessel?.get(vesselId) ?? [];
  if (index === -1) {
    index = events.findIndex((e) => e.start >= start);
    if (index === -1) {
      index = events.length;
    }
  }
  const nextEvent = get(events, index);

  const entry: SingleEntry = CreateSingleEntry({
    type: singleEventTypeToEventType(type),
    start,
    duration,
    locationId,
    startOverride: nextEvent?.startOverride,
    seaDurationOverride: nextEvent?.seaDurationOverride,
    portCallId:
      nextEvent &&
      'locationId' in nextEvent &&
      nextEvent.locationId === locationId
        ? nextEvent.portCallId
        : null,
  });

  const location = assertedDefined(
    context.locations.get(locationId),
    `Expected location ${locationId} to exist`,
  );

  const singleEvent: SingleEvent = assignEventTimingOverrides(
    nextEvent ?? {
      startOverride: null,
      seaDurationOverride: null,
    },
    {
      category: EventCategory.Single,
      start,
      end: start + duration,
      location,
      locationId,
      locationTimeZone,
      type: CoreEventType.Single,
      startOverride: nextEvent?.startOverride ?? null,
      durationOverride: undefined,
      seaDurationOverride: nextEvent?.seaDurationOverride ?? null,
      vesselId,
      vessel,
      constraints: defaultConstraints,
      duration,
      locationName,
      event: type,
      concurrent: entry.concurrent,
      entry,
      portCallId: entry.portCallId,
      weatherFactor: null,
    },
  );

  const schedule = events.slice();

  schedule.splice(index, 0, singleEvent);
  if (nextEvent) {
    schedule[index + 1] = clearEventTimingOverrides(nextEvent);
  }

  const changeMaps = deriveEventModifications(
    schedule,
    events,
    vessel,
    context,
    config,
    portCallEntries,
  );

  return {
    entry,
    ...changeMaps,
  };
};

/** Create or update an OffHire on the timeline, recompute the timeline in case anything else gets changed */
export const createOffHireEntry = (
  eventsByVessel: VesselToSortedEvents | undefined,
  vessel: Vessel,
  offHire: OffHireEntry,
  context: ScheduleDataContext,
  config: CalculationConfig,
  portCallEntries: Map<ClientId, PortCallEntry>,
): ModificationResult => {
  const vesselId = vessel.id;
  const entry: OffHireEntry = offHire;

  const event: OffHireEvent = buildOffHireEventFromEntry(
    entry,
    vessel,
    context.locations,
  );

  const events = eventsByVessel?.get(vesselId) ?? [];
  const existingIndex = events.findIndex(
    (e) => e.entry.clientId === entry.clientId,
  );
  const foundIndex =
    existingIndex > -1
      ? existingIndex
      : events.findIndex((e) => e.start > entry.start);
  const index = foundIndex === -1 ? events.length : foundIndex;
  const schedule = events.slice();
  const isNew = existingIndex === -1;
  schedule.splice(index, isNew ? 0 : 1, event);

  return deriveEventModifications(
    schedule,
    events,
    vessel,
    context,
    config,
    portCallEntries,
  );
};

/** Total idle in ms. Does not count time spent during events, only between. */
export function countTotalIdleTime(tds: TimeData[], fromIndex: number): number {
  const tdBefore = get(tds, fromIndex - 1) ?? get(tds, fromIndex);
  const lastTd = get(tds, tds.length - 1);
  if (!tdBefore || !lastTd) {
    throw new Error(`Expected there to be events to count total time for`);
  }

  return sum(
    tds
      .map(
        (td, i) =>
          (i === 0 ? 0 : td.start - tds[i - 1].end) - hToMs(td.hoursAtSea),
      )
      .slice(fromIndex),
  );
}

/** Return sum of delays for each order's load&discharge */
export const countTotalConstrainedDelayByOrder = (
  tds: TimeData[],
): Map<number, number> => {
  const result = new Map<number, number>();
  for (const td of tds) {
    if (isCargoEventTimeData(td)) {
      const order = td.event.order;
      const delay = Math.abs(td.signedDelays);
      const newTotal =
        (result.get(order.id) ?? 0) + Math.min(DELAY_CONSTRAINT_HOURS, delay);
      result.set(order.id, newTotal);
    }
  }
  return result;
};

/** Returns the highest single violation duration in *ms* of each kind
 * 'early' is in relation to the loading/discharging window start, 'late' is in relation to the window end.
 * Both are measured from the event start.
 */
export function getMaxLDViolations(
  tds: TimeData[],
  violationBasis: ViolationIndicatorBasis,
): {
  load: { early: number; late: number };
  discharge: { early: number; late: number };
} {
  return tds.reduce(
    (maxViolation, td) => {
      if (isLoadEventTimeData(td) || isDischargeEventTimeData(td)) {
        const window = isLoadEventTimeData(td)
          ? td.event.order.loadingWindow
          : td.event.order.dischargingWindow;

        const ts =
          violationBasis === 'eta'
            ? td.arriveInPort
            : (td.operationsStart ?? td.arriveInPort);

        const key = isLoadEventTimeData(td) ? 'load' : 'discharge';

        if (window.start !== undefined) {
          const earlyViolation = window.start - ts;
          maxViolation[key].early = Math.max(
            maxViolation[key].early,
            earlyViolation,
          );
        }

        if (window.end !== undefined) {
          const lateViolation = ts - window.end;
          maxViolation[key].late = Math.max(
            maxViolation[key].late,
            lateViolation,
          );
        }
      }
      return maxViolation;
    },
    { load: { early: 0, late: 0 }, discharge: { early: 0, late: 0 } },
  );
}

/***
 * Mutates combinations by setting the shipmentId on assignments, and adding port call events to schedule.
 */
export const FindBestSchedule = (
  combinations: OrdersToVesselCombination[],
  deriveParams: ComputeScheduleMetricsParams,
  originalResults: ScheduleData,
  objective = Objective.MinimizeCost,
  allAvailableOrderIds: Set<number>,
  /** Shipment to add new assignments to. When null, automatically added to an overlapping shipment. */
  shipmentId: ClientId | null,
  violationBasis: ViolationIndicatorBasis,
  {
    countIdleFromIndex,
    maximumDelayDeltaMs: originalMaximumDelayDeltaMs,
    violationLimitsMs,
    respectCargoExclusions,
    respectDraftRestrictions,
    maxBallastDistanceNm,
    logEntries: reportLog,
    exactStart,
    newOrderIds,
  }: Omit<ScheduleOptimizationOpts, 'objective'> & {
    logEntries?: SuggestionLogEntry[];
    exactStart?: number | null;
    newOrderIds?: ReadonlySet<number>;
  } = {},
): BestSchedule | undefined => {
  /** Setting higher limits would not be  effective if the limit on total is less */
  const maximumDelayDeltaMs =
    originalMaximumDelayDeltaMs &&
    Math.max(
      originalMaximumDelayDeltaMs,
      violationLimitsMs?.load.late ?? 0,
      violationLimitsMs?.load.early ?? 0,
      violationLimitsMs?.discharge.late ?? 0,
      violationLimitsMs?.discharge.early ?? 0,
    );

  const optimizationOpts: ScheduleOptimizationOpts = {
    objective,
    countIdleFromIndex,
    maximumDelayDeltaMs,
    violationLimitsMs,
    respectCargoExclusions,
    respectDraftRestrictions,
    maxBallastDistanceNm,
  };

  for (const combination of combinations) {
    // Ensure that new assignments belong to an appropriate shipment
    addNewAssignmentsToShipment(combination, shipmentId);
    // Ensure that all locations have port call events
    updateCombinationWithPortCalls(combination, deriveParams);
    // Ensure the first new load event has the user's exact start time pinned
    if (exactStart != null && newOrderIds) {
      applyExactStartOverride(combination, exactStart, newOrderIds);
    }
  }

  const metrics = filteredMap(combinations, (combination) => {
    const metricsValue = computeScheduleMetrics(
      combination,
      originalResults,
      deriveParams,
      optimizationOpts,
      allAvailableOrderIds,
      violationBasis,
    );

    const hasViolations = hasMetricsViolations(metricsValue);
    if (hasViolations && reportLog) {
      const orderIds = combination.assignments.map((a) => a.order.id);
      reportLog.push({
        stage: 'scheduling',
        reason: 'metrics_violation',
        orderIds,
        violations: metricsValue.violations,
      });
    }
    return hasViolations ? NONE : metricsValue;
  });
  if (metrics.length === 0) {
    return undefined;
  }

  const minTotalDelayH = mappedMin(metrics, (m) => m.totalDelayH);
  const maxDurationH = mappedMax(metrics, (m) => m.voyageDurationH);

  metrics.sort((a, b) => {
    // Delays only take effect if they exceed the benefit from shorter voyage duration (plus a small threshold)
    const forgivenDelayA =
      maxDurationH - a.voyageDurationH + DELAY_LATE_THRESHOLD_HOURS;
    const forgivenDelayB =
      maxDurationH - b.voyageDurationH + DELAY_LATE_THRESHOLD_HOURS;
    const remainingDelayA = Math.max(
      0,
      a.totalDelayH - minTotalDelayH - forgivenDelayA,
    );
    const remainingDelayB = Math.max(
      0,
      b.totalDelayH - minTotalDelayH - forgivenDelayB,
    );
    return (
      floatCmp(a.objectiveScore, b.objectiveScore) ||
      floatCmp(a.mass, b.mass) ||
      floatCmp(a.shipmentAmount, b.shipmentAmount) ||
      floatCmp(remainingDelayA, remainingDelayB)
    );
  });

  const result = metrics.at(0)?.resultValue;
  if (!result) return undefined;

  if (reportLog) {
    for (const metricsItem of metrics) {
      reportLog.push({
        stage: 'scheduling',
        reason: 'not_best_schedule',
        orderIds: metricsItem.resultValue.combination.assignments.map(
          (a) => a.order.id,
        ),
      });
    }
    reportLog.push({
      stage: 'scheduling',
      reason: 'selected',
      orderIds: result.combination.assignments.map((a) => a.order.id),
    });
  }

  // Fixes assignment indices, necessary because initial combination doesn't account for added port call events
  result.combination.assignments = updateAssignmentsInBestSchedule(
    result.combination.assignments.map((a) => a.order),
    result,
  );

  return result;
};

export type ScheduleOptimizationOpts = {
  objective: Objective;
  countIdleFromIndex?: number;
  maximumDelayDeltaMs?: number;
  violationLimitsMs?: OrderViolationLimits;
  respectCargoExclusions?: boolean;
  respectDraftRestrictions?: boolean;
  maxBallastDistanceNm?: number;
};

export type ComputeScheduleMetricsParams = DeriveCapacitiesAndTimesParams & {
  context: DeriveCapacitiesAndTimesParams['context'] &
    Pick<
      ScheduleDataContext,
      'locations' | 'portCalls' | 'voyageExclusions' | 'currentUser'
    >;
};

/** Updates the new entries in the combination with the shipmentId from the parameter, or an overlapping shipment */
function addNewAssignmentsToShipment(
  combination: OrdersToVesselCombination,
  shipmentId: ClientId | null,
) {
  shipmentId ??= getTargetShipmentForSuggestion(combination);
  if (!shipmentId) return;

  const newOrderIds = new Set(combination.assignments.map((a) => a.order.id));
  for (const e of combination.schedule) {
    if (isCargoEvent(e)) {
      if (newOrderIds.has(e.order.id)) {
        e.entry = { ...e.entry, shipmentId };
      }
    }
  }
}

/** Pins startOverride on the first new load event so DeriveCapacitiesAndTimes
 * evaluates timing with the exact placement the user requested. */
function applyExactStartOverride(
  combination: OrdersToVesselCombination,
  exactStart: number,
  newOrderIds: ReadonlySet<number>,
) {
  let foundFirst = false;
  for (let i = 0; i < combination.schedule.length; i++) {
    const e = combination.schedule[i];
    if (isTCOrderEvent(e) && isLoadEvent(e) && newOrderIds.has(e.order.id)) {
      if (!foundFirst) {
        foundFirst = true;
        if (e.startOverride !== exactStart) {
          combination.schedule[i] = { ...e, startOverride: exactStart };
        }
      } else if (e.startOverride !== null) {
        combination.schedule[i] = { ...e, startOverride: null };
      }
    }
  }
}

/** Adds missing port call events to the schedule and updates load/discharge indices for new assignments */
const updateCombinationWithPortCalls = (
  combination: OrdersToVesselCombination,
  deriveParams: ComputeScheduleMetricsParams,
) => {
  const { schedule, assignments } = combination;
  const withPortCalls = attachPortCalls(
    schedule,
    schedule[0].vessel ?? null,
    true,
    deriveParams.context,
    null,
  );

  const newIndexByEvent = new Map(
    withPortCalls.map((e, i) => [getEventKey(e), i]),
  );

  // Update load and discharge indices in assignments
  for (const assignment of assignments) {
    const loadIndex = newIndexByEvent.get(
      getEventKey(schedule[assignment.loadIndex]),
    );
    const dischargeIndex = newIndexByEvent.get(
      getEventKey(schedule[assignment.dischargeIndex]),
    );
    assignment.loadIndex = assertedDefined(
      loadIndex,
      'loadIndex expected in new array',
    );
    assignment.dischargeIndex = assertedDefined(
      dischargeIndex,
      'dischargeIndex expected in new array',
    );
  }

  combination.schedule = withPortCalls;
};

type BestVesselAndSchedule = {
  combination: OrdersToVesselCombination;
  timeData: TimeData[];
  cost: number;
  load: Event;
  discharge: Event;
  vessel: Vessel;
};

type SpecialRequirementCheck =
  | {
      isFulfilled: true;
    }
  | {
      isFulfilled: false;
      violations: string[];
    };

export function orderFullfillsSpecialRequirements(
  vessel: ScenarioTimeline_ShipFragment,
  order: ScenarioOrder,
  specialRequirements: ReturnType<typeof useSpecialRequirements>,
  port?: { portId: LocationId; portName: string },
): SpecialRequirementCheck {
  // Collect port requirements applicable to this vessel
  const violations: string[] = [];
  let isFulfilled = true;

  const targetPortIds = compact(
    port ? [port.portId] : [order.loadPortId, order.dischargePortId],
  );

  let specialVesselIds = new Set<number>();

  for (const portId of targetPortIds) {
    for (const sr of specialRequirements.values()) {
      if (sr.locationIds.has(portId)) {
        specialVesselIds = new Set([...specialVesselIds, ...sr.shipIds]);
      }
    }
  }

  if (specialVesselIds.size && !specialVesselIds.has(vessel.id)) {
    isFulfilled = false;
    violations.push(
      `Vessel does not fulfill special requirements at ${port?.portName}`,
    );
  }

  // Validate order-specific special requirements
  const orderSpecialRequirementIds: number[] =
    order.orderRequirementAssociationsByOrderId.nodes
      .filter(isDefined)
      .map((sr) => sr.specialRequirementId);

  for (const requirementId of orderSpecialRequirementIds) {
    const specialRequirement = specialRequirements.get(requirementId);
    if (!specialRequirement) continue;

    const vesselFulfillsRequirement =
      specialRequirement.shipIds.size === 0 ||
      specialRequirement.shipIds.has(vessel.id);

    const portsFulfillRequirement =
      specialRequirement.locationIds.size === 0 ||
      targetPortIds.every((portId) =>
        specialRequirement.locationIds.has(portId),
      );

    if (!vesselFulfillsRequirement) {
      isFulfilled = false;
      violations.push(
        `Order ${order.id}: Vessel does not fulfill requirement ${specialRequirement.name}`,
      );
    }

    if (!portsFulfillRequirement) {
      isFulfilled = false;
      violations.push(
        `Order ${order.id}: Ports does not fulfill special requirement ${specialRequirement.name}`,
      );
    }
  }

  return isFulfilled
    ? { isFulfilled: true }
    : { isFulfilled: false, violations };
}

export function vesselFullfillsOrderCargoTypeRequirements(
  vessel: ScenarioTimeline_ShipFragment,
  order: ScenarioOrder,
): boolean {
  const compartments = getCompartments(vessel);
  const cargoType = order.assortmentByAssortmentId?.cargoType ?? null;
  // If vessel defines compartments, order's cargo type myst be assignable to at least one of them
  return (
    compartments.length === 0 ||
    !!compartments.find((c) => isCorrectCargoAllocationType(cargoType, c))
  );
}

export function findBestVessel(
  order: ScenarioOrder,
  vessels: readonly Vessel[],
  eventsByVessel: VesselToSortedEvents,
  context: ScheduleDataContext,
  config: CalculationConfig,
  entryDefaults: Partial<TCOrderEntry>,
  now: number = Date.now(),
  allocationOptions: CargoAllocationOptions,
  allAvailableOrderIds: Set<number>,
  violationBasis: ViolationIndicatorBasis,
  specialRequirements: ReturnType<typeof useSpecialRequirements>,
): BestVesselAndSchedule | undefined {
  let minTotalDelay = Infinity;
  let maxAmount = -Infinity;
  let minDelay = Infinity;
  let minCost = Infinity;
  let bestSchedule;

  for (const vessel of vessels) {
    if (vessel.inactive) continue;
    const { isFulfilled: specialRequirementFulfilled } =
      orderFullfillsSpecialRequirements(vessel, order, specialRequirements);
    if (!specialRequirementFulfilled) {
      continue;
    }
    const orderEvents = deriveOrderEvents(
      order,
      vessel,
      now,
      entryDefaults,
      context.locations,
      {
        startIsOverride: true,
      },
    );
    const events = eventsByVessel.get(vessel.id);
    if (!orderEvents || !events) continue;

    const originalResults = DeriveCapacitiesAndTimes(
      vessel,
      events,
      context,
      config,
      { calculateCosts: true },
      now,
    );
    const combinations = CreateAllCombinations(
      events,
      orderEvents,
      originalResults.timeData,
      { allocationOptions },
    );
    const { totals: originalTotals } = originalResults;
    const { constrainedDelays: originalDelays } = originalTotals;

    const deriveParams: ComputeScheduleMetricsParams = {
      vessel,
      context,
      config,
      now,
    };

    const bestScheduleForVessel = FindBestSchedule(
      combinations,
      deriveParams,
      originalResults,
      Objective.MinimizeCost,
      allAvailableOrderIds,
      null,
      violationBasis,
    );
    if (!bestScheduleForVessel) continue;

    const { timeData, totals, amount, delay, cost, combination } =
      bestScheduleForVessel;
    const carriesMore = amount > maxAmount;
    const carriesSame = amount === maxAmount;
    const lessDelay = delay < minDelay;
    const sameDelay = delay === minDelay;
    const lessExpensive = cost < minCost;
    const totalDelayDelta = totals.constrainedDelays - originalDelays;
    const lessTotalDelay = totalDelayDelta < minTotalDelay;
    const sameTotalDelay = totalDelayDelta === minTotalDelay;
    if (
      carriesMore ||
      (carriesSame &&
        (lessTotalDelay ||
          (sameTotalDelay && (lessDelay || (sameDelay && lessExpensive)))))
    ) {
      const { load, discharge } = orderEvents;
      minTotalDelay = totalDelayDelta;
      maxAmount = amount;
      minDelay = delay;
      minCost = cost;
      bestSchedule = {
        combination,
        timeData,
        cost,
        load,
        discharge,
        vessel,
      };
    }
  }

  return bestSchedule;
}

/** Find the best scheduling for a single order in a given index range among events.
 * Used when the user creates new assignments onto the timeline. */
export function deriveBestTCSchedule(
  orderEvents: TCOrderEvents,
  existingEvents: TCTimelineEvent[],
  context: ScheduleDataContext,
  config: CalculationConfig,
  {
    now = 0,
    objective = Objective.MaximizeVCP,
    minimumIndex = 0,
    maximumIndex = existingEvents.length,
    maxAllowedLaydayRtaTooEarly = Infinity,
    tryOverloadingIfNoValidFound = false,
    allocationOptions = OPTION_NO_CARGO_ALLOCATIONS,
    shipmentId = null,
    exactStart,
    newOrderIds,
  }: {
    now?: number;
    objective?: Objective;
    minimumIndex?: number;
    maximumIndex?: number;
    maxAllowedLaydayRtaTooEarly?: number;
    tryOverloadingIfNoValidFound?: boolean;
    allocationOptions?: CargoAllocationOptions;
    shipmentId?: ClientId | null;
    exactStart?: number | null;
    newOrderIds?: ReadonlySet<number>;
  } = {},
  allAvailableOrderIds: Set<number>,
  violationBasis: ViolationIndicatorBasis,
): BestSchedule | undefined {
  const vessel = orderEvents.load.vessel;

  const originalResults = DeriveCapacitiesAndTimes(
    vessel,
    existingEvents,
    context,
    config,
    { calculateCosts: true },
    now,
  );

  let combinations = CreateAllCombinations(
    existingEvents,
    orderEvents,
    originalResults.timeData,
    {
      minimumIndex,
      maximumIndex,
      alwaysAllowMinimumAndMaximumIndex: true,
      firstAllowedTime: 0,
      maxAllowedLaydayRtaTooEarly,
      allocationOptions,
    },
  );

  if (combinations.length === 0 && tryOverloadingIfNoValidFound) {
    combinations = CreateAllCombinations(
      existingEvents,
      orderEvents,
      originalResults.timeData,
      {
        minimumIndex,
        maximumIndex,
        alwaysAllowMinimumAndMaximumIndex: true,
        firstAllowedTime: 0,
        maxAllowedLaydayRtaTooEarly,
        allocationOptions,
        cargoMargin: Infinity,
      },
    );
  }

  const deriveParams: ComputeScheduleMetricsParams = {
    vessel,
    context,
    config,
    now,
  };

  return FindBestSchedule(
    combinations,
    deriveParams,
    originalResults,
    objective,
    allAvailableOrderIds,
    shipmentId,
    violationBasis,
    {
      // Limits for manual assignments are not currently configurable, but could be
      violationLimitsMs: {
        load: {
          early: Infinity,
          late: Infinity,
        },
        discharge: {
          early: Infinity,
          late: Infinity,
        },
      },
      exactStart,
      newOrderIds,
    },
  );
}

/** Returns the index at which to insert the new event for optimal results.
 * The item formerly at that index should be pushed forward. */
export function findOptimalPosition(
  e: Events,
  existingEvents: Events[],
  eventBefore: Events | null,
  eventAfter: Events | null,
  distances: Distances,
): number {
  let lowestCost = Infinity;
  let lowestCostPosition: number = existingEvents.length - 0.5;

  for (
    // Position .5 is "in between" array indices, as we look to insert between two events
    let position = existingEvents.length - 0.5;
    position > -1;
    --position
  ) {
    const nextEvent =
      position > existingEvents.length - 1
        ? eventAfter
        : (get(existingEvents, Math.ceil(position)) ?? null);
    const prevEvent =
      position < 0
        ? eventBefore
        : (get(existingEvents, Math.floor(position)) ?? null);
    if (
      isDischargeEvent(e) &&
      nextEvent &&
      isLoadEvent(nextEvent) &&
      e.order.id === nextEvent.order.id
    ) {
      // Prevent discharge before load on the same order
      break;
    }
    const prevLocation = prevEvent && getEventEndLocationId(prevEvent);
    const nextLocation = nextEvent && getEventStartLocationId(nextEvent);

    /** The current distance between 2 events already in the result array */
    const existingDistance =
      (prevLocation !== null &&
        nextLocation != null &&
        distances.get(prevLocation, nextLocation, false)?.dist) ||
      0;
    /** If inserted here, the distance of the leg on the left side of our current event */
    const distanceBefore =
      (prevLocation &&
        distances.get(prevLocation, getEventStartLocationId(e), false)?.dist) ||
      0;
    /** If inserted here, the distance of the leg on the right side of our current event */
    const distanceAfter =
      (nextLocation &&
        distances.get(getEventEndLocationId(e), nextLocation, false)?.dist) ||
      0;
    /** If inserted here, how much the total distance of the whole sequence changes */
    const costDelta = distanceBefore + distanceAfter - existingDistance;

    if (costDelta <= lowestCost) {
      lowestCost = costDelta;
      lowestCostPosition = position;
    }
  }

  return Math.ceil(lowestCostPosition);
}

/** Rearrange a part of an event sequence to optimize those events' travel distance
 * In the future this may be improved to also consider other factors besides distance
 * Also, in the future we may be able to utilize the scenario optimization tool for this
 * Guarantees to find the optimal solution for only up to 10 events to rearrange
 * @param events
 * @param arrangeRange start and end indices, start is inclusive, end is exclusive
 * Returns a new array
 * @param distances
 * @param maxEventsToHandle
 */
export function rearrangeEventsOptimally(
  events: readonly Events[],
  arrangeRange: { startIndex: number; endIndex: number },
  distances: Distances,
  maxEventsToHandle = 10,
): Events[] {
  // Optimization: it's never useful to enter the same port twice for the same activity so we group similar items to be moved as one
  // The situation would be different if we had to consider vessel capacity as well (when all orders don't fit at once), which we don't currently do
  const elementsToArrange = Object.values(
    groupBy(
      events.slice(arrangeRange.startIndex, arrangeRange.endIndex),
      (e) => `${getEventStartLocationId(e)}-${e.type}`,
    ),
  );

  const eventBeforeIndex = findPrevIndexWith(
    events,
    arrangeRange.startIndex - 1,
    (e) => !isPortCallEvent(e),
  );
  const eventAfterIndex = findNextIndexWith(
    events,
    arrangeRange.endIndex,
    (e) => !isPortCallEvent(e),
  );
  const eventBefore =
    (eventBeforeIndex !== undefined && get(events, eventBeforeIndex)) || null;
  const eventAfter =
    (eventAfterIndex !== undefined && get(events, eventAfterIndex)) || null;

  let organizedEvents: Events[] | null;

  if (elementsToArrange.length <= maxEventsToHandle) {
    // Get all possible sequences
    const validPermutations = getValidEventPermutations(elementsToArrange);

    let shortestDistance = Infinity;
    let bestPermutation: Events[] | null = null;

    // Pick the one with shortest total distance
    for (const perm of validPermutations) {
      const permutationEvents = perm.flat();
      const totalDistance = getEventsTotalDistance(
        permutationEvents,
        distances,
        eventBefore,
        eventAfter,
      );

      if (totalDistance < shortestDistance) {
        bestPermutation = permutationEvents;
        shortestDistance = totalDistance;
      }
    }

    organizedEvents = bestPermutation;
  } else {
    // Too many elements to brute force all solutions, use a greedy algorithm instead
    let bestSolution: Events[] = elementsToArrange.flat();
    let bestDistance = Infinity;

    // Try the same algorithm several times with varying random starting order for events, which usually lead to different results
    for (let iter = 0; iter < 50; ++iter) {
      const [loads, rest] = partition(
        shuffle(elementsToArrange.flat()),
        isLoadEvent,
      );
      const iterationResult = [];

      // We insert loads to result first, then it's trivial to ensure correct L-D order when we look for the position for the discharge starting from the end
      for (const e of [...loads, ...rest]) {
        if (iterationResult.length === 0) {
          iterationResult.push(e);
          continue;
        }

        const lowestCostPosition = findOptimalPosition(
          e,
          iterationResult,
          eventBefore,
          eventAfter,
          distances,
        );
        iterationResult.splice(Math.ceil(lowestCostPosition), 0, e);
      }

      const iterationDistance = getEventsTotalDistance(
        iterationResult,
        distances,
        eventBefore,
        eventAfter,
      );

      if (iterationDistance < bestDistance) {
        bestDistance = iterationDistance;
        bestSolution = iterationResult;
      }
    }

    organizedEvents = bestSolution;
  }

  const result = [...events];

  if (organizedEvents) {
    // Sort each port call so that we discharge before load
    // Exception: if the same order is loaded & discharged in one port, we handle those orders in correct L-D sequence first
    const ldSorted = groupBySequence(
      organizedEvents,
      getEventStartLocationId,
    ).flatMap((evs) => {
      const loadedOrders = new Set(
        evs.filter(isLoadEvent).map((e) => e.order.id),
      );
      const dischargedOrders = new Set(
        evs.filter(isDischargeEvent).map((e) => e.order.id),
      );
      const ordersWithBothLnD = setIntersection([
        loadedOrders,
        dischargedOrders,
      ]);
      const [lndEvents, rest] = partition(
        evs,
        (e) => isCargoEvent(e) && ordersWithBothLnD.has(e.order.id),
      );

      const [lndLoads, lndOthers] = partition(lndEvents, isLoadEvent);
      const [restDischarges, restOthers] = partition(rest, isDischargeEvent);

      return [...lndLoads, ...lndOthers, ...restDischarges, ...restOthers];
    });

    result.splice(
      arrangeRange.startIndex,
      arrangeRange.endIndex - arrangeRange.startIndex,
      ...ldSorted,
    );
  }
  return result;
}

export function* getZeroLegOrders(
  orders: Iterable<ScenarioOrder>,
  tds: TimeData[],
  firstIndex: number,
  locations: ScheduleDataContext['locations'],
  {
    orderEventsCache,
  }: { orderEventsCache: DefaultMap<number, Map<number, TCOrderEvents>> },
): Generator<OrderPlacement> {
  const events = tds.map((td) => td.event).filter(isTCTimelineEvent);
  const vessel = events.at(0)?.vessel;
  const firstAllowedTime = tds.at(firstIndex)?.start;
  if (!vessel || firstAllowedTime === undefined) return [];

  const capacitiesInPorts = computeCapacityAtPorts(tds, firstIndex);

  for (const o of orders) {
    // Quickly filter out obviously impossible orders
    if (
      !capacitiesInPorts.find(
        (c) =>
          c.locationId === o.loadPortId &&
          isInRange(c.arriveAt, o.loadingWindow) &&
          cargoRangeFits(o.cargoRanges, c.capacity),
      )
    ) {
      continue;
    }
    const orderEvents = deriveOrderEvents(
      o,
      vessel,
      firstAllowedTime,
      {},
      locations,
      { orderEventsCache },
    );
    if (!orderEvents) return [];

    const combinations = CreateAllCombinations(events, orderEvents, tds, {
      maxResults: 1,
      onlyInsertInExistingPortCalls: true,
      allocationOptions: OPTION_NO_CARGO_ALLOCATIONS,
    });

    const validCombinationAssignment = combinations.at(0)?.assignments.at(0);
    if (validCombinationAssignment) {
      yield validCombinationAssignment;
    }
  }
}

export function computeCapacityAtPorts(
  tds: TimeData[],
  firstIndex: number,
): {
  locationId: LocationId;
  nextLocationId: LocationId | null;
  capacity: MassVolumeSize;
  arriveAt: number;
  loadPlacementIndex: number;
  dischargePlacementIndex: number;
}[] {
  const capacityAtPorts: {
    locationId: LocationId;
    nextLocationId: LocationId | null;
    capacity: MassVolumeSize;
    arriveAt: number;
    loadPlacementIndex: number;
    dischargePlacementIndex: number;
  }[] = [];
  const length = tds.length;
  const lastIndex = length - 1;
  for (
    let i = firstIndex === length ? lastIndex : firstIndex;
    i <= length;
    ++i
  ) {
    const prev = get(tds, i - 1);
    const current = get(tds, i);
    const prevLocation = prev && getEventEndLocationId(prev.event);
    const currentLocation = current && getEventStartLocationId(current.event);
    const changeLocation = currentLocation !== prevLocation;
    if (
      (changeLocation || i === lastIndex) &&
      current &&
      currentLocation !== undefined
    ) {
      let loadPlacementIndex = isPortCallEvent(current.event)
        ? findIndex(tds, (td) => isPortCallDepartureEventTimeData(td), i + 2)
        : findIndex(
            tds,
            (td) => getEventStartLocationId(td.event) !== currentLocation,
            i,
          );
      if (loadPlacementIndex === -1) {
        loadPlacementIndex = length;
      }
      let lastTdAtPort = null;
      let nextIndex = i;
      for (
        ;
        nextIndex < length &&
        getEventLocationId(tds[nextIndex].event) === currentLocation;
        ++nextIndex
      ) {
        lastTdAtPort = tds[nextIndex];
      }
      const tdAfterThisLocation = get(tds, nextIndex);
      const capacity: MassVolumeSize = {
        mass:
          lastTdAtPort?.availableMassCapacity ??
          prev?.availableMassCapacity ??
          0,
        volume:
          lastTdAtPort?.availableVolumeCapacity ??
          prev?.availableVolumeCapacity ??
          0,
      };

      capacityAtPorts.push({
        locationId: currentLocation,
        nextLocationId: tdAfterThisLocation
          ? getEventStartLocationId(tdAfterThisLocation.event)
          : null,
        capacity,
        arriveAt: current.start,
        loadPlacementIndex,
        dischargePlacementIndex: isPortCallEvent(current.event) ? i + 2 : i,
      });
    }
  }

  return capacityAtPorts;
}

/** When we have the route from A to B, how much the distance changes if we add another location C in between: A->C->B.
 * B may be null, in which case we only consider the new leg from A to C. */
export function getDistanceDeltaOnAddition(
  from: LocationId,
  to: LocationId | null,
  addedInBetween: LocationId,
  distances: Distances,
  avoidChannels = false,
): number | undefined {
  if (from === addedInBetween || addedInBetween === to) return 0;
  const original =
    to === null ? null : distances.get(from, to, avoidChannels)?.dist;
  const fromToAdded = distances.get(from, addedInBetween, avoidChannels)?.dist;
  const addedToTo =
    to === null ? null : distances.get(addedInBetween, to, avoidChannels)?.dist;
  if (
    fromToAdded === undefined ||
    addedToTo === undefined ||
    original === undefined
  )
    return undefined;
  const withAdded = fromToAdded + (addedToTo ?? 0);
  return withAdded - (original ?? 0);
}

export type VoyageSuggestion = {
  voyageCargoPerformance: number;
  schedule: NonNullable<BestSchedule>;
};

export const MAX_SHORTLIST_ORDERS_PER_DEPTH = [20, 10, 7, 10, 5, 5, 5, 3];
/** How many recursions we search with branching,
 * after this depth we only add a single new order for as long as VCP increases */
export const VOYAGE_SEARCH_BRANCHING_DEPTH = 3;

export type OmitFunctions<T> = {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  [K in keyof T]: T[K] extends Function ? unknown : T[K];
};

export type PreventFunctions<T> = {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  [K in keyof T]: T[K] extends Function ? undefined : T[K];
};

export type VoyageSuggestionStatusMessage = {
  type: 'progress' | 'complete';
  workDone: number;
  workTotal: number;
  isStopped: boolean;
  currentResults: NonNullable<BestSchedule>[];
};

export const sortAndClipVoyageSuggestions = (
  res: VoyageSuggestion[],
  maxResults: number,
  toleratedDelayH: number,
  toleratedIdleMs: number,
): void => {
  const lowestIdle = min(res.map((x) => x.schedule?.idle)) ?? 0;

  const sorted = orderBy(
    res,
    [
      (suggestion) => suggestion.schedule.objectiveScore,
      (suggestion) => suggestion.voyageCargoPerformance,
      (suggestion) =>
        !suggestion.schedule
          ? Infinity
          : suggestion.schedule.delay < toleratedDelayH
            ? 0
            : suggestion.schedule.delay,
      (suggestion) =>
        !suggestion.schedule
          ? Infinity
          : suggestion.schedule.idle - lowestIdle < toleratedIdleMs
            ? 0
            : suggestion.schedule.idle - lowestIdle,
    ],
    ['asc', 'desc', 'asc', 'asc'],
  );

  // Drop identical suggestions which have been found by a different permutation of the same orders
  const filtered = sorted.filter((sugg, i, arr) => {
    const prev = get(arr, i - 1);
    return !(
      prev &&
      prev.voyageCargoPerformance === sugg.voyageCargoPerformance &&
      prev.schedule &&
      prev.schedule.combination.assignments.every(({ order }) =>
        sugg.schedule.combination.assignments.find(
          (other) => other.order.id === order.id,
        ),
      )
    );
  });
  replaceContents(res, filtered.slice(0, maxResults));
};

/*
 * Rearranges shipment events using rearrangeEventsOptimally
 */
export function rearrangeShipmentEvents(
  originalEvents: VesselSchedule,
  shipmentStartIndex: number,
  shipmentEndIndex: number,
  vessel: Vessel,
  context: ScheduleDataContext,
  config: CalculationConfig,
  portCallEntries: Map<ClientId, PortCallEntry>,
): ChangedEntries {
  const arrangedAllEvents = rearrangeEventsOptimally(
    originalEvents,
    {
      startIndex: shipmentStartIndex,
      endIndex: shipmentEndIndex + 1, // endIndex is assumed exclusive
    },
    context.distances,
  );

  return deriveEventModifications(
    arrangedAllEvents,
    originalEvents,
    vessel,
    context,
    config,
    portCallEntries,
    { firstChangedIndex: shipmentStartIndex },
  );
}

type AutoAssignResult = {
  entries: TCScheduleEntry[];
  eventsByVessel: VesselToSortedEvents;
};

/** Find the first event in the last port call containing any cargo events */
export const getFirstEventInLastPortCall = (
  events: TCTimelineEvent[],
): TCTimelineEvent | null => {
  const lastCargoEventIndex = findLastIndex(events, isCargoEvent);
  if (lastCargoEventIndex === -1) return null;

  return (
    // Iterate to the left to find the first event in the port call
    findWith(events, lastCargoEventIndex, -1, (e, i) => {
      const preceding = get(events, i - 1);
      if (!preceding) return true;
      return getEventEndLocationId(preceding) !== getEventStartLocationId(e);
    }) ?? null
  );
};

export type ScheduleChangeMap<T extends Entry = Entry> = Map<ClientId, T>;

export function initPortCallEntry(
  params: Partial<PortCallEntry> &
    Pick<PortCallEntry, 'locationId' | 'plannedArrival'>,
): Required<PortCallEntry> {
  // Apply some sane default values but an empty default port call will never be really used alone so the times will get changed by timeline calculations anyway
  const { plannedArrival, plannedArrivalDuration } = params;
  let { plannedBerthing, plannedDeparture } = params;
  plannedBerthing = Math.max(
    plannedBerthing ?? 0,
    plannedArrival + (plannedArrivalDuration || minimumEventDuration),
  );
  plannedDeparture = Math.max(
    plannedDeparture ?? 0,
    plannedBerthing + minimumEventDuration,
  );

  return {
    category: EventCategory.PortCall,
    dbId: params.dbId ?? null,
    clientId: params.clientId ?? generateClientId(),
    updatedAt: params.updatedAt ?? Date.now(),
    vesselId: params.vesselId ?? null,
    locationId: params.locationId,
    previousLocationId: params.previousLocationId ?? null,
    plannedArrival,
    plannedBerthing,
    plannedDeparture,
    plannedArrivalOverride: params.plannedArrivalOverride ?? null,
    plannedArrivalOverrideSourcedAt:
      params.plannedArrivalOverrideSourcedAt ?? null,
    plannedArrivalOverrideSourcedFrom:
      params.plannedArrivalOverrideSourcedFrom ?? null,
    plannedBerthingOverride: params.plannedBerthingOverride ?? null,
    plannedBerthingOverrideSourcedAt:
      params.plannedBerthingOverrideSourcedAt ?? null,
    plannedBerthingOverrideSourcedFrom:
      params.plannedBerthingOverrideSourcedFrom ?? null,
    plannedDepartureOverride: params.plannedDepartureOverride ?? null,
    plannedDepartureOverrideSourcedAt:
      params.plannedDepartureOverrideSourcedAt ?? null,
    plannedDepartureOverrideSourcedFrom:
      params.plannedDepartureOverrideSourcedFrom ?? null,
    plannedArrivalDuration: params.plannedArrivalDuration ?? null,
    plannedBerthingDuration: params.plannedBerthingDuration ?? null,
    plannedDepartureDuration: params.plannedDepartureDuration ?? null,
    seaDurationOverride: params.seaDurationOverride ?? null,
    legAvoidsChannels: params.legAvoidsChannels ?? false,
    totalLoadDurationOverride: params.totalLoadDurationOverride ?? null,
    totalDischargeDurationOverride:
      params.totalDischargeDurationOverride ?? null,
    channelCostDefaultId: params.channelCostDefaultId ?? null,
    oneTimeOverrideChannelCost: params.oneTimeOverrideChannelCost ?? null,
    oneTimeOverrideCost: params.oneTimeOverrideCost ?? null,
    portCostDefaultId: params.portCostDefaultId ?? null,
    useHistoricalPortCost: params.useHistoricalPortCost ?? false,
    useLatestHistoricalPortCost: params.useLatestHistoricalPortCost ?? false,
    useOneTimeOverrideCost: params.useOneTimeOverrideCost ?? false,
    useIntegrationPortCost: params.useIntegrationPortCost ?? false,
    channelDurationOverhead: params.channelDurationOverhead ?? null,
    bunkerOverrides: params.bunkerOverrides ?? null,
    bunkerPriceSource: params.bunkerPriceSource ?? null,
    weatherFactor: params.weatherFactor ?? null,
  };
}

/** Restore committed seaDurationOverride/weatherFactor/previousLocationId
 *  on port call arrival events whose route reverted to the original
 *  configuration. The assign step may have nulled these values; without
 *  restoring them, deriveEventModifications produces incorrect timing
 *  that cascades into wrong values for dependent entries. */
export function restoreCommittedOverrides(
  events: Events[],
  committedPortCalls: Map<
    ClientId,
    Pick<
      PortCallEntry,
      'seaDurationOverride' | 'weatherFactor' | 'previousLocationId'
    >
  >,
): Events[] {
  return events.map((event, i) => {
    if (!isPortCallArrivalEvent(event)) return event;
    const committed = committedPortCalls.get(event.entry.clientId);
    if (!committed?.previousLocationId) return event;
    const prevEvent = i > 0 ? events[i - 1] : undefined;
    if (
      prevEvent &&
      getEventEndLocationId(prevEvent) === committed.previousLocationId
    ) {
      const overrides = {
        seaDurationOverride: committed.seaDurationOverride,
        weatherFactor: committed.weatherFactor ?? null,
        previousLocationId: committed.previousLocationId,
      };
      return {
        ...event,
        ...overrides,
        entry: { ...event.entry, ...overrides },
      };
    }
    return event;
  });
}

/** When the previous location of a port call has changed we'll clear the sea duration override because the duration must be different now */
export function clearChangedLegOverrides(
  newEvents: Events[],
  oldEvents: Events[],
): Events[] {
  const oldPreviousEventByPortCall = new Map(
    oldEvents.flatMap((e, i) => {
      if (i === 0 || !isPortCallArrivalEvent(e)) {
        return [];
      }
      return [[e.entry.clientId, oldEvents[i - 1]] as const];
    }),
  );

  return newEvents.map((e, i) => {
    if (!isPortCallArrivalEvent(e)) {
      return e;
    }
    const newPreviousEvent = get(newEvents, i - 1);
    const oldPreviousEvent = oldPreviousEventByPortCall.get(e.entry.clientId);
    if (
      !oldPreviousEvent ||
      (newPreviousEvent &&
        getEventEndLocationId(oldPreviousEvent) ===
          getEventEndLocationId(newPreviousEvent))
    ) {
      return e;
    } else if (
      newPreviousEvent &&
      e.previousLocationId &&
      getEventEndLocationId(newPreviousEvent) === e.previousLocationId
    ) {
      // Route reverted to its original configuration (e.g. after unassigning
      // a planned voyage), so the existing sea duration override is still valid
      return e;
    } else {
      return {
        ...e,
        seaDurationOverride: null,
        weatherFactor: null,
      };
    }
  });
}

// @TODO: in detecting changes to Events from updated TimeData, always check every item's timings (instead of relying on firstChangedIndex) if they have changed and then drop the ones which were already equal
/** Inspects the events on the voyage group after some update and determines what changes need to be made to scenario entries.
 * In particular
 * 1) recomputes timings after a duration or time on an event was changed
 * 2) removes orphaned single events
 * 3) marks affected events changed
 */
export function deriveEventModifications(
  originalNewEvents: Events[],
  oldEvents: Events[],
  vessel: Vessel | null,
  context: ScheduleDataContext,
  config: CalculationConfig,
  portCallEntries: Map<ClientId, PortCallEntry>,
  opts?: { firstChangedIndex?: number },
): ModificationResult {
  vessel = vessel ?? defaultVessel;
  let firstChangedIndex = opts?.firstChangedIndex ?? 0;
  let newEvents = [...originalNewEvents];

  newEvents = transferDeletedEventsTimingOverrides(newEvents, oldEvents);
  newEvents = clearChangedLegOverrides(newEvents, oldEvents);
  newEvents = attachPortCalls(
    newEvents,
    vessel,
    true,
    context,
    portCallEntries,
  );
  newEvents = applyCleaningEvents(newEvents, context.voyageExclusions);

  // If an event was removed or port call events were added/updated all events after it may have changed
  // TODO: we might want to make this check to diff between original scheduled and updated scheduled (after DeriveCapacitiesAndTimes) to totally catch any possible changes
  const firstTouchedIndex = newEvents.findIndex(
    (e, i) => !isEqual(e, oldEvents[i]),
  );
  if (firstTouchedIndex !== -1) {
    firstChangedIndex = Math.min(firstChangedIndex, firstTouchedIndex);
  }

  const schedule = DeriveCapacitiesAndTimes(
    vessel,
    newEvents,
    context,
    config,
    {
      // Need to calculate costs because also channel duration overrides depend on it
      calculateCosts: true,
    },
    Date.now(),
  );
  const { timeData } = schedule;

  // If time derivation would place any updated TimeData so that it overlaps an even earlier Event (with untouched scenario timings) the earlier Event's timing must be wrong/outdated in scenario and we'll include it to be updated as well
  // This fixes accidental re-ordering of events in certain situations when event and timedata times are no longer in sync for some events
  while (
    firstChangedIndex > 0 &&
    firstChangedIndex < timeData.length &&
    newEvents[firstChangedIndex - 1].end > timeData[firstChangedIndex].start
  ) {
    firstChangedIndex--;
  }

  return addChangedEntriesToMap(
    firstChangedIndex,
    timeData,
    newEvents,
    oldEvents,
  );
}

export function cargoEntryUpdateFromTimedata(
  td: TimeData<TCOrderEvent> | TimeData<CoaEvent> | TimeData<SpotEvent>,
): (
  | Pick<
      CargoEntry,
      | 'loadingStart'
      | 'loadingStartOverride'
      | 'loadingDuration'
      | 'loadingDurationOverride'
      | 'loadingEnd'
      | 'loadingSeaDurationOverride'
      | 'loadingConcurrent'
      | 'loadingPortCallId'
    >
  | Pick<
      CargoEntry,
      | 'dischargingStart'
      | 'dischargingStartOverride'
      | 'dischargingDuration'
      | 'dischargingDurationOverride'
      | 'dischargingEnd'
      | 'dischargingSeaDurationOverride'
      | 'dischargingConcurrent'
      | 'dischargingPortCallId'
    >
) &
  Pick<CargoEntry, 'vesselId'> {
  const { event } = td;
  const { start, end } = td;

  const startOverride =
    event.startOverride ??
    // When suggestion has marked event to wait until layday, use as startOverride but only it's applicable
    (event.delayUntil && event.delayUntil >= start ? event.delayUntil : null);

  return event.type === CoreEventType.Load
    ? {
        loadingStart: start,
        loadingStartOverride: startOverride,
        loadingDuration: end - start,
        loadingDurationOverride:
          event.durationOverride !== end - start
            ? null
            : event.durationOverride,
        loadingEnd: end,
        loadingSeaDurationOverride: event.seaDurationOverride,
        loadingConcurrent: event.concurrent,
        loadingPortCallId: event.portCallId,
        vesselId: event.vesselId,
      }
    : {
        dischargingStart: start,
        dischargingStartOverride: startOverride,
        dischargingDuration: end - start,
        dischargingDurationOverride:
          event.durationOverride !== end - start
            ? null
            : event.durationOverride,
        dischargingEnd: end,
        dischargingSeaDurationOverride: event.seaDurationOverride,
        dischargingConcurrent: event.concurrent,
        dischargingPortCallId: event.portCallId,
        vesselId: event.vesselId,
      };
}

/** Updates entries from changes into events in timedatas, returns changemaps with updated timedatas. */
function addChangedEntriesToMap(
  firstChangedIndex: number,
  originalUpdatedTimeData: readonly TimeData[],
  events: readonly Events[],
  oldEvents: readonly Events[],
  changeMaps: Omit<ModificationResult, 'schedule' | 'updatedTimeData'> = {
    changedTCs: new Map(),
    changedEvents: new Map(),
    changedOffHires: new Map(),
    changedCoas: new Map(),
    changedSpots: new Map(),
    changedPortCalls: new Map(),
    deletedPortCalls: new Set(),
    deletedEvents: new Map([
      [CoreEventType.Single, []],
      [CoreEventType.OffHire, []],
    ]),
    changedCargoAllocations: new Map(),
    changedOrderVoyageCharterIds: new Map(),
  },
): Omit<ModificationResult, 'schedule'> {
  // TODO: could reuse code with timeDataToUpdatedEntry implementation
  const timeData: TimeData[] = [...originalUpdatedTimeData];

  // Always process full port calls. Makes sure we always check & update Arrival and Berthing times on a port call together with Departure.
  while (
    firstChangedIndex >= 1 &&
    firstChangedIndex < timeData.length &&
    timeData[firstChangedIndex - 1].event.portCallId ===
      timeData[firstChangedIndex].event.portCallId
  ) {
    firstChangedIndex--;
  }

  const existingEntries = new Map(
    oldEvents.map((e) => [e.entry.clientId, e.entry]),
  );
  const existingEventsByClientId = mapGroupBy(
    oldEvents,
    (e) => e.entry.clientId,
  );
  const eventIndicesByEntryId = mapGroupBy(
    events.map((_e, i): number => i),
    (eventIndex) => timeData[eventIndex].event.entry.clientId,
  );
  // Assign updated entry into every event which is based on this entry (relevant for cargo events and port call events)
  const updateEntryInResult = (updatedEntry: Entry) => {
    const eventIndices = eventIndicesByEntryId.get(updatedEntry.clientId) ?? [];
    for (const eventIndex of eventIndices) {
      const event = timeData[eventIndex].event;
      if (event.entry.category !== updatedEntry.category) {
        // Should never happen, but better to be sure
        throw new Error(
          `Unexpected type mismatch in updating entry by id: ${updatedEntry.clientId}, ${event.entry.category} !== ${updatedEntry.category}`,
        );
      }
      const updatedEvent = { ...event };
      updatedEvent.entry = updatedEntry;

      timeData[eventIndex] = {
        ...timeData[eventIndex],
        event: updatedEvent,
      };
    }
  };

  const oldTCLoadEventsByOrderId = mapKeyBy(
    oldEvents.filter(isTCOrderEvent).filter(isLoadEvent),
    (e) => e.order.id,
  );

  const {
    changedTCs,
    changedEvents,
    changedOffHires,
    changedCoas,
    changedSpots,
    changedPortCalls,
    deletedPortCalls,
    deletedEvents,
    changedCargoAllocations,
    changedOrderVoyageCharterIds,
  } = changeMaps;

  const now = Date.now();
  for (let i = firstChangedIndex; i < events.length; i++) {
    const td = timeData[i];
    const event = events[i];
    const { seaDurationOverride } = event;
    if (isSingleEventTimeData(td)) {
      const {
        duration,
        locationId,
        vesselId,
        event: eventType,
        startOverride,
        concurrent,
      } = td.event;
      const newEntry: SingleEntry = {
        dbId: event.entry.dbId,
        clientId: event.entry.clientId,
        category: EventCategory.Single,
        type: singleEventTypeToEventType(eventType),
        concurrent,
        locked: false,
        shared: false,
        startOverride: startOverride || null,
        seaDurationOverride,
        vesselId,
        locationId,
        meta: '',
        start: td.start,
        duration,
        end: td.end,
        portCallId: getPortCallId(event),
        updatedAt: event.entry.updatedAt,
        cleaningAssortmentExclusionId: event.cleaningAssortmentExclusionId,
      };

      if (
        !isEqual(event.entry, newEntry) ||
        !existingEntries.has(newEntry.clientId)
      ) {
        updateEntryInResult(newEntry);
        changedEvents.set(event.entry.clientId, {
          ...newEntry,
          updatedAt: now,
        });
      }
    } else if (isOffHireEventTimeData(td)) {
      const newEntry: OffHireEntry = {
        ...td.event.entry,
        start: td.start,
        end: td.end,
        duration: td.end - td.start,
        locationAId: td.event.locationAId,
        locationBId: td.event.locationBId,
        vesselId: td.event.vesselId,
      };

      const existingEntry = existingEntries.get(newEntry.clientId);
      if (!isEqual(event.entry, existingEntry)) {
        updateEntryInResult(newEntry);
        changedOffHires.set(event.entry.clientId, {
          ...newEntry,
          updatedAt: now,
        });
      }
    } else if (isTCOrderEventTimeData(td)) {
      const changes = changedTCs;

      const newEntry: ExactProps<
        typeof td.event.entry,
        ReturnType<typeof cargoEntryUpdateFromTimedata>
      > = {
        ...td.event.entry,
        ...changes.get(event.entry.clientId),
        ...cargoEntryUpdateFromTimedata(td),
      };

      if (isLoadEventTimeData(td) && isTCOrderEvent(td.event)) {
        const oldEvent = oldTCLoadEventsByOrderId.get(td.event.order.id);
        if (oldEvent && !td.event.order.incomingVoyageCharterRelation) {
          // Updated order has no voyage charter, clear from scenario
          changedOrderVoyageCharterIds.set(td.event.order.id, null);
        }
        const existingCargoAllocations = mapKeyBy(
          oldEvent?.cargoAllocations ?? [],
          (a) => a.clientId,
        );
        // Create/update
        for (const c of td.event.cargoAllocations) {
          const existing = existingCargoAllocations.get(c.clientId);
          if (!existing || !isEqual(existing, c)) {
            changedCargoAllocations.set(c.clientId, c);
          }
        }
        // Delete missing
        for (const c of existingCargoAllocations.values()) {
          const remaining = td.event.cargoAllocations.find(
            (a) => a.clientId === c.clientId,
          );
          if (!remaining) {
            changedCargoAllocations.set(c.clientId, null);
          }
        }
      }

      if (
        !isEqual(event.entry, newEntry) ||
        !existingEntries.has(newEntry.clientId)
      ) {
        updateEntryInResult(newEntry);
        changes.set(event.entry.clientId, { ...newEntry, updatedAt: now });
      }
    } else if (isCoaEventTimeData(td)) {
      const changes = changedCoas;

      const newEntry: ExactProps<
        typeof td.event.entry,
        ReturnType<typeof cargoEntryUpdateFromTimedata>
      > = {
        ...td.event.entry,
        ...changes.get(event.entry.clientId),
        ...cargoEntryUpdateFromTimedata(td),
      };

      if (
        !isEqual(event.entry, newEntry) ||
        !existingEntries.has(newEntry.clientId)
      ) {
        updateEntryInResult(newEntry);
        changes.set(event.entry.clientId, { ...newEntry, updatedAt: now });
      }
    } else if (isSpotEventTimeData(td)) {
      const changes = changedSpots;

      const newEntry: ExactProps<
        typeof td.event.entry,
        ReturnType<typeof cargoEntryUpdateFromTimedata>
      > = {
        ...td.event.entry,
        ...changes.get(event.entry.clientId),
        ...cargoEntryUpdateFromTimedata(td),
      };

      if (
        !isEqual(event.entry, newEntry) ||
        !existingEntries.has(newEntry.clientId)
      ) {
        updateEntryInResult(newEntry);
        changes.set(event.entry.clientId, { ...newEntry, updatedAt: now });
      }
    } else if (isPortCallEventTimeData(td)) {
      const changes = changedPortCalls;
      const oldEntry = td.event.entry;
      const existingChange = changes.get(oldEntry.clientId);

      const change: Partial<typeof td.event.entry> = {
        category: td.event.entry.category,
        clientId: oldEntry.clientId,
        locationId: td.event.locationId,
        vesselId: td.event.vesselId,
      };

      switch (td.event.event) {
        case PortCallEventType.Arrival:
          change.plannedArrival = td.start;
          change.plannedArrivalOverride = event.startOverride;
          change.plannedArrivalDuration = td.duration || null;
          change.seaDurationOverride = event.seaDurationOverride;
          // This should actually always be true, but at least we get a safe type assertion here
          if (isPortCallArrivalEvent(event)) {
            change.totalLoadDurationOverride = event.totalLoadDurationOverride;
            change.totalDischargeDurationOverride =
              event.totalDischargeDurationOverride;
            change.legAvoidsChannels = event.legAvoidsChannels;
            change.oneTimeOverrideCost = event.oneTimeOverrideCost;
            change.portCostDefaultId = event.portCostDefaultId;
            change.useHistoricalPortCost = event.useHistoricalPortCost;
            change.useLatestHistoricalPortCost =
              event.useLatestHistoricalPortCost;
            change.useOneTimeOverrideCost = event.useOneTimeOverrideCost;
            change.weatherFactor = event.weatherFactor;
            change.useIntegrationPortCost = event.useIntegrationPortCost;

            // Condition is there's to check we don't set 0 when the old value is null, making unnecessary commit diffs
            if (
              oldEntry.channelDurationOverhead ||
              td.channelDurationOverhead
            ) {
              change.channelDurationOverhead = td.channelDurationOverhead;
            }

            const oldEvent = existingEventsByClientId
              .get(oldEntry.clientId)
              ?.find(isPortCallArrivalEvent);
            if (
              event.previousLocationId &&
              ((oldEvent?.previousLocationId &&
                event.previousLocationId !== oldEvent.previousLocationId) ||
                (oldEntry.previousLocationId &&
                  event.previousLocationId !== oldEntry.previousLocationId))
            ) {
              // If previous port has changed, invalidate the sea duration override
              change.seaDurationOverride = null;
            }
            change.previousLocationId = event.previousLocationId;
          }
          break;
        case PortCallEventType.Berthing:
          change.plannedBerthing = td.start;
          change.plannedBerthingOverride = event.startOverride;
          change.plannedBerthingDuration = td.duration || null;
          break;
        case PortCallEventType.Departure:
          change.plannedDeparture = td.start;
          change.plannedDepartureOverride = event.startOverride;
          change.plannedDepartureDuration = td.duration || null;
          break;
        default:
          exhaustiveCheck(td.event);
      }

      const newEntry: typeof existingChange = {
        ...oldEntry,
        ...existingChange,
        ...change,
      };

      if (
        !isEqual(event.entry, newEntry) ||
        !existingEntries.has(newEntry.clientId)
      ) {
        updateEntryInResult(newEntry);
        changes.set(event.entry.clientId, { ...newEntry, updatedAt: now });
      }
    } else if (isStaticEventTimeData(td)) {
      /*
       * Static event entries should not be changed as they are not part of the scenario
       */
    } else {
      console.error(`Unhandled timedata type in addChangedEntriesToMap:`, td);
    }
  }

  // Determine which port call entries were deleted
  const oldPortCallIds = new Set(
    oldEvents.map(getPortCallId).filter(isDefined),
  );
  const newPortCallIds = new Set(events.map(getPortCallId).filter(isDefined));
  for (const oldPortCallId of oldPortCallIds) {
    if (!newPortCallIds.has(oldPortCallId)) {
      deletedPortCalls.add(oldPortCallId);
    }
  }

  // Determine which single event entries were deleted
  const oldSingleEventIds = new Set(
    oldEvents.filter((e) => isSingleEvent(e)).map((e) => e.entry.clientId),
  );
  const newSingleEventIds = new Set(
    events.filter((e) => isSingleEvent(e)).map((e) => e.entry.clientId),
  );
  for (const oldSingleEventId of oldSingleEventIds) {
    if (!newSingleEventIds.has(oldSingleEventId)) {
      deletedEvents.get(CoreEventType.Single)?.push(oldSingleEventId);
    }
  }

  return {
    changedTCs,
    changedEvents,
    changedOffHires,
    changedCoas,
    changedSpots,
    changedPortCalls,
    deletedPortCalls,
    // TODO: could be convenient if we also handled deleted cargo entries by adding to this map, then handing in scenario reducer completely
    deletedEvents,
    changedCargoAllocations,
    updatedTimeData: [timeData],
    changedOrderVoyageCharterIds,
  };
}

export interface ChangedEntries {
  changedTCs: ScheduleChangeMap<TCOrderEntry>;
  changedEvents: ScheduleChangeMap<SingleEntry>;
  changedOffHires: ScheduleChangeMap<OffHireEntry>;
  changedCoas: ScheduleChangeMap<CoaEntry>;
  changedSpots: ScheduleChangeMap<SpotEntry>;
  changedPortCalls: ScheduleChangeMap<PortCallEntry>;
  deletedEvents: Map<CoreEventType.Single | CoreEventType.OffHire, ClientId[]>;
  deletedPortCalls: Set<ClientId>;
  updatedTimeData: TimeData[][];
  changedCargoAllocations: Map<
    ClientId,
    null | CargoAllocation | NewCargoAllocation
  >;
  changedOrderVoyageCharterIds: Map<OrderId, string | VoyageCharterId | null>;
}

export const initChangedEntries = (
  params: Partial<ChangedEntries>,
): ChangedEntries => ({
  changedTCs: new Map(),
  changedEvents: new Map(),
  changedOffHires: new Map(),
  changedCoas: new Map(),
  changedSpots: new Map(),
  deletedEvents: new Map(),
  deletedPortCalls: new Set(),
  updatedTimeData: [],
  changedPortCalls: new Map(),
  changedCargoAllocations: new Map(),
  changedOrderVoyageCharterIds: new Map(),
  ...params,
});

/** Get changes from an auto assign result (which deals with TC timelines only) */
export const getChangedScheduleEntries = (
  vessels: readonly Vessel[],
  result: AutoAssignResult,
  originalTdsByVessel: Map<number, TimeData[]>,
  context: ScheduleDataContext,
  config: CalculationConfig,
  portCallEntries: Map<ClientId, PortCallEntry>,
): ChangedEntries => {
  const { entries, eventsByVessel } = result;
  const vesselIds = entries.map((e) => e.vesselId).filter(isDefined);
  const changedVesselIds = new Set<ShipId>(vesselIds);
  const changedTCs: ModificationResult['changedTCs'] = new Map();
  const changedEvents: ModificationResult['changedEvents'] = new Map();
  const changedOffHires: ModificationResult['changedOffHires'] = new Map();
  const changedCoas: ModificationResult['changedCoas'] = new Map();
  const changedSpots: ModificationResult['changedSpots'] = new Map();
  const changedPortCalls: ModificationResult['changedPortCalls'] = new Map();
  const deletedPortCalls: ModificationResult['deletedPortCalls'] = new Set();
  const deletedEvents: ModificationResult['deletedEvents'] = new Map([
    [CoreEventType.Single, []],
    [CoreEventType.OffHire, []],
  ]);
  const changedCargoAllocations: ModificationResult['changedCargoAllocations'] =
    new Map();
  const changedOrderVoyageCharterIds: ModificationResult['changedOrderVoyageCharterIds'] =
    new Map();

  const updatedTimeData: TimeData[][] = [];

  for (const vesselId of changedVesselIds.values()) {
    let events: Events[] | undefined = eventsByVessel.get(vesselId);
    const vessel = vessels.find((v) => v.id === vesselId);
    const originalTds = originalTdsByVessel.get(vesselId);
    if (!vessel || !events || !originalTds) throw new Error('!');

    events = attachPortCalls(
      events,
      events[0]?.vessel ?? null,
      true,
      context,
      portCallEntries,
    );

    const { timeData } = DeriveCapacitiesAndTimes(
      vessel,
      events,
      context,
      config,
      { calculateCosts: true },
    );

    const originalEvents = originalTds.map((td) => td.event);
    const firstChangedIndex = events.findIndex(
      (e, i) => !isEqual(e, originalEvents.at(i)),
    );

    const {
      updatedTimeData: [currentTimeDataUpdated],
    } = addChangedEntriesToMap(
      firstChangedIndex,
      timeData,
      events,
      originalEvents,
      {
        changedTCs,
        changedEvents,
        changedOffHires,
        changedCoas,
        changedSpots,
        changedPortCalls,
        deletedPortCalls,
        deletedEvents,
        changedCargoAllocations,
        changedOrderVoyageCharterIds,
      },
    );

    updatedTimeData.push(currentTimeDataUpdated);
  }

  return {
    changedTCs,
    changedEvents,
    changedOffHires,
    changedCoas,
    changedSpots,
    changedPortCalls,
    deletedEvents,
    deletedPortCalls,
    updatedTimeData,
    changedCargoAllocations,
    changedOrderVoyageCharterIds,
  };
};

export enum SwapDirection {
  Left = 'L',
  Right = 'R',
}

export type ModificationResult = ChangedEntries & {
  // schedule: ScheduleData;
};

const clearConcurrent = (e: SingleEvent | CargoEvent): void => {
  e.concurrent = false;
  if (isSingleEvent(e)) {
    e.entry.concurrent = false;
  } else {
    if (e.type === CoreEventType.Load) {
      e.entry.loadingConcurrent = false;
    } else {
      e.entry.dischargingConcurrent = false;
    }
  }
};

const setControlledEventDurations = (
  movableEvents: CargoEvent[],
  totalDuration: number,
): void => {
  movableEvents.forEach((e, i) => {
    const duration = divideWithRemainder(
      totalDuration,
      movableEvents.length,
      i === movableEvents.length - 1,
      minimumEventDuration,
    );
    movableEvents[i] = {
      ...e,
      duration,
      durationOverride: duration,
      portCallControlled: true,
      concurrent: false,
    };
    if (i > 0) {
      movableEvents[i].startOverride = null;
    }
  });
};

/** Rearranges events array (if total duration present, arranges events of that type together), mutates events */
export const applyPortCallDurationOverrides = <T extends Events>(
  events: T[],
  overrides: PortCallArrivalEvent,
): T[] => {
  // Normally each order related to port call only has either Load/Discharge here, but in case an order has both Load and Discharge we can't reorder those events
  const eventCountsByOrderId = countBy(events, (e) =>
    'order' in e ? e.order.id : null,
  );

  const { totalDischargeDurationOverride, totalLoadDurationOverride } =
    overrides;

  if (totalDischargeDurationOverride != null) {
    const firstDischargeIndex = findIndex(events, isDischargeEvent);
    if (firstDischargeIndex !== -1) {
      const [movables, otherEvents]: [Extract<T, CargoEvent>[], T[]] =
        partition(
          events,
          (e): e is Extract<T, CargoEvent> =>
            isDischargeEvent(e) && eventCountsByOrderId[e.order.id] === 1,
        );
      setControlledEventDurations(movables, totalDischargeDurationOverride);

      // Cargo events that could not be moved in a port call which has total duration, we must set this duration to 0
      for (let i = 0; i < otherEvents.length; ++i) {
        let e = otherEvents[i];
        if (isDischargeEvent(e)) {
          e = {
            ...e,
            duration: minimumEventDuration,
            durationOverride: minimumEventDuration,
            portCallControlled: true,
          };
          otherEvents[i] = e;
        }
      }
      otherEvents.splice(firstDischargeIndex, 0, ...movables);
      events = otherEvents;
    }
  }

  if (totalLoadDurationOverride != null) {
    const lastLoadIndex = findLastIndex(events, isLoadEvent);
    if (lastLoadIndex !== -1) {
      const [movables, otherEvents]: [Extract<T, CargoEvent>[], T[]] =
        partition(
          events,
          (e): e is Extract<T, CargoEvent> =>
            isLoadEvent(e) && eventCountsByOrderId[e.order.id] === 1,
        );
      setControlledEventDurations(movables, totalLoadDurationOverride);

      // Cargo events that could not be moved in a port call which has total duration, we must set this duration to 0
      for (let i = 0; i < otherEvents.length; ++i) {
        let e = otherEvents[i];
        if (isLoadEvent(e)) {
          e = {
            ...e,
            duration: minimumEventDuration,
            durationOverride: minimumEventDuration,
            portCallControlled: true,
          };
          otherEvents[i] = e;
        }
      }
      otherEvents.splice(lastLoadIndex - movables.length + 1, 0, ...movables);
      events = otherEvents;
    }
  }

  return events;
};

/** Wraps events in each port visit inside Arrival-Berthing-Departure event combo.
 * This should be done after the event sequence is already ordered by start time.
 * 1) Reuses existing port call events & entries from the input events array where possible.
 * 2) When events don't already exist, create them from Entries in schedule context.
 * 3) When no existing Entries, generates new ones (with insertMissingEntries = true).
 * Applies port call total load/discharge durations to individual load/discharge events.
 * Returns a new events array.
 */
export const attachPortCalls = <T extends Events>(
  originalEvents: T[],
  vessel: Vessel | null,
  insertMissingEntries: boolean, // Whether to generate empty PortCallEntries if a location doesn't have a valid one
  {
    locations,
    portCalls,
    distances,
  }: Pick<ScheduleDataContext, 'locations' | 'portCalls' | 'distances'>,
  portCallEntries: Map<ClientId, PortCallEntry> | null,
): (T | PortCallEvent)[] => {
  // @TODO remove insertMissing parameters from attachPortCalls (it's always true) https://github.com/Seaber-io/frontend/pull/626#discussion_r704354349
  // We will reuse old events where possible because these may have updated values vs. generated events
  const existingPCEvents = mapKeyBy2<
    ClientId | null,
    PortCallEventType,
    PortCallEvent
  >(
    originalEvents.filter(isPortCallEvent),
    (e) => e.portCallId,
    (e) => e.event,
  );
  const existingPCEntries = mapKeyBy(
    originalEvents.filter(isPortCallEvent).map((e) => e.entry),
    (e) => e.clientId,
  );

  // Clean up events array from port call events, which will be regenerated from scratch in this method
  const events = originalEvents.filter((e) => !isPortCallEvent(e));

  // Makes sure each port call is used only once
  const usedPortCallIds = new Set<ClientId>();

  const createdPortCallEntries = new Map<ClientId, PortCallEntry>();

  // Arrange port calls by the start time of their first event
  const locationEventGroups = groupBySequence(events, (e, i) => {
    const prevEvent = events.at(i - 1);
    const nextEvent = events.at(i + 1);

    if (isStaticEvent(e)) {
      // We want static events to be displayed separate of other events so they should not be
      // included in any surrounding port calls
      return null;
    }

    return isOffHireEvent(e)
      ? // Include off hires in the middle of the same port call when they start & end at the same place and they are surrounded by other events in the same location, otherwise break the current port call with it
        prevEvent &&
        nextEvent &&
        getEventEndLocationId(prevEvent) === getEventStartLocationId(e) &&
        getEventStartLocationId(e) === getEventEndLocationId(e) &&
        getEventEndLocationId(e) === getEventStartLocationId(nextEvent)
        ? getEventStartLocationId(e)
        : null
      : getEventLocationId(e);
  });

  // Recreate the events array by inserting PortCallEvents at the correct places.
  // Constructing to tmp array, so we don't modify our indices during the process.
  const result: (T | PortCallEvent)[] = [];
  for (let i = 0, l = locationEventGroups.length; i < l; i++) {
    const locationEvents = locationEventGroups[i];
    // Offhires and static events don't need port call data
    if (
      locationEvents.every(
        (event) => isOffHireEvent(event) || isStaticEvent(event),
      )
    ) {
      result.push(...locationEvents);
      continue;
    }

    const firstEvent = locationEvents[0];

    const isCanalTransitPortCall =
      isSingleEvent(firstEvent) &&
      firstEvent.entry.type === EventType.CANAL_TRANSIT;

    const allPortCallIds = uniq(
      locationEvents.map(getPortCallId).filter(isDefined),
    );
    const unusedPortCallIds = allPortCallIds.filter(
      (id) => !usedPortCallIds.has(id),
    );
    const eventLocationId = getEventLocationId(firstEvent);
    const validPortCallIds = unusedPortCallIds.filter((pcId) => {
      const portCallEntry =
        existingPCEntries.get(pcId) ??
        portCallEntries?.get(pcId) ??
        createdPortCallEntries.get(pcId);

      // Sometimes the location on an order changes. This may leave us with outdated port call relations, which would show as an outdated port call id on an event
      // TODO: https://github.com/Seaber-io/frontend/pull/1710#discussion_r1032390916
      return portCallEntry?.locationId === eventLocationId;
    });

    const lastEventInPrevious =
      i > 0 ? locationEventGroups.at(i - 1)?.at(-1) : undefined;
    const previousLocationId =
      lastEventInPrevious && getEventEndLocationId(lastEventInPrevious);
    let portCallId = get(validPortCallIds, 0) ?? null;

    if (portCallId === null && insertMissingEntries) {
      const location = locations.get(eventLocationId);
      const extraPortDays = stringToNumOpt(location?.extraPortDays) || 0;
      const newEntry = initPortCallEntry({
        locationId: eventLocationId,
        previousLocationId,
        plannedArrival: firstEvent.start,
        plannedArrivalDuration: hToMs(extraPortDays * 24),
        vesselId: firstEvent.vesselId,
        seaDurationOverride: firstEvent.seaDurationOverride,
      });
      // Steal the override from the first event to the port call.
      // Normally the override only exists there as a result of dropping an item onto timeline and the result is better when the override is applied on the port call instead.
      const override = firstEvent.startOverride ?? firstEvent.delayUntil;
      if (
        override != null &&
        newEntry.plannedBerthingOverride === null &&
        firstEvent.type !== CoreEventType.Single
      ) {
        newEntry.plannedBerthingOverride = override - 2 * minimumEventDuration;
        firstEvent.startOverride = null;
        firstEvent.delayUntil = undefined;
      }

      portCallId = newEntry.clientId;
      createdPortCallEntries.set(portCallId, newEntry);
    }

    // Entries in store may have mismatches, especially with legacy data. Fix with the correct id.
    locationEvents.forEach((e) => {
      if (!isOffHireEvent(e) && !isStaticEvent(e)) {
        e.portCallId = portCallId;
      }
    });

    if (portCallId === null) {
      if (allPortCallIds.length > 0) {
        console.error(
          `Port call ids ${allPortCallIds} on new visit to location at ${new Date(
            firstEvent.start,
          )} were already used on an earlier visit.`,
        );
      } else {
        console.error(
          `Visit to location at ${new Date(
            firstEvent.start,
          )} did not refer to a port call`,
        );
      }
      result.push(...locationEvents);
      continue;
    }

    usedPortCallIds.add(portCallId);

    const entry =
      existingPCEntries.get(portCallId) ??
      portCallEntries?.get(portCallId) ??
      createdPortCallEntries.get(portCallId);

    if (!entry) {
      console.error(
        `Port call entry ${portCallId} referred to by an event was not found`,
      );
      result.push(...locationEvents);
      continue;
    }

    const shipmentCount = uniqBy(locationEvents.filter(isCargoEvent), (e) =>
      'shipmentId' in e.entry ? e.entry.shipmentId : e.order.shipmentId,
    ).length;

    const applyShipmentCount = (e: PortCallEvent): PortCallEvent =>
      e.shipmentCount === shipmentCount
        ? e
        : {
            ...e,
            shipmentCount,
          };

    const pce = existingPCEvents.get(portCallId);

    let arrival = pce?.get(PortCallEventType.Arrival);
    let berthing = pce?.get(PortCallEventType.Berthing);
    let departure = pce?.get(PortCallEventType.Departure);

    if (!(arrival && berthing && departure)) {
      // Turn entry to events
      const createdEvents = portCallEntryToPortCallEvents(
        entry,
        portCalls.get(portCallId) ?? null,
        vessel,
        locations,
        firstEvent.start,
        firstEvent.seaDurationOverride,
        shipmentCount,
        isCanalTransitPortCall,
      );
      arrival ||= createdEvents.a;
      berthing ||= createdEvents.b;
      departure ||= createdEvents.d;
    }

    assertKind(
      arrival,
      isPortCallArrivalEvent,
      `Port call arrival event for ${arrival.portCallId} is wrong type ${arrival.event}`,
    );

    // When override on berthing is used to place the port call on the timeline we need to update arrival start.
    // This is mostly only relevant for the first port call on the timeline - but in case of COA this is practically every shipment.
    if (berthing.startOverride && !arrival.startOverride) {
      arrival.start =
        berthing.startOverride - (arrival.durationOverride ?? arrival.duration);
    }

    if (
      previousLocationId !== undefined &&
      previousLocationId !== arrival.previousLocationId
    ) {
      arrival = {
        ...arrival,
        previousLocationId,
      };
    }

    // Check if vessel has banned channels and if the route uses them
    if (
      vessel &&
      previousLocationId !== undefined &&
      !arrival.legAvoidsChannels
    ) {
      const bannedChannels = getVesselBannedChannels(vessel);
      if (bannedChannels.size > 0) {
        const routeWithChannels = distances.get(
          previousLocationId,
          arrival.locationId,
          false,
        );
        const bannedChannelsInRoute = (
          routeWithChannels?.used_channels ?? []
        ).filter((c) => bannedChannels.has(c.toLowerCase()));

        if (bannedChannelsInRoute.length > 0) {
          arrival = {
            ...arrival,
            legAvoidsChannels: true,
          };
        }
      }
    }

    // push all events wrapped by port call events
    result.push(
      applyShipmentCount(arrival),
      applyShipmentCount(berthing),
      ...applyPortCallDurationOverrides(locationEvents, arrival),
      applyShipmentCount(departure),
    );
  }

  return result;
};

/** Determines from a sequence of events whether the given port call is valid for swapping with the adjacent one. */
export const canSwapPortCall = (
  originalEvents: Events[],
  portCallCId: ClientId,
  shipmentIds: Array<ScenarioOrder['shipmentId']>,
  direction: SwapDirection,
): boolean => {
  const eventsByLocation = groupBySequence(originalEvents, (e) =>
    getEventLocationId(e),
  );
  const portCallSeqIndex = eventsByLocation.findIndex((events) =>
    events.some((e) => e.portCallId === portCallCId),
  );

  const directionStep = direction === SwapDirection.Left ? -1 : 1;
  const pairIndex = portCallSeqIndex + directionStep;
  if (pairIndex < 0 || pairIndex >= eventsByLocation.length) {
    return false;
  }

  const pairEvents = eventsByLocation[pairIndex];

  const swappedOrders = new Set(
    originalEvents
      .filter(isCargoEvent)
      .filter(
        (e) =>
          e.portCallId === portCallCId &&
          shipmentIds.includes(e.order.shipmentId),
      )
      .map((e) => e.order.id),
  );

  // When swapping, the same order not allowed to be in both ports
  return !pairEvents.some(
    (e) => isCargoEvent(e) && swappedOrders.has(e.order.id),
  );
};

export const swapPortCall = <T extends Events>(
  originalEvents: T[],
  portCallCId: ClientId,
  shipmentIds: Array<ScenarioOrder['shipmentId']>,
  direction: SwapDirection,
  step: number = 1,
): T[] => {
  if (!canSwapPortCall(originalEvents, portCallCId, shipmentIds, direction)) {
    throw new Error(`port call swap is not valid`);
  }
  const events = [...originalEvents];

  const eventsByLocation = groupBySequence(events, (e) =>
    getEventLocationId(e),
  );
  const portCallSeqIndex = eventsByLocation.findIndex((events) =>
    events.some((e) => e.portCallId === portCallCId),
  );
  if (!step) {
    step = 1;
  }
  if (step === 0) {
    throw new Error('Step must not be 0');
  }

  const directionStep = direction === SwapDirection.Left ? -1 * step : step;
  const pairIndex = portCallSeqIndex + directionStep;

  const targetPCAllEvents = get(eventsByLocation, portCallSeqIndex);
  const pairPCAllEvents = get(eventsByLocation, pairIndex);
  assertDefined(targetPCAllEvents, `Expected events to swap to be found`);
  assertDefined(pairPCAllEvents, `Expected events to swap with to be found`);

  const targetPCAllShipmentIds = uniq(
    targetPCAllEvents.flatMap((e) =>
      isCargoEvent(e) ? [e.order.shipmentId] : [],
    ),
  );
  const pairPCAllShipmentIds = uniq(
    pairPCAllEvents.flatMap((e) =>
      isCargoEvent(e) ? [e.order.shipmentId] : [],
    ),
  );

  /** Which events to move. Move whole port call if only one shipment in PC. */
  const targetEvents = targetPCAllShipmentIds.every((shipmentId) =>
    shipmentIds.includes(shipmentId),
  )
    ? targetPCAllEvents
    : targetPCAllEvents.filter(
        (e) => isCargoEvent(e) && shipmentIds.includes(e.order.shipmentId),
      );
  /** Which events to move from the other port call. */
  const pairEvents =
    // Every order belongs to our shipment, move whole PC
    pairPCAllShipmentIds.every((shipmentId) =>
      shipmentIds.includes(shipmentId),
    ) ||
    // No order belongs to our shipment, we can move whole PC in this case too
    pairPCAllShipmentIds.every(
      (shipmentId) => !shipmentIds.includes(shipmentId),
    )
      ? pairPCAllEvents
      : // Move only events belonging to our shipment
        pairPCAllEvents.filter(
          (e) => isCargoEvent(e) && shipmentIds.includes(e.order.shipmentId),
        );

  const targetFirstIndex: number = events.indexOf(targetEvents[0]);
  const pairFirstIndex: number = events.indexOf(pairEvents[0]);

  // Rename target/pair variables to left/right so we can  treat them uniformly on the actual swapping in the array
  const [leftFirstIndex, _rightFirstIndex]: [number, number] =
    targetFirstIndex < pairFirstIndex
      ? [targetFirstIndex, pairFirstIndex]
      : [pairFirstIndex, targetFirstIndex];
  const [leftEvents, rightEvents] =
    targetFirstIndex < pairFirstIndex
      ? [targetEvents, pairEvents]
      : [pairEvents, targetEvents];
  const [leftPCAllEvents, rightPCAllEvents] =
    targetFirstIndex < pairFirstIndex
      ? [targetPCAllEvents, pairPCAllEvents]
      : [pairPCAllEvents, targetPCAllEvents];

  const leftIsFullPortCall = leftEvents.length === leftPCAllEvents.length;
  const rightIsFullPortCall = rightEvents.length === rightPCAllEvents.length;

  const leftPCId = leftEvents[0].portCallId;
  const rightPCId = rightEvents[0].portCallId;
  const eventAfterRight =
    get(events, findLastIndex(events, (e) => e.portCallId === rightPCId) + 1) ??
    null;

  /** We'll mutate and insert left/right events back into this array */
  const result = without(events, ...leftEvents, ...rightEvents);

  /** If Left PC moving completely, place Right where Left was. Otherwise after the remaining events of Left. */
  const leftInsertAt = leftIsFullPortCall
    ? leftFirstIndex
    : Math.max(0, findLastIndex(result, (e) => e.portCallId === leftPCId) + 1);
  result.splice(leftInsertAt, 0, ...rightEvents);

  /** If Right PC moving completely, place at the end of array or Right before the PC that used to follow Right. Otherwise before remaining events of Right. */
  const rightInsertAt = rightIsFullPortCall
    ? eventAfterRight === null
      ? result.length
      : result.indexOf(eventAfterRight)
    : findIndex(
        result,
        (e) => e.portCallId === rightPCId,
        leftInsertAt + rightEvents.length,
      );
  result.splice(rightInsertAt, 0, ...leftEvents);

  /** After swap start overrides usually don't make sense, so we'll clear all for the affected events. */
  result.forEach((e, i) => {
    if (leftEvents.includes(e) || rightEvents.includes(e)) {
      result[i] = {
        ...e,
        startOverride: null,
        seaDurationOverride: null,
      };
    }
  });

  return result;
};

export const swapEvent = <T extends Events>(
  originalEvents: T[],
  indexToMove: number,
  direction: SwapDirection,
): T[] => {
  const newEvents = originalEvents.slice();

  const directionStep = direction === SwapDirection.Right ? 1 : -1;

  let indexOfFirstPair = findIndexWith(
    originalEvents,
    indexToMove + directionStep,
    directionStep,
    (e) => !e.skipOnSwap,
  );
  if (indexOfFirstPair === undefined) {
    throw new Error(
      `Cannot swap item: no acceptable swap candidate in the direction of the swap`,
    );
  }

  // If swapping with a sequence of port call controlled events, find the first one as the primary pair to trade timing overrides with
  while (
    originalEvents[indexOfFirstPair].portCallControlled &&
    originalEvents[indexOfFirstPair - 1]?.portCallControlled
  ) {
    indexOfFirstPair--;
  }

  const targetEvent = originalEvents[indexToMove];
  if (targetEvent.portCallControlled) {
    throw new Error('cannot swap port call controlled events');
  }
  const pairEvent = originalEvents[indexOfFirstPair];
  const targetTimingOverrides: TimingOverrides = targetEvent;
  const pairTimingOverrides: TimingOverrides = pairEvent;
  const updatedPair = (newEvents[indexOfFirstPair] = assignEventTimingOverrides(
    targetTimingOverrides,
    pairEvent,
  ));
  const updatedTarget = (newEvents[indexToMove] = assignEventTimingOverrides(
    pairTimingOverrides,
    targetEvent,
  ));

  for (const [e, other] of [
    [updatedTarget, updatedPair],
    [updatedPair, updatedTarget],
  ]) {
    if (isSingleEvent(e) || isCargoEvent(e)) {
      clearConcurrent(e);
    }
    if (isSingleEvent(e)) {
      // Single events can move from port to another, in which case their location needs to update NOTE: does not update locationName & locationTimezone (which we do not have available here), probably not necessary as these get fixed on recomputation automatically
      e.locationId = getEventLocationId(other);
    }
  }

  const indexOfLastPair =
    (findIndexWith(
      originalEvents,
      indexOfFirstPair + 1,
      1,
      (e) => !e.portCallControlled || e.portCallId !== updatedPair.portCallId,
    ) ?? originalEvents.length) - 1;

  return swapSubArrays(
    newEvents,
    [indexToMove, indexToMove + 1],
    [indexOfFirstPair, indexOfLastPair + 1],
  );
};

export const swapScheduleEntry = <T extends Events>(
  tds: TimeData<T>[],
  vessel: Vessel | null,
  swappedTd: TimeData,
  direction: SwapDirection,
  context: ScheduleDataContext,
  config: CalculationConfig,
  portCallEntries: Map<ClientId, PortCallEntry>,
): ModificationResult => {
  // Swap would have to deal with swapping potential port calls as well. For the purpose of swap we'll assume port calls don't exist.
  const oldEvents = tds.map((td) => td.event);
  const eventsWithoutPortCalls = oldEvents.filter((e) => !isPortCallEvent(e));

  const indexToMove = eventsWithoutPortCalls.findIndex(
    (e) => e === swappedTd.event,
  );

  if (indexToMove === -1) {
    throw new Error(
      `Could not find index for event to swap. Some sort of timeline mixup.`,
    );
  }

  const newEventsWithoutPortCalls = swapEvent(
    eventsWithoutPortCalls,
    indexToMove,
    direction,
  );

  const firstChangedEvent =
    newEventsWithoutPortCalls[
      findIndex(
        newEventsWithoutPortCalls,
        (e, i) => eventsWithoutPortCalls[i] !== e,
      )
    ];

  const newEvents = attachPortCalls(
    newEventsWithoutPortCalls,
    vessel,
    true,
    context,
    portCallEntries,
  );

  let firstChanged = findIndex(
    newEvents,
    (e) =>
      e === firstChangedEvent || e.portCallId === firstChangedEvent?.portCallId,
  );
  if (firstChanged === -1) {
    firstChanged = newEvents.length - 1;
  }

  resetArrivalDurations(firstChanged, newEvents, config);

  return deriveEventModifications(
    newEvents,
    tds.map((td) => td.event),
    vessel,
    context,
    config,
    portCallEntries,
    { firstChangedIndex: Math.min(indexToMove, firstChanged) },
  );
};

export function resetArrivalDurations(
  fromIndex: number,
  events: Events[],
  calculationConfig: CalculationConfig,
) {
  const now = Date.now();
  for (let i = fromIndex; i < events.length; i++) {
    const event = events[i];
    if (isPortCallArrivalEvent(event) && event.start > now) {
      const location = getEventLocation(event, calculationConfig.locationsById);
      const extraPortDuration = hToMs(
        (stringToNumOpt(location?.extraPortDays) || 0) * 24,
      );
      events[i] = {
        ...event,
        duration: extraPortDuration || minimumEventDuration,
      };
    }
  }
}

export const getPortCallSwapModifications = <T extends Events>(
  tds: TimeData<T>[],
  vessel: Vessel | null,
  portCallId: ClientId,
  shipmentIds: ScenarioOrder['shipmentId'][],
  direction: SwapDirection,
  context: ScheduleDataContext,
  config: CalculationConfig,
  step: number,
  portCallEntries: Map<ClientId, PortCallEntry>,
): ModificationResult => {
  const events = tds.map((td) => td.event);

  const newEvents = swapPortCall(
    events,
    portCallId,
    shipmentIds,
    direction,
    step,
  );

  let firstChanged = findIndex(newEvents, (e, i) => events[i] !== e);
  if (firstChanged === -1) {
    firstChanged = newEvents.length - 1;
  }

  return deriveEventModifications(
    newEvents,
    tds.map((td) => td.event),
    vessel,
    context,
    config,
    portCallEntries,
    { firstChangedIndex: firstChanged },
  );
};

/**
 * Reorders port calls to match a target sequence based on ETA updates.
 * Used when external ETA updates suggest a different port order than the current rotation.
 */
export const reorderPortCallsBySequence = <T extends Events>(
  tds: TimeData<T>[],
  vessel: Vessel | null,
  targetPortCallOrder: ClientId[],
  context: ScheduleDataContext,
  config: CalculationConfig,
  portCallEntries: Map<ClientId, PortCallEntry>,
): ModificationResult => {
  const events = tds.map((td) => td.event);

  // Group events by location (port call)
  const eventsByLocation = groupBySequence(events, (e) =>
    getEventLocationId(e),
  );

  // Create a map of portCallId to its location group
  const portCallToLocationGroup = new Map<ClientId | null, T[]>();
  for (const locationEvents of eventsByLocation) {
    const portCallId = locationEvents.find((e) => e.portCallId)?.portCallId;
    if (portCallId) {
      portCallToLocationGroup.set(portCallId, locationEvents);
    }
  }

  // Build the target order map
  const targetOrderMap = new Map(targetPortCallOrder.map((pc, i) => [pc, i]));
  const targetPortCallSet = new Set(targetPortCallOrder);

  // Reorder the location groups based on target order
  const reorderedLocationGroups = [...eventsByLocation].sort((a, b) => {
    const pcA = a.find((e) => e.portCallId)?.portCallId;
    const pcB = b.find((e) => e.portCallId)?.portCallId;

    const hasOrderA = pcA && targetOrderMap.has(pcA);
    const hasOrderB = pcB && targetOrderMap.has(pcB);

    // If both have target order, sort by that
    if (hasOrderA && hasOrderB) {
      return targetOrderMap.get(pcA!)! - targetOrderMap.get(pcB!)!;
    }

    return eventsByLocation.indexOf(a) - eventsByLocation.indexOf(b);
  });

  // Flatten back to events array
  let reorderedEvents = reorderedLocationGroups.flat();

  // Clear startOverride and seaDurationOverride for events in affected port calls
  // This ensures the timing is recalculated from scratch after reordering
  reorderedEvents = reorderedEvents.map((e) => {
    if (e.portCallId && targetPortCallSet.has(e.portCallId)) {
      return {
        ...e,
        startOverride: null,
        seaDurationOverride: null,
      };
    }
    return e;
  });

  // Find the first changed event index
  const firstChanged = reorderedEvents.findIndex((e, i) => events[i] !== e);

  return deriveEventModifications(
    reorderedEvents,
    events,
    vessel,
    context,
    config,
    portCallEntries,
    { firstChangedIndex: Math.max(0, firstChanged) },
  );
};

/*
 Modifies newEvents to resolve timing conflicts
 and returns updated firstChangedIndex
*/
function resolveTimingConflicts<T extends Events>(
  changes: Partial<Events>,
  eventIndexToModify: number,
  newEvents: T[],
  vessel: Vessel,
  context: ScheduleDataContext,
  config: CalculationConfig,
  events: T[],
  firstChangedIndex: number,
) {
  const { timeData } = DeriveCapacitiesAndTimes(
    vessel,
    events,
    context,
    config,
    {
      calculateCosts: false,
    },
    Date.now(),
  );

  const tdToModify = timeData[eventIndexToModify];

  const prevIndex = eventIndexToModify - 1;
  const arrivalIndex = findIndexWith(
    newEvents,
    prevIndex,
    -1,
    isPortCallArrivalEvent,
  );
  const arrival = withDefined(arrivalIndex, (idx) =>
    assertedCast(newEvents[idx], isPortCallArrivalEvent),
  );

  // When changing start of ETD, we apply total load/discharge durations
  // because otherwise the cargo events original durations would prevent moving ETD backwards.
  // We handle ETD changes as a special case because altering it affects every event in the port call and not only the previous row.
  if (
    'startOverride' in changes &&
    isDefined(changes.startOverride) &&
    isTimeData(tdToModify) &&
    isPortCallDepartureEventTimeData(tdToModify) &&
    arrival
  ) {
    const berthingTd = findLast(
      timeData,
      isPortCallBerthingEventTimeData,
      eventIndexToModify,
    );
    assertDefined(
      berthingTd,
      `no matching etb event found for etd at ${eventIndexToModify}`,
    );

    const locationTds = timeData.filter(
      (td) => td.event.portCallId === tdToModify.event.portCallId,
    );
    const cargoTds = locationTds.filter(isCargoEventTimeData);
    /** Single events, idle time etc */
    const nonCargoTds = locationTds.filter(
      (td) => !isCargoEventTimeData(td) && !isPortCallEventTimeData(td),
    );

    const nonCargoEventsDuration = sumBy(nonCargoTds, (td) => td.duration);
    const currentInnerDuration = tdToModify.start - berthingTd.end;
    // Changing ETD also moves any idle times to L/D durations
    const currentTotalCargoAndIdleDuration =
      currentInnerDuration - nonCargoEventsDuration;

    const etdStartDelta = tdToModify.start - changes.startOverride;
    const newTotalCargoDuration = Math.max(
      minimumEventDuration * cargoTds.length,
      currentTotalCargoAndIdleDuration - etdStartDelta,
    );
    const [loadTds, dischargeTds] = partition(cargoTds, isLoadEventTimeData);
    if (loadTds.length > 0) {
      const totalLoadDurationOverride = divideWithRemainder(
        newTotalCargoDuration * loadTds.length,
        cargoTds.length,
        false,
      );
      for (const td of loadTds) {
        const isLast = td === loadTds.at(-1);
        td.event.durationOverride = divideWithRemainder(
          totalLoadDurationOverride,
          loadTds.length,
          isLast,
        );
        td.event.portCallControlled = true;
      }
      arrival.totalLoadDurationOverride = totalLoadDurationOverride;
    }
    if (dischargeTds.length > 0) {
      const totalDischargeDurationOverride = divideWithRemainder(
        newTotalCargoDuration * dischargeTds.length,
        cargoTds.length,
        true,
      );
      for (const td of dischargeTds) {
        const isLast = td === dischargeTds.at(-1);
        td.event.durationOverride = divideWithRemainder(
          totalDischargeDurationOverride,
          dischargeTds.length,
          isLast,
        );
        td.event.portCallControlled = true;
      }
      arrival.totalDischargeDurationOverride = totalDischargeDurationOverride;
    }

    const cargoDurationDelta =
      currentTotalCargoAndIdleDuration - newTotalCargoDuration;
    // After we already reduced total load/discharge to 0, we'll continue to subtract the change from ETB duration
    if (etdStartDelta > cargoDurationDelta) {
      const etbDelta = etdStartDelta - cargoDurationDelta;
      berthingTd.event.durationOverride = berthingTd.event.duration = Math.max(
        minimumEventDuration,
        berthingTd.event.duration - etbDelta,
      );
    }
    return findIndex(
      timeData,
      (td) => !isPortCallEventTimeData(td),
      arrivalIndex,
    );
  }

  // Handling of all other events besides ETD. Altering a start time updates the previous row's duration.
  if (
    'start' in changes ||
    ('startOverride' in changes && changes.startOverride !== null)
  ) {
    const prevEvent = get(newEvents, prevIndex);
    const hasDurationOverride = prevEvent && 'durationOverride' in prevEvent;
    if (isPortCallArrivalEvent(newEvents[eventIndexToModify])) {
      if (eventIndexToModify > 0) {
        newEvents[eventIndexToModify].seaDurationOverride =
          minimumEventDuration;
        const { timeData } = DeriveCapacitiesAndTimes(
          vessel,
          newEvents,
          context,
          config,
          {
            calculateCosts: false,
          },
          Date.now(),
        );
        const prevTd = timeData[prevIndex];
        const currentTd = timeData[eventIndexToModify];
        newEvents[eventIndexToModify].seaDurationOverride =
          currentTd.start - prevTd.end;
      }
    } else if (
      eventIndexToModify > 0 &&
      ((prevEvent && 'duration' in prevEvent) || hasDurationOverride)
    ) {
      let resolved = false;
      if (arrivalIndex !== undefined && arrival) {
        const start = changes.start || changes.startOverride;

        assertDefined(
          start,
          'start should be defined when resolving timing conflicts',
        );

        const delta = timeData[eventIndexToModify].start - start;

        // If we have caused a change to a load/discharge event's duration and we are using the total duration, we need to update it too.
        if (
          isLoadEvent(prevEvent) &&
          arrival.totalLoadDurationOverride != null
        ) {
          arrival.totalLoadDurationOverride = Math.max(
            minimumEventDuration,
            arrival.totalLoadDurationOverride - delta,
          );
          resolved = true;
        } else if (
          isDischargeEvent(prevEvent) &&
          arrival.totalDischargeDurationOverride != null
        ) {
          arrival.totalDischargeDurationOverride = Math.max(
            minimumEventDuration,
            arrival.totalDischargeDurationOverride - delta,
          );
          resolved = true;
        }
        if (resolved) {
          firstChangedIndex = arrivalIndex;
        }
      }
      if (!resolved) {
        newEvents[prevIndex] = {
          ...prevEvent,
          duration: minimumEventDuration,
        };
        if (hasDurationOverride) {
          newEvents[prevIndex].durationOverride = minimumEventDuration;
        }
        const { timeData } = DeriveCapacitiesAndTimes(
          vessel,
          newEvents,
          context,
          config,
          {
            calculateCosts: false,
          },
          Date.now(),
        );
        const prevTd = timeData[prevIndex];
        const currentTd = timeData[eventIndexToModify];
        const duration = currentTd.start - prevTd.start;
        newEvents[prevIndex] = {
          ...prevEvent,
          duration,
        };
        if (hasDurationOverride) {
          newEvents[prevIndex].durationOverride = duration;
        }
        if (firstChangedIndex > 0) {
          firstChangedIndex--;
        }
      }
    }
  } else if ('startOverride' in changes && changes.startOverride === null) {
    // SEABER-2434: Clearing start override should also clear the duration override on the previous row
    const prevIndex = eventIndexToModify - 1;
    const prevEvent = get(newEvents, prevIndex);
    const currentEvent = newEvents[eventIndexToModify];
    if (isPortCallArrivalEvent(currentEvent)) {
      currentEvent.seaDurationOverride = null;
    } else if (prevEvent) {
      firstChangedIndex = Math.min(firstChangedIndex, prevIndex);
      if ('durationOverride' in prevEvent) {
        prevEvent.durationOverride = null;
      } else if (isPortCallArrivalEvent(prevEvent)) {
        prevEvent.duration = minimumEventDuration;
      }
    }
  }

  return firstChangedIndex;
}

export const modifyEntry = <T extends Events>(
  context: ScheduleDataContext,
  voyageGroup: VoyageGroup<T>,
  tdIndexToModify: number, // Index is given as it stands in "eventsAndLegs"
  changes: Partial<Events>,
  config: CalculationConfig,
  portCallEntries: Map<ClientId, PortCallEntry>,
): ModificationResult => {
  if (!voyageGroup) throw new Error('!');

  const tdToModify = voyageGroup.eventsAndLegs[tdIndexToModify];

  if (!isTimeData(tdToModify)) {
    throw new Error(`Event to modify not a timedata`);
  }
  const eventToModify = tdToModify.event;

  const tds = voyageGroup.events;
  const events = tds.map((td) => td.event);

  // Index along "events" where there are no implicit events in between
  const eventIndexToModify = events.findIndex((e) => e === eventToModify);
  let firstChangedIndex = eventIndexToModify;

  const vessel = voyageGroup.vessel ?? defaultVessel;

  const newEvents = events.slice();

  const existing = events[eventIndexToModify];

  // Ensure that discharge event stays after the load event on the same order
  if (isCargoEvent(existing) && existing.type === CoreEventType.Discharge) {
    const origSchedule = DeriveCapacitiesAndTimes(
      vessel,
      newEvents,
      context,
      config,
      {
        calculateCosts: true,
      },
    );
    const order = existing.order;
    const loadIndex = events.findIndex(
      (e) =>
        isCargoEvent(e) && e.type === CoreEventType.Load && e.order === order,
    );
    const loadTd = origSchedule.timeData[loadIndex];

    if (changes.durationOverride) {
      changes.durationOverride = Math.max(
        changes.durationOverride,
        minimumEventDuration,
      );
    }
    if (changes.seaDurationOverride) {
      changes.seaDurationOverride = Math.max(
        changes.seaDurationOverride,
        minimumEventDuration,
      );
    }
    if (changes.startOverride) {
      const startOverride = Math.max(changes.startOverride, loadTd.end);
      changes.startOverride = startOverride;
      changes.start = startOverride;
    }
  }

  /** When a port call total duration is cleared we'll clear duration overrides on the events that were affected.
   * This is not a critical step, but is probably a better user experience as these durations were set by the port call total override. */
  if (
    'totalDischargeDurationOverride' in changes ||
    'totalLoadDurationOverride' in changes
  ) {
    for (const [i, e] of newEvents.entries()) {
      if (
        changes.totalDischargeDurationOverride === null &&
        isDischargeEvent(e) &&
        e.portCallId === existing.portCallId
      ) {
        newEvents[i] = { ...newEvents[i], durationOverride: null };
      }
      if (
        changes.totalLoadDurationOverride === null &&
        isLoadEvent(e) &&
        e.portCallId === existing.portCallId
      ) {
        newEvents[i] = { ...newEvents[i], durationOverride: null };
      }
    }
  }

  newEvents[eventIndexToModify] = {
    ...existing,
    ...changes,
  };

  let firstSourcedIndex = -1;
  if (
    'start' in changes ||
    ('startOverride' in changes && changes.startOverride !== null) ||
    ('duration' in changes && changes.duration !== null) ||
    ('durationOverride' in changes && changes.durationOverride !== null) ||
    ('totalDischargeDurationOverride' in changes &&
      changes.durationOverride !== null) ||
    ('totalLoadDurationOverride' in changes &&
      changes.durationOverride !== null)
  ) {
    // When total port call duration was set, we don't want this to clear startOverride on the ETB because it only affects ETD timing, not ETB.
    // TODO: Might make sense to move the clearing logic to e.g. deriveEventModifications where we can check if the computed start time has changed, and simply clear based on that without complicated rules.
    const startClearingFrom =
      isPortCallArrivalEvent(existing) &&
      !('startOverride' in changes || 'durationOverride' in changes)
        ? eventIndexToModify + 2
        : eventIndexToModify + 1;
    for (let i = startClearingFrom; i < newEvents.length; i++) {
      const newEvent = newEvents[i];
      if (isPortCallEvent(newEvent) && isPortCallEventStartSourced(newEvent)) {
        firstSourcedIndex = i;
        // Do not override any sourced port call start times
        break;
      }

      newEvents[i] = {
        ...newEvent,
        startOverride: null,
      };
    }
  }

  firstChangedIndex = resolveTimingConflicts(
    changes,
    eventIndexToModify,
    newEvents,
    vessel,
    context,
    config,
    events,
    firstChangedIndex,
  );
  if (firstSourcedIndex !== -1) {
    firstChangedIndex = Math.min(
      firstChangedIndex,
      resolveTimingConflicts(
        newEvents[firstSourcedIndex],
        firstSourcedIndex,
        newEvents,
        vessel,
        context,
        config,
        events,
        firstChangedIndex,
      ),
    );
  }

  return deriveEventModifications(
    newEvents,
    events,
    voyageGroup.vessel,
    context,
    config,
    portCallEntries,
    { firstChangedIndex },
  );
};

/** Sets cleaningOmitted to the following load event's entry after the deleted CLEANING event */
const setCleaningOmittedAfterDelete = (
  events: TCTimelineEvent[],
  indexToDelete: number,
): TCTimelineEvent[] => {
  let omittingEntry: TCOrderEntry | undefined;

  return events.map((e, i) => {
    if (i < indexToDelete) return e;
    if (isLoadEvent(e) && !omittingEntry) {
      return {
        ...e,
        entry: (omittingEntry = {
          ...e.entry,
          cleaningOmitted: true,
        }),
      };
    } else if (
      isDischargeEvent(e) &&
      omittingEntry &&
      e.entry.clientId === omittingEntry.clientId
    ) {
      return {
        ...e,
        entry: omittingEntry,
      };
    }
    return e;
  });
};

export const deleteEntry = (
  context: ScheduleDataContext,
  eventsByVessel: VesselToSortedEvents | undefined,
  indexToDelete: number,
  vesselId: ShipId,
  config: CalculationConfig,
  portCallEntries: Map<ClientId, PortCallEntry>,
): ModificationResult => {
  if (!eventsByVessel) throw new Error('!');

  const events = eventsByVessel.get(vesselId);
  if (!events) throw new Error('!');

  const vessel = context.vessels.get(vesselId);

  if (!vessel) throw new Error('!');

  const shouldRemoveAt = (idx: number): boolean => {
    const e = events[idx];
    if (!e) {
      return false;
    }
    return isTCOrderEvent(event)
      ? isTCOrderEvent(e) && e.order.id === event.order.id
      : e === event;
  };

  const event = events[indexToDelete];

  let newEvents = events.filter((_e, i) => !shouldRemoveAt(i));
  // Transfer any necessary timing overrides that will fall onto the next event and drop the removed event (and its pair if any)
  newEvents = transferDeletedEventsTimingOverrides(newEvents, events);

  if (isSingleEvent(event) && event.event === SingleEventType.CLEANING) {
    newEvents = setCleaningOmittedAfterDelete(newEvents, indexToDelete);
  }

  return deriveEventModifications(
    newEvents,
    events,
    vessel,
    context,
    config,
    portCallEntries,
  );
};

export type Id<
  T extends { id?: K | undefined | null },
  K extends string | number = string | number,
> = T & { id: K };

export type DurationEstimates = {
  loadingDuration: number;
  dischargeDuration: number;
  ladenVoyageDuration: number;
  totalDuration: number;
};

export type WithDurationEstimates<T extends CoaEntry> = T & {
  estimates: DurationEstimates | undefined;
};

interface OrderDurationEstimates {
  distances: Distances;
  config: CalculationConfig;
  order: ScenarioOrder;
  coasById: ScheduleDataContext['coasById'];
  vessel?: Vessel;
}

export const getOrderDurationEstimates = ({
  distances,
  config,
  order,
  coasById,
  vessel,
}: OrderDurationEstimates): DurationEstimates | undefined => {
  if (!order.loadPortId) return undefined;

  const mass = order.cargoRanges.mass.max;
  const volume = order.cargoRanges.volume.max;
  const vesselSpeedLaden = getDefaultVesselSpeedByType(
    vessel,
    VesselSpeedType.LADEN,
    fallbackLadenSpeed,
  );
  const ladenDistance = distances.get(
    order.loadPortId,
    order.dischargePortId,
    false,
  )?.dist;
  const hoursAtSea = ladenDistance ? ladenDistance / vesselSpeedLaden : 0;
  const ladenVoyageDuration = hToMs(hoursAtSea);

  const { loadingRate, loadingUnit, dischargingRate, dischargingUnit } =
    getEffectiveHandlingRates(order, null, vessel ?? null, coasById, config);

  const loadingWork = calculateWorkShifts(
    order.locationByLoadPortId,
    null,
    getLoadingTime(
      mass,
      volume,
      loadingRate,
      loadingUnit,
      msToH(Number(order.loadingOverheadDuration ?? 0)),
    ),
    getOrderLaytimeDays('loading', order, coasById),
  );
  const loadingDuration = hToMs(
    ceilIfEnabled(loadingWork.duration, config.useRoundedEstimations),
  );

  const dischargeWork = calculateWorkShifts(
    order.locationByDischargePortId,
    null,
    getLoadingTime(
      mass,
      volume,
      dischargingRate,
      dischargingUnit,
      msToH(Number(order.dischargingOverheadDuration ?? 0)),
    ),
    getOrderLaytimeDays('discharge', order, coasById),
  );
  const dischargeDuration = hToMs(
    ceilIfEnabled(dischargeWork.duration, config.useRoundedEstimations),
  );

  const totalDuration =
    dischargeDuration + loadingDuration + ladenVoyageDuration;

  return {
    dischargeDuration,
    loadingDuration,
    ladenVoyageDuration,
    totalDuration,
  };
};

/*
 * Get time difference between start of port call arrival event and end of departure event
 */
export const getPortCallDuration = (portCall: PortCallData): number => {
  const eta = Math.min(...portCall.tds.map((td) => td.arriveInPort));
  const etd = Math.max(...portCall.tds.map((td) => td.end));

  return etd - eta;
};

export const isPortNoticeTimesValid = (
  p: Pick<
    ScenarioTimeline_PortNoticeFragment,
    'eta' | 'berthingProspects' | 'etdBerth'
  >,
): boolean => {
  const eta = dateStringToNumOpt(p.eta) ?? -Infinity;
  const etb = dateStringToNumOpt(p.berthingProspects) ?? eta;
  const etd = dateStringToNumOpt(p.etdBerth) ?? etb;

  return eta <= etb && etb <= etd;
};

/** Weight in metric tonnes */
export function getShipWeightCapacity(
  weight: number,
  unit: WeightUnits,
): MetricTonnes {
  switch (unit) {
    case WeightUnits.TONNE:
    case WeightUnits.ADMT:
    case WeightUnits.BDMT:
    case WeightUnits.GMT:
      return weight as MetricTonnes;
    case WeightUnits.KG:
      return (weight / 1000) as MetricTonnes;
    case WeightUnits.LONGTON: // 2,240 pounds
      return (weight * LONG_TON_IN_METRIC_TONS) as MetricTonnes;
    case WeightUnits.POUND:
      return (weight * POUND_IN_METRIC_TONS) as MetricTonnes;
    case WeightUnits.SHORTTON: // 2,000 pound
      return (weight * SHORT_TON_IN_METRIC_TONS) as MetricTonnes;
    default:
      exhaustiveCheck(unit, 'missing weight unit conversion');
  }
}
